// src/auth0/callExternalApiService.ts
import axios from "axios";
var callExternalApi = async (options) => {
  try {
    const response = await axios(options.config);
    const { data } = response;
    return {
      data,
      error: null
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";
      if (response && response.statusText) {
        message = response.statusText;
      }
      if (axiosError.message) {
        message = axiosError.message;
      }
      if (response && response.data && response.data.message) {
        message = response.data.message;
      }
      return {
        data: null,
        error: {
          message,
          // Absent when there was no response at all (transport failure, abort, DNS). That
          // absence is itself the signal: `message` is then axios's own text, not the server's.
          status: response?.status,
          // Present only with a response; lets a caller read a structured refusal's `code`.
          body: response?.data
        }
      };
    }
    return {
      data: null,
      error: {
        message: error.message
      }
    };
  }
};

// src/auth0/messageService.ts
var getPublicResource = async (apiServerUrl) => {
  const config2 = {
    url: `${apiServerUrl}/api/messages/public`,
    method: "GET",
    headers: {
      "content-type": "application/json"
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};
var getProtectedResource = async (apiServerUrl, accessToken) => {
  const config2 = {
    url: `${apiServerUrl}/api/messages/protected`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};
var getAdminResource = async (apiServerUrl, accessToken) => {
  const config2 = {
    url: `${apiServerUrl}/api/messages/admin`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/routes.ts
var createRoutes = (baseUrl) => ({
  dGraphProxy: `${baseUrl}api/dgraph/`,
  get: {
    dgraph: {
      getToken: (bID) => `${baseUrl}api/dgraph/token/${bID}`,
      getMetricTypesAndMetrics: (orgBID) => `${baseUrl}api/dgraph/metric-types?org_bid=${orgBID}`,
      getSensors: (orgBID, entityBID) => `${baseUrl}api/dgraph/sensors?org_bid=${orgBID}&entity_bid=${entityBID}`
    },
    calendar: {
      getCalendarView: (twinBID) => `${baseUrl}api/calendar/${twinBID}/calendar-view`,
      getCalendarViewSelection: (twinBID) => `${baseUrl}api/calendar/${twinBID}/calendar-selection-view`,
      getBaselineView: (twinBID) => `${baseUrl}api/calendar/${twinBID}/baseline-view`,
      getBaselineSelectionView: (twinBID) => `${baseUrl}api/calendar/${twinBID}/baseline-selection-view`,
      getPeakHoursView: (twinBID) => `${baseUrl}api/calendar/${twinBID}/peak-hours`
    },
    dashboardData: {
      // Operations-dashboard query-service proxies (replace-embeddable R1).
      getNow: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/now`,
      // Query-service 2.7.0 / ledger row 26. A TWIN-PATH route, like its two neighbours and
      // deliberately NOT like the org-scoped family below: this endpoint answers "what is about to
      // get busy in THIS gym", so the twin is the primary scope and belongs in the path where the
      // tenancy guard reads it.
      getPredictedBusyNextHour: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/predicted-busy-next-hour`,
      getEquipmentHealth: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/equipment-health`,
      getTimeseries: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/timeseries`,
      // equipment-overview catalogue proxies (FR5.2/FR5.3).
      getUsageBands: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/usage-bands`,
      getHierarchy: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/hierarchy`,
      // The ORG-SCOPED baseline family (query-service 2.0.0, GS16). Deliberately NOT functions of a
      // twinBID and deliberately WITHOUT a `${twinBID}` path segment: organisation is the primary
      // authorisation token here, twins ride as `twin_bids`/`twin_bid` query params, and omitting
      // both is an org-wide read. Adding a path segment would silently re-narrow the family.
      getBaselineSummary: `${baseUrl}api/dashboard-data/baseline-summary`,
      getBaselineViewScoped: `${baseUrl}api/dashboard-data/baseline-view`,
      getPeakHours: `${baseUrl}api/dashboard-data/peak-hours`,
      // manufacturer-comparison (Dashboard 1.2b, row 15) — the fourth member of that org-scoped
      // family, in the same form for the same reason: a plain constant with NO `${twinBID}`
      // segment. This endpoint blends the twins in scope into ONE comparison set (1.2b Req 1), so a
      // path twin would silently narrow the one thing it exists to do.
      getManufacturerComparison: `${baseUrl}api/dashboard-data/manufacturer-comparison`,
      // equipment-comparison (Dashboard 1.2a, row 14) — the FIFTH member of that org-scoped family,
      // same form, NO `${twinBID}` segment. The reason differs from the sibling's, and it is worth
      // stating because the two lines look identical: 1.2b needs org scope so it can BLEND the twins
      // in scope, whereas 1.2a needs it so each of its two rails can name its OWN twin — cross-twin
      // comparison is compulsory (Peter, 2026-08-12) and a path twin would make it unreachable.
      // Rows here are never blended; `twin_bid` is a column on every row.
      getEquipmentComparison: `${baseUrl}api/dashboard-data/equipment-comparison`,
      // ranking + timeseries (Equipment Utilisation multi-gym, ruling MG-1 / Option C) — the SIXTH
      // and SEVENTH members of the org-scoped family, in the same form for the same reason: plain
      // constants with NO `${twinBID}` segment.
      //
      // 🚨 `getTimeseries` ABOVE STILL EXISTS AND IS A DIFFERENT ROUTE. That one is path-scoped and
      // single-twin; this one takes a twin LIST as query params. Both are real, both are proxied,
      // and the names differ by the `Scoped` suffix — the same distinction `getBaselineView` and
      // `getBaselineViewScoped` already draw two entries up.
      //
      // ⚠️ `ranking` had NO route of either kind before this. It reached upstream only through the
      // path-scoped batch, whose bridge overwrites an item's twin selector with the one path twin —
      // so a multi-gym ranked card was not expressible at all.
      getRanking: `${baseUrl}api/dashboard-data/ranking`,
      getTimeseriesScoped: `${baseUrl}api/dashboard-data/timeseries`
    },
    organisations: {
      getOrgUrlsAndMetaData: `${baseUrl}api/organisations/static/`,
      getOrganisationById: (bID) => `${baseUrl}api/organisations/${bID}`,
      getTwins: (bID) => `${baseUrl}api/organisations/${bID}/twins`
    },
    twins: {
      getTwinById: (bID) => `${baseUrl}api/twins/${bID}`,
      // Its query (organisation_id, bid, name, except_bid) is passed as axios `params`, so encoded.
      availability: `${baseUrl}api/twins/availability`,
      // The whole read — published, authored and launchHours — without the deprecated parameter.
      readOpeningHours: (bID) => `${baseUrl}api/twins/${encodeURIComponent(bID)}/opening-hours`,
      getRTSPEndpoints: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp`,
      getRTSPStatus: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/status`,
      getStreamBalancePlan: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/balance/plan`,
      // The bytes of one stored catalogue frame, read under its twin and stream. `thumb` asks for the
      // small derivative (stream-thumbnails-derivative); absent or `original` sends no query, so
      // every caller that passes no size gets exactly the URL it always did.
      getStreamFrame: (twinId, rtspId, frameId, size) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspId}/frames/${encodeURIComponent(frameId)}${size === "thumb" ? "?size=thumb" : ""}`,
      // The newest stored frame of one stream, or `frame: null`. A pure read: it starts no capture.
      getLatestStreamFrame: (twinId, rtspId) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspId}/frames/latest`,
      // A Fetch thumbnails run's progress (stream-thumbnails-run). 404 when the run is gone or is not
      // this twin's.
      getThumbnailRun: (twinId, runId) => `${baseUrl}api/twins/${twinId}/rtsp/thumbnails/runs/${runId}`,
      getConfig: (bID) => `${baseUrl}api/twins/${bID}/config-by-bid`,
      // `getCapabilities` removed with the viewer's Edit-twin capabilities panel (ledger row 28).
      // The middleware route still exists (guarded by row 27a) and `put.twins.updateCapabilities`
      // below is KEPT — the admin console's per-twin toggle is its caller now.
      getThresholdAlertRulesets: (bID) => `${baseUrl}api/twins/${bID}/threshold-alert-ruleset`,
      getEquipmentClassifications: (bID) => `${baseUrl}api/twins/${bID}/equipment-classifications`
    },
    metrics: {
      getMetrics: (orgBID) => `${baseUrl}api/twin-metrics?org_bid=${orgBID}`
    },
    twinEntity: {
      getTwinEntityById: (bID) => `${baseUrl}api/twin-entity/${bID}`
    },
    users: {
      getUsers: `${baseUrl}api/users`,
      getUserById: `${baseUrl}api/users`
    },
    notifications: {
      getNotifications: `${baseUrl}api/notifications`
    },
    terms: {
      getStatus: `${baseUrl}api/terms/status`,
      getNotices: `${baseUrl}api/terms/notices`
    },
    legalDocuments: {
      // PR 1 (legal-document-registry) public read endpoints — the shared contract PRs
      // 2/3/5/6 consume. `getCurrentVersions` is the cheap poll (no content); the other two
      // return full `content_html`.
      getCurrentVersions: `${baseUrl}api/legal-documents/current`,
      getCurrent: (document2) => `${baseUrl}api/legal-documents/${document2}/current`,
      getVersion: (document2, version) => `${baseUrl}api/legal-documents/${document2}/versions/${version}`,
      // TL_ADMIN admin list (drafts + carve-outs included). Public read routes above stay separate.
      getAdmin: `${baseUrl}api/legal-documents/admin`
    }
  },
  post: {
    dgraph: {
      createNamespace: `${baseUrl}api/dgraph/namespace`
    },
    terms: {
      acceptTerms: `${baseUrl}api/terms/accept`
    },
    deviceClaims: `${baseUrl}api/device-claims`,
    legalDocuments: {
      createDraft: `${baseUrl}api/legal-documents/drafts`,
      publishDraft: (id) => `${baseUrl}api/legal-documents/drafts/${id}/publish`
    },
    twins: {
      createTwin: `${baseUrl}api/twins`,
      // The Opening hours step's whole-week save (spec twin-details-page-hours-week, R4.2).
      saveOpeningHours: (bID) => `${baseUrl}api/twins/${encodeURIComponent(bID)}/opening-hours`,
      claimDevice: (bID) => `${baseUrl}api/twins/${bID}/devices/claim`,
      addRTSPEndpoint: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp`,
      pushRTSPConfig: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/config-push`,
      measureStreamBalance: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/balance/measure`,
      // Start a Fetch thumbnails run, or get back the one already running on the twin.
      startThumbnailRun: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/thumbnails/runs`,
      applyStreamBalancePlan: (twinId) => `${baseUrl}api/twins/${twinId}/rtsp/balance/apply`,
      captureFrame: (twinId, rtspId) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspId}/capture-frame`,
      // POST, not GET, because it can publish an MQTT command — a side-effecting GET is one a
      // prefetcher, a retry or a browser cache can fire on its own.
      // `force` skips the stored-frame short-circuit and always asks the edge for a NEW frame.
      // Omit it for the instant-open path; the wall passes it on its Refresh controls, which would
      // otherwise be a no-op on a wall whose tiles all already have a stored frame.
      ensureLatestStreamFrame: (twinId, rtspId, force) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspId}/frames/ensure-latest${force ? "?force=true" : ""}`,
      addThresholdAlertRuleset: (bID) => `${baseUrl}api/twins/${bID}/threshold-alert-ruleset`
    },
    organisations: {
      createOrganisation: `${baseUrl}api/organisations`
    },
    users: {
      createUser: `${baseUrl}api/users/`,
      loginSync: `${baseUrl}api/users/login-sync`
    },
    "azure-file-storage": {
      uploadFml: `${baseUrl}api/azure-file-storage/upload-fml`
    },
    dashboardData: {
      // equipment-overview batch proxy (FR5.1) — coalesced multi-panel fetch.
      batch: (twinBID) => `${baseUrl}api/dashboard-data/${twinBID}/batch`
    }
  },
  patch: {
    organisations: {
      updateOrganisationById: (bID) => `${baseUrl}api/organisations/${bID}`
    },
    twins: {
      updateRTSPEndpoint: (twinId, rtspID) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspID}`,
      updateTwinById: (bID) => `${baseUrl}api/twins/${bID}`,
      updateThresholdAlertRuleset: (bID, thresholdAlertRulesetID) => `${baseUrl}api/twins/${bID}/threshold-alert-ruleset/${thresholdAlertRulesetID}`
    }
  },
  put: {
    twins: {
      updateCapabilities: (bID) => `${baseUrl}api/twins/${bID}/capabilities`
    },
    legalDocuments: {
      updateDraft: (id) => `${baseUrl}api/legal-documents/drafts/${id}`
    },
    users: {
      updateProfile: `${baseUrl}api/users/me`,
      updateUserById: `${baseUrl}api/users/`
    }
  },
  delete: {
    // The `dgraph` block was removed by the metrics-control-centre registry feature
    // (ledger row 17a). DGraph is read-only reference data under epic decision 1.
    twins: {
      deleteTwin: (bID) => `${baseUrl}api/twins/${bID}`,
      // Remove a standard week and hand its dates to the week before, in one save
      // (spec twin-details-page-hours-week, R2.3).
      removeStandardHours: (bID, effectiveDate, expectedEtag) => `${baseUrl}api/twins/${encodeURIComponent(bID)}/opening-hours/${encodeURIComponent(
        effectiveDate
      )}?kind=standard&expectedEtag=${encodeURIComponent(expectedEtag)}&extendsPreviousStandard=true`,
      // Delete a special period; the standard hours apply again on its dates
      // (spec twin-details-page-hours-special, R2.2).
      removeSpecialPeriod: (bID, effectiveDate, expectedEtag) => `${baseUrl}api/twins/${encodeURIComponent(bID)}/opening-hours/${encodeURIComponent(
        effectiveDate
      )}?kind=special&expectedEtag=${encodeURIComponent(expectedEtag)}`,
      deleteRTSPEndpoint: (twinId, rtspID) => `${baseUrl}api/twins/${twinId}/rtsp/${rtspID}`,
      deleteThresholdAlertRuleset: (bID, thresholdAlertRulesetID) => `${baseUrl}api/twins/${bID}/threshold-alert-ruleset/${thresholdAlertRulesetID}`
    },
    users: `${baseUrl}api/users/`,
    legalDocuments: {
      deleteDraft: (id) => `${baseUrl}api/legal-documents/drafts/${id}`
    }
  }
});
var routes_default = createRoutes;

// src/middleware/httpTimeout.ts
var MIDDLEWARE_REQUEST_TIMEOUT_MS = 3e4;
var MIDDLEWARE_WRITE_TIMEOUT_MS = 12e4;
var BI_IMPORT_TIMEOUT_MS = 24e4;
var BI_OUTCOME_SAVE_TIMEOUT_MS = 12e3;

// src/requestId.ts
import axios2 from "axios";
var REQUEST_ID_HEADER = "x-request-id";
var currentLoad = "init";
var sequence = 0;
var installed = false;
var interceptorId;
var middlewareBaseUrl;
var shortRandomId = () => {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid.slice(0, 8);
  return Math.random().toString(36).slice(2, 10);
};
var beginLoad = (label) => {
  currentLoad = `${label}-${shortRandomId()}`;
  sequence = 0;
  return currentLoad;
};
var currentLoadId = () => currentLoad;
var nextRequestId = () => {
  sequence += 1;
  return `${currentLoad}-${sequence}`;
};
var installRequestIdInterceptor = (baseUrl) => {
  middlewareBaseUrl = baseUrl;
  if (installed) return;
  installed = true;
  interceptorId = axios2.interceptors.request.use((config2) => {
    if (config2.headers?.has?.(REQUEST_ID_HEADER)) return config2;
    if (!targetsMiddleware(config2.baseURL, config2.url)) return config2;
    config2.headers?.set?.(REQUEST_ID_HEADER, nextRequestId());
    return config2;
  });
};
var targetsMiddleware = (baseURL, url) => {
  const target = `${baseURL ?? ""}${url ?? ""}`;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(target)) return true;
  if (!middlewareBaseUrl) return false;
  try {
    return new URL(target).origin === new URL(middlewareBaseUrl).origin;
  } catch {
    return false;
  }
};

// src/clientPerf.ts
var MAX_BATCH = 32;
var FLUSH_DEBOUNCE_MS = 1e4;
var config;
var buffer = [];
var flushTimer;
var listenersBound = false;
var initClientPerfReporter = (next) => {
  config = next;
  if (listenersBound || typeof document === "undefined") return;
  listenersBound = true;
  const flushOnHide = () => {
    if (document.visibilityState !== "hidden") return;
    queueMicrotask(() => void flushClientPerf());
  };
  document.addEventListener("visibilitychange", flushOnHide);
  window.addEventListener("pagehide", () => void flushClientPerf());
};
var reportClientPerf = (entry) => {
  try {
    if (buffer.length >= MAX_BATCH) buffer.shift();
    buffer.push(entry);
    if (!flushTimer) {
      flushTimer = setTimeout(() => void flushClientPerf(), FLUSH_DEBOUNCE_MS);
    }
  } catch {
  }
};
var flushClientPerf = async () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = void 0;
  }
  const current = config;
  if (!current || buffer.length === 0) return;
  const token = current.getAccessToken();
  const twinBid = current.getTwinBid();
  if (!token || !twinBid) {
    flushTimer = setTimeout(() => void flushClientPerf(), FLUSH_DEBOUNCE_MS);
    return;
  }
  const entries = buffer;
  buffer = [];
  try {
    const base = current.baseUrl.endsWith("/") ? current.baseUrl : `${current.baseUrl}/`;
    await fetch(`${base}api/twins/${encodeURIComponent(twinBid)}/client-perf`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ app: current.app, entries })
    });
  } catch {
  }
};

// src/middleware/azure-file-storage/uploadFmlFile.ts
var uploadFmlFile = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/azure-file-storage/upload-fml`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Failed to upload FML file to Azure Storage: ${response.status} ${errorData}`);
  }
  return response.json();
};

// src/middleware/bi-dashboard/deleteBiDashboard.ts
var deleteBiDashboard = async (baseUrl, accessToken, dashboardId) => {
  const config2 = {
    url: `${baseUrl}api/bi-dashboards/${dashboardId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/bi-dashboard/getBiDashboardById.ts
import axios3 from "axios";
var getBiDashboardById = async (baseUrl, accessToken, dashboardId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/bi-dashboards/${dashboardId}`;
    const res = await axios3.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching BI dashboard by ID:", error);
    return void 0;
  }
};

// src/middleware/bi-dashboard/getBiDashboards.ts
import axios4 from "axios";
var getBiDashboards = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/bi-dashboards`;
    const res = await axios4.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching BI dashboards:", error);
    return void 0;
  }
};

// src/middleware/bi-dashboard/updateBiDashboard.ts
var updateBiDashboard = async (baseUrl, accessToken, dashboardId, dashboardPayload) => {
  const config2 = {
    url: `${baseUrl}api/bi-dashboards/${dashboardId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: dashboardPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/bi-dashboard/getBiDashboardDefinition.ts
import axios5 from "axios";
var getBiDashboardDefinition = async (baseUrl, accessToken, dashboardId, version) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const query = version !== void 0 ? `?version=${version}` : "";
    const url = `${baseUrl}api/bi-dashboards/${dashboardId}/definition${query}`;
    const res = await axios5.get(url, { headers: headers3, timeout: 3e4 });
    return res.data;
  } catch (error) {
    console.error("Error fetching BI dashboard definition:", error);
    return void 0;
  }
};

// src/middleware/bi-dashboard/getBiDashboardVersions.ts
import axios6 from "axios";
var getBiDashboardVersions = async (baseUrl, accessToken, dashboardId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/bi-dashboards/${dashboardId}/versions`;
    const res = await axios6.get(url, { headers: headers3, timeout: 3e4 });
    return res.data;
  } catch (error) {
    console.error("Error fetching BI dashboard versions:", error);
    return void 0;
  }
};

// src/middleware/bi-dashboard/publishBiDashboard.ts
var publishBiDashboard = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/bi-dashboards/publish`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/bi-dashboard/setPublishedVersion.ts
var setPublishedVersion = async (baseUrl, accessToken, dashboardId, versionId) => {
  const config2 = {
    url: `${baseUrl}api/bi-dashboards/${dashboardId}/published-version`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: { version_id: versionId }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/calendar/describeRequestError.ts
var describeRequestError = (error) => {
  const e = error;
  return { status: e?.response?.status, message: e?.message ?? String(error) };
};

// src/middleware/calendar/getCalendarSelectionView.ts
import axios7 from "axios";
var getCalendarSelectionView = async (baseUrl, accessToken, organisation, twinBID, aggregation, time_windows, metrics, debug) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.get.calendar.getCalendarViewSelection(twinBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const data = {};
  if (organisation) data.organisation = organisation;
  if (twinBID) data.twin_bid = twinBID;
  if (aggregation) data.aggregation = aggregation;
  if (time_windows && time_windows.length > 0) data.time_windows = time_windows;
  if (metrics && metrics.length > 0) data.metrics = metrics;
  if (debug !== void 0) data.debug = debug;
  try {
    const res = await axios7.post(url, data, { headers: headers3 });
    return res.data.data;
  } catch (error) {
    const { status, message } = describeRequestError(error);
    console.warn(
      `[getCalendarSelectionView] calendar-selection-view read failed for twin ${twinBID} \u2014 rendering empty:`,
      status ?? "(no response)",
      message
    );
    return null;
  }
};

// src/middleware/calendar/getCalendarView.ts
import axios8 from "axios";
var getCalendarView = async (baseUrl, accessToken, twinBID, organisation, bid, startDateTime, endDateTime, aggregation, metrics, monthlyView, debug) => {
  const routes = routes_default(baseUrl);
  let data = null;
  let url = `${routes.get.calendar.getCalendarView(twinBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const queryParams = new URLSearchParams();
  if (organisation) queryParams.append("organisation", organisation);
  if (twinBID) queryParams.append("twinBID", twinBID);
  if (bid) queryParams.append("bid", bid);
  if (startDateTime) queryParams.append("start_time", startDateTime);
  if (endDateTime) queryParams.append("end_time", endDateTime);
  if (aggregation) queryParams.append("aggregation", aggregation);
  if (metrics && metrics.length > 0) {
    for (const metric of metrics) {
      queryParams.append("metrics", metric);
    }
  }
  if (monthlyView !== void 0) queryParams.append("monthly_view", String(monthlyView));
  if (debug !== void 0) queryParams.append("debug", String(debug));
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  try {
    const res = await axios8.get(url, { headers: headers3 });
    data = res.data.data;
  } catch (error) {
    console.error(error);
  }
  return data;
};

// src/middleware/calendar/getBaselineSelectionView.ts
import axios9 from "axios";
var getBaselineSelectionView = async (baseUrl, accessToken, organisation, twinBID, aggregation, weekday_windows, metrics, debug) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.get.calendar.getBaselineSelectionView(twinBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const data = {};
  if (organisation) data.organisation = organisation;
  if (twinBID) data.twin_bid = twinBID;
  if (aggregation) data.aggregation = aggregation;
  if (weekday_windows && weekday_windows.length > 0) data.weekday_windows = weekday_windows;
  if (metrics && metrics.length > 0) data.metrics = metrics;
  if (debug !== void 0) data.debug = debug;
  try {
    const res = await axios9.post(url, data, { headers: headers3 });
    return res.data.data;
  } catch (error) {
    const { status, message } = describeRequestError(error);
    console.warn(
      `[getBaselineSelectionView] baseline-selection-view read failed for twin ${twinBID} \u2014 rendering empty:`,
      status ?? "(no response)",
      message
    );
    return null;
  }
};

// src/middleware/calendar/getBaselineView.ts
import axios10 from "axios";
var getBaselineView = async (baseUrl, accessToken, twinBID, organisation, bid, aggregation, metrics, debug, entityBids, peakHoursOnly, dayType) => {
  const routes = routes_default(baseUrl);
  let data = null;
  let url = `${routes.get.calendar.getBaselineView(twinBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const queryParams = new URLSearchParams();
  if (organisation) queryParams.append("organisation", organisation);
  if (entityBids && entityBids.length > 0) {
    for (const entityBid of entityBids) {
      queryParams.append("entity_bids", entityBid);
    }
  } else if (bid) {
    queryParams.append("bid", bid);
  }
  if (aggregation) queryParams.append("aggregation", aggregation);
  if (metrics && metrics.length > 0) {
    for (const metric of metrics) {
      queryParams.append("metrics", metric);
    }
  }
  if (debug !== void 0) queryParams.append("debug", String(debug));
  if (peakHoursOnly) queryParams.append("peak_hours_only", "true");
  if (dayType === "weekday" || dayType === "weekend") queryParams.append("day_type", dayType);
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  try {
    const res = await axios10.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    data = res.data.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`getBaselineView failed for ${twinBID}: ${message}`);
  }
  return data;
};

// src/middleware/calendar/getPeakHoursView.ts
import axios11 from "axios";
var getPeakHoursView = async (baseUrl, accessToken, organisation, twinBID, entityBID) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.get.calendar.getPeakHoursView(twinBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const data = {
    organisation,
    entity_bid: entityBID
  };
  try {
    const res = await axios11.post(url, data, {
      headers: headers3,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    return res.data?.data?.peak_windows ?? null;
  } catch (error) {
    return null;
  }
};

// src/middleware/dashboard-data/getUsageNow.ts
import axios12 from "axios";
var getUsageNow = async (baseUrl, accessToken, twinBID, params = {}) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getNow(twinBID);
  const queryParams = new URLSearchParams();
  if (params.group_type) queryParams.append("group_type", params.group_type);
  if (params.group_level) queryParams.append("group_level", params.group_level);
  if (params.direction) queryParams.append("direction", params.direction);
  if (params.n != null) queryParams.append("n", String(params.n));
  if (queryParams.toString()) url += `?${queryParams.toString()}`;
  const res = await axios12.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // Bound the request so a stalled middleware/QS rejects (→ React Query isError) rather than
    // pinning the panel in loading forever (the refetchInterval won't re-fire while one is pending).
    // 30s matches the middleware's outbound cap so the browser doesn't abort a slow-cold BI query
    // (esp. equipment_health) before the middleware itself would.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getPredictedBusyNextHour.ts
import axios13 from "axios";
var getPredictedBusyNextHour = async (baseUrl, accessToken, twinBID, params = {}) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getPredictedBusyNextHour(twinBID);
  const queryParams = new URLSearchParams();
  if (params.group_level) queryParams.append("group_level", params.group_level);
  if (params.direction) queryParams.append("direction", params.direction);
  if (params.n != null) queryParams.append("n", String(params.n));
  if (queryParams.toString()) url += `?${queryParams.toString()}`;
  const res = await axios13.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // Matches the middleware's own outbound cap, so the browser does not abort a slow-cold BI
    // query before the middleware itself would.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getEquipmentHealth.ts
import axios14 from "axios";
var getEquipmentHealth = async (baseUrl, accessToken, twinBID, params = {}) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getEquipmentHealth(twinBID);
  const queryParams = new URLSearchParams();
  if (params.flagged_only !== void 0)
    queryParams.append("flagged_only", String(params.flagged_only));
  if (queryParams.toString()) url += `?${queryParams.toString()}`;
  const res = await axios14.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // See getUsageNow: bound the request so a stall surfaces as an error, not perpetual loading.
    // 30s matches the middleware cap — equipment_health is the slow-cold endpoint this headroom is for.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getTimeseries.ts
import axios15 from "axios";
var getTimeseries = async (baseUrl, accessToken, twinBID, params) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getTimeseries(twinBID);
  const queryParams = new URLSearchParams();
  queryParams.append("start", params.start);
  queryParams.append("end", params.end);
  for (const metric of params.metrics) queryParams.append("metrics", metric);
  if (params.grain) queryParams.append("grain", params.grain);
  if (params.bids) for (const bid of params.bids) queryParams.append("bids", bid);
  if (params.group_type) queryParams.append("group_type", params.group_type);
  url += `?${queryParams.toString()}`;
  const res = await axios15.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // See getUsageNow: bound the request so a stall surfaces as an error, not perpetual loading.
    // 30s matches the middleware cap so the browser doesn't abort a slow-cold query first.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getUsageBands.ts
import axios16 from "axios";
var getUsageBands = async (baseUrl, accessToken, twinBID, params) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getUsageBands(twinBID);
  const queryParams = new URLSearchParams();
  queryParams.append("start", params.start);
  queryParams.append("end", params.end);
  if (params.grain) queryParams.append("grain", params.grain);
  if (params.group_type) queryParams.append("group_type", params.group_type);
  if (params.bids) for (const bid of params.bids) queryParams.append("bids", bid);
  if (params.day_type) queryParams.append("day_type", params.day_type);
  if (params.peak_hours_only) queryParams.append("peak_hours_only", "true");
  url += `?${queryParams.toString()}`;
  const res = await axios16.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so the browser doesn't abort a slow-cold BI query first.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getHierarchy.ts
import axios17 from "axios";
var getHierarchy = async (baseUrl, accessToken, twinBID, params = {}) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.dashboardData.getHierarchy(twinBID);
  const queryParams = new URLSearchParams();
  if (params.tree) queryParams.append("tree", params.tree);
  const qs = queryParams.toString();
  if (qs) url += `?${qs}`;
  const res = await axios17.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so the browser doesn't abort a slow-cold BI query first.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/postDashboardBatch.ts
import axios18 from "axios";
var postDashboardBatch = async (baseUrl, accessToken, twinBID, requests) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.dashboardData.batch(twinBID);
  const res = await axios18.post(
    url,
    { requests },
    {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      // 30s matches the middleware cap so the browser doesn't abort a slow-cold BI query first.
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    }
  );
  return res.data;
};

// src/middleware/dashboard-data/getBaselineSummary.ts
import axios19 from "axios";

// src/middleware/dashboard-data/orgScopedQuery.ts
var appendTwinScope = (queryParams, params, fnName) => {
  const { twin_bids: twinBids, twin_bid: twinBid } = params;
  if (twinBids !== void 0 && twinBid !== void 0) {
    throw new Error(
      `${fnName}: exactly one of twin_bids / twin_bid may be supplied (omit both for an org-only read)`
    );
  }
  if (twinBids !== void 0) {
    if (twinBids.length === 0) {
      throw new Error(
        `${fnName}: twin_bids was supplied but empty \u2014 an empty array is NOT org-only. Omit both twin selectors to read across the organisation.`
      );
    }
    for (const bid of twinBids) queryParams.append("twin_bids", bid);
    return;
  }
  if (twinBid !== void 0) queryParams.append("twin_bid", twinBid);
};
var appendBids = (queryParams, bids, fnName) => {
  if (bids.length === 0) {
    throw new Error(`${fnName}: bids is required and must name at least one group bID`);
  }
  for (const bid of bids) queryParams.append("bids", bid);
};
var appendOptionalList = (queryParams, name, values) => {
  if (!values || values.length === 0) return;
  for (const value of values) queryParams.append(name, value);
};
var appendGroupSelector = (queryParams, params, fnName) => {
  const { bids } = params;
  const groupType = params.group_type?.trim() || void 0;
  const groupLevel = params.group_level?.trim() || void 0;
  const named = bids !== void 0;
  const halfPair = groupType === void 0 !== (groupLevel === void 0);
  if (halfPair) {
    throw new Error(
      `${fnName}: group_type and group_level must be supplied together \u2014 the pair ENUMERATES a level, and one without the other is never a request the query service can answer`
    );
  }
  const enumerated = groupType !== void 0 && groupLevel !== void 0;
  if (named && enumerated) {
    throw new Error(
      `${fnName}: pass exactly one of bids or the group_type+group_level pair, not both (the pair ENUMERATES a level; it is not a filter on named bids)`
    );
  }
  if (!named && !enumerated) {
    throw new Error(
      `${fnName}: exactly one of bids or the group_type+group_level pair is required \u2014 omitting both would read the whole twin, so absence is refused rather than guessed`
    );
  }
  if (enumerated) {
    queryParams.append("group_type", groupType);
    queryParams.append("group_level", groupLevel);
    return;
  }
  appendBids(queryParams, bids, fnName);
};
var appendFlag = (queryParams, name, value) => {
  if (value === true) queryParams.append(name, "true");
};

// src/middleware/dashboard-data/getBaselineSummary.ts
var getBaselineSummary = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getBaselineSummary");
  appendGroupSelector(queryParams, params, "getBaselineSummary");
  if (params.measures)
    for (const measure of params.measures) queryParams.append("measures", measure);
  if (params.day_type) queryParams.append("day_type", params.day_type);
  appendFlag(queryParams, "peak_hours_only", params.peak_hours_only);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getBaselineSummary}?${queryParams.toString()}`;
  const res = await axios19.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware's outbound cap so the browser doesn't abort a slow-cold BI query
    // before the middleware itself would.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getBaselineViewScoped.ts
import axios20 from "axios";
var getBaselineViewScoped = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getBaselineViewScoped");
  appendGroupSelector(queryParams, params, "getBaselineViewScoped");
  if (params.metrics) for (const metric of params.metrics) queryParams.append("metrics", metric);
  if (params.aggregation) queryParams.append("aggregation", params.aggregation);
  appendFlag(queryParams, "peak_hours_only", params.peak_hours_only);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getBaselineViewScoped}?${queryParams.toString()}`;
  const res = await axios20.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap — baseline_view is the slowest BI-view read, so the browser
    // must not abort a slow-cold query before the middleware would.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getPeakHours.ts
import axios21 from "axios";
var getPeakHours = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getPeakHours");
  appendBids(queryParams, params.bids, "getPeakHours");
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getPeakHours}?${queryParams.toString()}`;
  const res = await axios21.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so a stall surfaces as an error, not perpetual loading.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getManufacturerComparison.ts
import axios22 from "axios";
var appendOptionalList2 = (queryParams, name, values) => {
  if (!values || values.length === 0) return;
  for (const value of values) queryParams.append(name, value);
};
var getManufacturerComparison = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getManufacturerComparison");
  appendOptionalList2(queryParams, "bids", params.bids);
  appendOptionalList2(queryParams, "manufacturer_bids", params.manufacturer_bids);
  if (params.split_level !== void 0) queryParams.append("split_level", params.split_level);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getManufacturerComparison}?${queryParams.toString()}`;
  const res = await axios22.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so a stall surfaces as an error, not perpetual loading. This
    // endpoint is the slowest in the family (measured 279 ms scoped to one type, 3,959 ms whole-gym
    // per twin on BI Prod 2), so the cap is closer to being reached here than anywhere else.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getEquipmentComparison.ts
import axios23 from "axios";
var appendOptionalBids = (queryParams, bids) => {
  if (!bids || bids.length === 0) return;
  for (const bid of bids) queryParams.append("bids", bid);
};
var getEquipmentComparison = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getEquipmentComparison");
  appendOptionalBids(queryParams, params.bids);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getEquipmentComparison}?${queryParams.toString()}`;
  const res = await axios23.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so a stall surfaces as an error rather than perpetual loading.
    // This endpoint issues three BI reads per twin, so it sits in the same cost band as its 1.2b
    // sibling (measured there at 279 ms scoped to one type, 3,959 ms whole-gym per twin) — closer to
    // the cap than anything else in the family.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getRanking.ts
import axios24 from "axios";
var getRanking = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getRanking");
  queryParams.append("measure", params.measure);
  if (params.direction !== void 0) queryParams.append("direction", params.direction);
  if (params.n !== void 0) queryParams.append("n", String(params.n));
  if (params.grain !== void 0) queryParams.append("grain", params.grain);
  if (params.group_type !== void 0) queryParams.append("group_type", params.group_type);
  if (params.group_level !== void 0) queryParams.append("group_level", params.group_level);
  if (params.start !== void 0) queryParams.append("start", params.start);
  if (params.end !== void 0) queryParams.append("end", params.end);
  if (params.last_minutes !== void 0) {
    queryParams.append("last_minutes", String(params.last_minutes));
  }
  appendFlag(queryParams, "exclude_unknown", params.exclude_unknown);
  appendOptionalList(queryParams, "bids", params.bids);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getRanking}?${queryParams.toString()}`;
  const res = await axios24.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so a stall surfaces as an error, not perpetual loading.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/getTimeseriesScoped.ts
import axios25 from "axios";
var getTimeseriesScoped = async (baseUrl, accessToken, params) => {
  const routes = routes_default(baseUrl);
  const queryParams = new URLSearchParams();
  appendTwinScope(queryParams, params, "getTimeseriesScoped");
  queryParams.append("start", params.start);
  queryParams.append("end", params.end);
  if (params.metrics.length === 0) {
    throw new Error(
      "getTimeseriesScoped: metrics is required and must name at least one metric \u2014 an empty array would append nothing and the request would be a 400 upstream"
    );
  }
  for (const metric of params.metrics) queryParams.append("metrics", metric);
  if (params.grain !== void 0) queryParams.append("grain", params.grain);
  appendOptionalList(queryParams, "bids", params.bids);
  if (params.group_type !== void 0) queryParams.append("group_type", params.group_type);
  appendFlag(queryParams, "debug", params.debug);
  const url = `${routes.get.dashboardData.getTimeseriesScoped}?${queryParams.toString()}`;
  const res = await axios25.get(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    // 30s matches the middleware cap so a stall surfaces as an error, not perpetual loading.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/dashboard-data/baselineMeasures.ts
var CAPACITY = "capacity";
var TRACKED_CAPACITY = "trackedCapacity";
var SCALED_AVG_HEADCOUNT = "scaledBaselineAvgHeadcount";
var SCALED_TYPICAL_PEAK_HEADCOUNT = "scaledTypicalPeakHeadcount";
var SCALED_ABSOLUTE_PEAK_HEADCOUNT = "scaledAbsolutePeakHeadcount";
var SPARE_AT_TYPICAL_PEAK = "spareAtTypicalPeak";
var AVG_SPARE = "avgSpare";
var BASELINE_MEASURE_TOKENS = [
  CAPACITY,
  TRACKED_CAPACITY,
  SCALED_AVG_HEADCOUNT,
  SCALED_TYPICAL_PEAK_HEADCOUNT,
  SCALED_ABSOLUTE_PEAK_HEADCOUNT,
  SPARE_AT_TYPICAL_PEAK,
  AVG_SPARE
];
var TOKEN_SET = new Set(BASELINE_MEASURE_TOKENS);
var isBaselineMeasureToken = (value) => typeof value === "string" && TOKEN_SET.has(value);

// src/middleware/dashboard-data/createMiddlewareDataProvider.ts
function resolveBindingTwin(binding, context) {
  return asString(binding.params?.twin_bid) ?? context.twinBid;
}
function resolveBindingTwins(binding) {
  const raw = binding.params?.twin_bids;
  if (raw === void 0 || raw === null) return { kind: "absent" };
  if (typeof raw === "string") {
    return raw.trim() === "" ? { kind: "invalid", why: "it is an empty string, which names no gym" } : { kind: "twins", twins: [raw.trim()] };
  }
  if (!Array.isArray(raw)) return { kind: "invalid", why: "it is not an array" };
  if (raw.length === 0) {
    return {
      kind: "invalid",
      // Spelled out because the consequence is not guessable from the shape: an empty array appends
      // ZERO repeated keys, so the selector is simply absent on the wire, and absent is org-wide.
      why: 'it is empty \u2014 an empty selection is NOT "every gym", and omitting the selector reads the whole organisation'
    };
  }
  const bad = raw.findIndex((v) => typeof v !== "string" || v.trim() === "");
  if (bad !== -1) {
    return { kind: "invalid", why: `member ${bad} is not a non-empty gym bID` };
  }
  const seen = /* @__PURE__ */ new Set();
  const twins = [];
  for (const v of raw) {
    const bid = v.trim();
    if (seen.has(bid)) continue;
    seen.add(bid);
    twins.push(bid);
  }
  return { kind: "twins", twins };
}
var MAX_COMPARISON_READS = 24;
var MAX_COMPARISON_TWINS = 6;
function comparisonBudgetRefusal(twins, params) {
  const allowed = maxGymsForScope(params.scope_levels);
  if (twins <= allowed) return void 0;
  const excess = twins - allowed;
  return `Up to ${allowed} ${allowed === 1 ? "gym" : "gyms"} can be compared at this equipment scope, and ${twins} are selected. Remove ${excess} ${excess === 1 ? "gym" : "gyms"}, or narrow the equipment selection.`;
}
function maxGymsForScope(scopeLevels) {
  const levels = asPositiveInt(scopeLevels) ?? 1;
  const budgetCap = Math.floor(MAX_COMPARISON_READS / (READS_PER_TWIN_PER_LEVEL * levels));
  return Math.max(1, Math.min(MAX_COMPARISON_TWINS, budgetCap));
}
var READS_PER_TWIN_PER_LEVEL = 3;
function asPositiveInt(value) {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : void 0;
}
var BATCHABLE = /* @__PURE__ */ new Set([
  // 🔻 `timeseries` AND `ranking` WERE HERE AND ARE DELIBERATELY GONE (Equipment Utilisation
  // multi-gym, ruling MG-1 / Option C). Both are now served PER-CALL, for the SCOPE reason stated
  // in full on the `manufacturer_comparison` leg below — and here the reason is not a preference,
  // it is the feature:
  //
  //   The batch route is `POST /dashboard-data/:twinBid/batch` and it has exactly ONE path twin.
  //   Its bridge injects that twin into every item, overwriting whatever selector the definition
  //   authored — `twin_bid: pathTwin` for anything outside the middleware's
  //   `TWIN_BIDS_BATCH_ENDPOINTS`, and `twin_bids: [pathTwin]` for anything inside it. So a
  //   dashboard asking for three gyms is answered for ONE, at 200, with nothing in the envelope
  //   marking the scope as reduced. Multi-gym is not expressible on the batch at ALL, by either
  //   arm. ⛔ No lane may move either of these back.
  //
  // ⚠️ AND `ranking` HAS A SECOND CONSUMER THAT DID NOT ASK FOR THIS. The Operations dashboard's
  // busiest-areas card binds it (spatial zones, 5-minute grain, a trailing 60-minute window,
  // refreshed every five minutes), so that read moves from the coalescing batch to its own GET too.
  // Its NUMBERS do not move — same upstream endpoint, same params, and the org-scoped guard
  // authorises its one twin exactly as the path guard did — but the page's first paint costs one
  // extra round trip. Taken deliberately over a transport that varies with the binding: one
  // endpoint reachable through two paths is how a shape divergence gets introduced later.
  "hierarchy",
  "now",
  "equipment_health",
  "usage_bands",
  "events",
  "event_filters",
  // Query-service 2.7.0 / ledger row 26 — the Operations dashboard's upcoming-hour panel.
  //
  // Batched rather than per-call, which is the OPPOSITE of the baseline family's answer above, and
  // for reasons that are the mirror image of theirs: this endpoint is TWIN-scoped (so the batch's
  // path-twin overwrite is exactly right rather than a silent narrowing), it returns a bare row
  // list that the fan-out can hand back verbatim (so the missing per-endpoint adapter costs
  // nothing), and it sits on a dashboard whose whole point is that its four reads coalesce into one
  // round trip. Its params are plain scalars, so `toBatchParams` carries them unchanged.
  "predicted_busy_next_hour",
  // Query-service 2.10.0 / ledger row 16 — the Equipment Recommendations dashboard (1.3).
  //
  // Batched, and it meets all three of the criteria stated above for `predicted_busy_next_hour`
  // rather than the baseline family's: it is TWIN-scoped and takes ONE twin, so the batch's
  // path-twin overwrite is exactly right rather than a silent narrowing; it returns a bare row
  // list plus `stats` that the fan-out hands back verbatim, so the missing per-endpoint adapter
  // costs nothing; and its params are plain scalars (plus a `bids` string array), so
  // `toBatchParams` carries them unchanged.
  //
  // ⭐ AND BATCHING IS WORTH MORE HERE THAN ANYWHERE ELSE ON THIS SURFACE. Dashboard 1.3 binds
  // FIVE panels to this one endpoint (two ranked lists and three suggestion buckets), each
  // asking for a different subset/order/cut of the same twin-level answer. Batched, they
  // coalesce into ONE outbound POST — and upstream they share ONE cached BI read, so the whole
  // paint costs a single 90-day scan.
  //
  // ⚠️ `twin_bid` is REQUIRED upstream (an estate-wide read of that view times out), and the
  // middleware injects it from the `:twinBid` path param for every endpoint outside
  // `TWIN_BIDS_BATCH_ENDPOINTS`. Verified in the middleware source rather than assumed from the
  // precedent — on the baseline family the same mechanism is a silent NARROWING, which is why
  // that family is excluded; on a single-twin endpoint it is the correct scope.
  "equipment_recommendations",
  // Query-service 2.13.0 / ledger row 18 — the Classes dashboard (1.2), the epic's last.
  //
  // Batched, and it meets the same three criteria as the two entries above rather than the baseline
  // family's: it is TWIN-scoped and takes ONE required `twin_bid`, so the batch's path-twin
  // overwrite is exactly right rather than a silent narrowing; its params are plain scalars, so
  // `toBatchParams` carries them unchanged; and its body is a bare row list plus `stats`.
  //
  // ⚠️ THE THIRD CRITERION IS THE ONE THAT CHANGED. Every previous batched endpoint was chosen
  // partly BECAUSE the fan-out could hand its body back verbatim, the missing per-endpoint adapter
  // being the stated reason the baseline family stays per-call. This endpoint is the first that
  // needs a projection — so rather than route it per-call, this release adds the adapter step the
  // fan-out was always missing (`BATCH_PROJECTORS` below). Batching is worth it here for the same
  // reason it is on dashboard 1.3: THREE panels bind this one endpoint (a top card, a bottom card
  // and the complete class list), each asking for a different cut of the same twin-level answer, so
  // batched they coalesce into ONE outbound POST.
  //
  // ⚠️ THEY DO NOT SHARE ONE UPSTREAM READ, and an earlier version of this comment said they did.
  // De-duplication is keyed on the CONSTRUCTED WIRE PARAMS, and the three cuts differ by
  // construction, so the paint is one POST and THREE concurrent upstream aggregations — which is
  // what the spec's NFR4 budget actually says. The saving is the round trips and the auth hops, not
  // the BI work.
  "event_ranking",
  // Query-service 2.14.0 / ledger row 18's SECOND pass — the Classes dashboard's per-instance list.
  //
  // Batched on the same three criteria as `event_ranking` above: it is TWIN-scoped and takes ONE
  // required `twin_bid`, so the batch's path-twin overwrite is exactly right rather than a silent
  // narrowing; its params are plain scalars, so `toBatchParams` carries them unchanged; and its
  // body is a bare row list plus `stats`. It needs a projection (`BATCH_PROJECTORS` below) — the
  // adapter step row 18's first pass added is what makes that admissible.
  //
  // Batching it is what keeps the page's budget at ONE outbound POST: the dashboard's FIVE reads
  // (room list, two ranked cards, the aggregate list, this instance list) coalesce. As with
  // `event_ranking`, that is one POST and five upstream reads — de-duplication is keyed on the
  // constructed wire params, and the cuts differ by construction.
  "event_instances"
]);
var BATCH_PROJECTORS = Object.assign(
  // ⚠️ `env` is annotated explicitly: `Object.create(null)` is typed `any`, which erases the
  // contextual type the object literal would otherwise get from the annotation above and makes the
  // parameter an implicit `any`. `tsc --noEmit` on this package tolerates it; the dts BUILD does not,
  // so the failure appears at `turbo run build` rather than at the type check.
  /* @__PURE__ */ Object.create(null),
  {
    event_ranking: (env) => ({
      ...env,
      // ⚠️ `shareSafe`, and this is the FIRST payload the fan-out shares that the provider itself
      // DERIVED rather than handed through from the wire — the exact situation that helper's docblock
      // was written for. Every waiter of one de-duplicated key receives the SAME array by reference,
      // so an in-place sort or normalisation in a future adapter would corrupt the SIBLING panel:
      // reproducibly on one gym and not another (the service's ordering differs per gym), with
      // nothing thrown and nothing logged. No consumer mutates today — verified: `adaptRanking`,
      // `BarChartPanel` and `MetricTablePanel` all map to fresh objects and `MetricTable` sorts a
      // copy — and the freeze is what keeps that true.
      //
      // ⚠️ The IDENTITY arm is deliberately NOT frozen: the provider suite asserts object identity
      // for an unregistered endpoint, and freezing raw wire bodies for six shipped dashboards is a
      // behaviour change well outside this row. The freeze is applied at the CALL SITE rather than
      // here, because this registry is module-level and `shareSafe` is scoped to the factory.
      data: projectEventRanking(env)
    }),
    // Row 18's second pass — same freeze-at-call-site / null-pass-through rules as its sibling
    // above; see `projectEventInstances` for the shape.
    event_instances: (env) => ({
      ...env,
      data: projectEventInstances(env)
    })
  }
);
function projectEventRanking(env) {
  const rows = Array.isArray(env?.data) ? env.data : [];
  return rows.filter((r) => r !== null && typeof r === "object").map((r) => ({
    group_bid: r.group_key,
    group_label: r.group_key,
    // ⚠️ `null` PASSES THROUGH UNTOUCHED, in both positions. `Number(null)` is 0, so this must be a
    // copy and never a coercion: a fabricated zero would sort to the very TOP of the BOTTOM-ranked
    // card, presenting "we could not measure this class" as "this class was empty" in the most
    // prominent slot on the panel.
    value: r.value,
    values: {
      // The same served number the bars read. Duplicated rather than derived: one field is what a
      // bar mark reads, the other is what a table column names.
      value: r.value,
      // The WEIGHT behind `value`, surfaced as the class list's two right-hand columns. A ranked
      // fill % with no weight beside it is the most misreadable number on that dashboard.
      instances: r.instances,
      slots: r.slots
    }
  }));
}
function projectEventInstances(env) {
  const rows = Array.isArray(env?.data) ? env.data : [];
  if (rows.length > 0) {
    const KNOWN = [
      "event_instance_id",
      "local_date",
      "class_title_clean",
      "avg_utilisation_pct",
      "avg_headcount"
    ];
    const recognisable = rows.some(
      (r) => r !== null && typeof r === "object" && KNOWN.some((k) => k in r)
    );
    if (!recognisable) {
      throw new Error(
        "event_instances: the served rows carry none of the contract\u2019s fields \u2014 refusing to project a broken body into an honest-looking empty or unmeasurable state"
      );
    }
  }
  return rows.filter((r) => r !== null && typeof r === "object").map((r, index) => ({
    // `?? \`row-${index}\`` mirrors the wrapper's own fallback rather than passing `null` into a
    // field typed as the React identity. It cannot collide with a real id: the view's ids are
    // UUID-shaped.
    group_bid: r.event_instance_id ?? `row-${index}`,
    group_label: r.class_title_clean,
    values: {
      // The panel's fill column. The served slot-weighted ratio, never re-derived.
      value: r.avg_utilisation_pct,
      avg_headcount: r.avg_headcount,
      max_headcount: r.max_headcount,
      tracked_capacity: r.tracked_capacity
    },
    labels: {
      date: r.local_date,
      time: r.start_time_only,
      room: r.entity_label
    }
  }));
}
function asString(value) {
  return typeof value === "string" ? value : void 0;
}
function asStringArray(value) {
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) return value;
  return void 0;
}
function asDayType(value) {
  return value === "weekday" || value === "weekend" ? value : void 0;
}
function asFlag(value) {
  return value === true ? true : void 0;
}
function strictString(value, param, leg) {
  if (value === void 0 || value === null) return void 0;
  if (typeof value === "string") return value;
  throw new Error(
    `${leg}: \`${param}\` must be a string \u2014 got ${JSON.stringify(value)}. Fix the binding; omitting it would ask the endpoint a different question and answer it plausibly.`
  );
}
function strictNumber(value, param, leg) {
  if (value === void 0 || value === null) return void 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new Error(
    `${leg}: \`${param}\` must be a finite number \u2014 got ${JSON.stringify(value)}. Fix the binding; omitting it would fall back to the endpoint's own default, which is a different question answered plausibly.`
  );
}
function resolveBids(twinBid, params) {
  const bids = asStringArray(params.bids);
  if (params.bids !== void 0 && bids === void 0) {
    throw new Error(
      "baseline family: `bids` must be an array of strings \u2014 omit it (or bind `[]`) for whole gym. A malformed value must not widen the read, which is what resolving it to the twin group would do."
    );
  }
  return bids && bids.length > 0 ? bids : [twinBid];
}
function enumeratedScopeParams(params) {
  const groupType = asString(params.group_type)?.trim();
  const groupLevel = asString(params.group_level)?.trim();
  const authored = "group_type" in params || "group_level" in params;
  if (!groupType && !groupLevel) {
    if (authored) {
      throw new Error(
        "baseline enumeration: group_type and group_level were authored but resolved to nothing \u2014 refusing to fall back to a whole-gym read, which is ONE group and not a level"
      );
    }
    return void 0;
  }
  if (!groupType || !groupLevel) {
    throw new Error(
      "baseline enumeration: group_type and group_level must be supplied together (the pair ENUMERATES a level; one without the other is never a request the query service can answer)"
    );
  }
  return { group_type: groupType, group_level: groupLevel };
}
function refuseBothSelectors(level, params, endpoint) {
  const bids = asStringArray(params.bids);
  if (level && bids && bids.length > 0) {
    throw new Error(
      `${endpoint}: pass exactly one of bids or the group_type+group_level pair, not both (the pair ENUMERATES a level; it is not a filter on named bids)`
    );
  }
}
function resolveSingleGroupBid(twinBid, params, endpoint) {
  const bids = resolveBids(twinBid, params);
  if (bids.length > 1) {
    throw new Error(
      `${endpoint}: this binding names ${bids.length} bids, but the panel-facing unwrap serves ONE group per binding (got ${bids.join(", ")}). Bind one bid per panel.`
    );
  }
  return bids[0];
}
function unwrapTwin(data, twinBid, endpoint) {
  if (data != null && typeof data === "object" && twinBid in data) return data[twinBid];
  throw new Error(
    `${endpoint}: response carried no entry for twin "${twinBid}" \u2014 the twin-keyed map is contractually complete, so a missing key is a failed read, not an empty result`
  );
}
function unwrapGroup(byGroup, groupBid, twinBid, endpoint) {
  if (byGroup != null && typeof byGroup === "object" && groupBid in byGroup)
    return byGroup[groupBid];
  throw new Error(
    `${endpoint}: response carried no entry for group "${groupBid}" under twin "${twinBid}" \u2014 the group map is contractually complete, so a missing key is a failed read, not an empty result`
  );
}
function asMeasureTokens(value) {
  const measures = asStringArray(value);
  if (!measures || measures.length === 0) return void 0;
  const unknownTokens = measures.filter((m) => !isBaselineMeasureToken(m));
  if (unknownTokens.length > 0) {
    throw new Error(
      `baseline_summary: unknown measure token(s) ${unknownTokens.join(", ")} \u2014 known tokens are ${BASELINE_MEASURE_TOKENS.join(", ")} (add it in baselineMeasures.ts to request it)`
    );
  }
  return measures;
}
function toCapacitySpare(cell) {
  if (cell == null) return null;
  return {
    capacity: cell[CAPACITY] ?? null,
    trackedCapacity: cell[TRACKED_CAPACITY] ?? null,
    estTypicalPeakHeadcount: cell[SCALED_TYPICAL_PEAK_HEADCOUNT] ?? null,
    estAbsolutePeakHeadcount: cell[SCALED_ABSOLUTE_PEAK_HEADCOUNT] ?? null,
    estAvgHeadcount: cell[SCALED_AVG_HEADCOUNT] ?? null,
    spareAtTypicalPeak: cell[SPARE_AT_TYPICAL_PEAK] ?? null,
    avgSpare: cell[AVG_SPARE] ?? null
  };
}
function baselineViewWireParams(twinBid, params) {
  if (params.include_scalars === true) {
    throw new Error(
      "baseline_view: `include_scalars` was removed at query-service 2.0.0 \u2014 bind the `baseline_summary` endpoint for the scalar block (its measure tokens are exported as BASELINE_MEASURE_TOKENS)"
    );
  }
  const level = enumeratedScopeParams(params);
  refuseBothSelectors(level, params, "baseline_view");
  const metrics = asStringArray(params.metrics);
  return {
    ...level ?? { bids: resolveBids(twinBid, params) },
    // ⚠️ EMPTY IS OMITTED, not sent. `asStringArray([])` returns `[]` and `[]` is truthy, so a bare
    // `metrics ? …` would put it in the wire object and therefore in the KEY — while the wrapper's
    // own `if (params.metrics)` loop appends nothing, so the outbound URL is byte-identical to a
    // binding that omitted `metrics`. That is precisely the two-identical-requests-that-do-not-share
    // defect this change exists to remove, and it is the rule `asMeasureTokens` and `toBatchParams`
    // already apply to their own empty arrays.
    ...metrics && metrics.length > 0 ? { metrics } : {},
    // Omitted unless the binding names one, so the query service's own default applies. The
    // pre-2.0.0 leg hard-coded 'avg' here, which pinned our reading of that default.
    ...params.aggregation === "max" ? { aggregation: "max" } : {},
    ...asFlag(params.peak_hours_only) ? { peak_hours_only: true } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
function baselineSummaryWireParams(twinBid, params) {
  const level = enumeratedScopeParams(params);
  refuseBothSelectors(level, params, "baseline_summary");
  const measures = asMeasureTokens(params.measures);
  const dayType = asDayType(params.day_type);
  return {
    // ⚠️ RESOLVED, not authored. `resolveSingleGroupBid` throws on more than one bid BY DESIGN, and
    // `resolveBids` beneath it substitutes the twin's own bID for the whole-gym case — so `bids: []`
    // and an absent `bids` are the same request and must share one key, which a key over the
    // authored params could not see.
    ...level ?? { bids: [resolveSingleGroupBid(twinBid, params, "baseline_summary")] },
    ...measures ? { measures } : {},
    // A ROW SELECTOR here, not a cell filter: it chooses which pre-aggregated scope row to read.
    ...dayType ? { day_type: dayType } : {},
    ...asFlag(params.peak_hours_only) ? { peak_hours_only: true } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
function peakHoursWireParams(twinBid, params) {
  if (enumeratedScopeParams(params)) {
    throw new Error(
      "peak_hours: the `group_type` + `group_level` enumeration selector is not supported on this endpoint (it has no enumeration arm upstream) \u2014 bind one named bid instead. Leaving the pair here would silently read the whole gym."
    );
  }
  return {
    bids: [resolveSingleGroupBid(twinBid, params, "peak_hours")],
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
function wireDedupKey(twinBid, endpoint, wire) {
  return bindingKey(twinBid, { endpoint, params: wire });
}
function windowDays(window2) {
  if (!window2) return 90;
  const match = /^P(\d+)D$/.exec(window2);
  return match ? Number(match[1]) : 90;
}
function resolveWindow(window2) {
  const days = windowDays(window2);
  const end = /* @__PURE__ */ new Date();
  const start = new Date(end.getTime());
  start.setUTCDate(start.getUTCDate() - days);
  const toDate = (d) => d.toISOString().slice(0, 10);
  return { start: toDate(start), end: toDate(end) };
}
function bindingKey(twinBid, binding) {
  const params = binding.params ?? {};
  const enc = (v) => encodeURIComponent(String(v));
  const parts = Object.keys(params).sort().map((k) => {
    const v = params[k];
    return `${enc(k)}=${Array.isArray(v) ? `[${v.map(enc).join(",")}]` : enc(v)}`;
  });
  return `${enc(twinBid)}|${enc(binding.endpoint)}?${parts.join("&")}`;
}
function stripServerParams(params) {
  const { twin_bid: _t, organisation: _o, ...rest } = params;
  return rest;
}
function toBatchParams(binding) {
  const src = stripServerParams(binding.params ?? {});
  const out = {};
  for (const [k, v] of Object.entries(src)) {
    if (k === "day_type" && v === "all") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (k === "window") {
      const { start, end } = resolveWindow(asString(v));
      out.start = start;
      out.end = end;
      continue;
    }
    out[k] = v;
  }
  return out;
}
function rankingWireParams(params) {
  const leg = "ranking";
  const measure = strictString(params.measure, "measure", leg);
  if (measure === void 0) {
    throw new Error(
      "ranking: `measure` is required by /api/ranking and has no default \u2014 author it on the binding (Equipment Utilisation uses `utilisation` and `avg_users`; Operations `utilisation`)."
    );
  }
  const direction = strictString(params.direction, "direction", leg);
  if (direction !== void 0 && direction !== "top" && direction !== "bottom") {
    throw new Error(
      `ranking: \`direction\` must be "top" or "bottom" \u2014 got ${JSON.stringify(direction)}.`
    );
  }
  const n = strictNumber(params.n, "n", leg);
  const lastMinutes = strictNumber(params.last_minutes, "last_minutes", leg);
  const grain = strictString(params.grain, "grain", leg);
  const groupType = strictString(params.group_type, "group_type", leg);
  const groupLevel = strictString(params.group_level, "group_level", leg);
  const start = strictString(params.start, "start", leg);
  const end = strictString(params.end, "end", leg);
  const bids = asStringArray(params.bids);
  return {
    measure,
    ...direction !== void 0 ? { direction } : {},
    ...n !== void 0 ? { n } : {},
    ...grain !== void 0 ? { grain } : {},
    ...groupType !== void 0 ? { group_type: groupType } : {},
    ...groupLevel !== void 0 ? { group_level: groupLevel } : {},
    ...start !== void 0 ? { start } : {},
    ...end !== void 0 ? { end } : {},
    ...lastMinutes !== void 0 ? { last_minutes: lastMinutes } : {},
    ...asFlag(params.exclude_unknown) ? { exclude_unknown: true } : {},
    ...bids !== void 0 && bids.length > 0 ? { bids } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
function timeseriesWireParams(params) {
  const metrics = asStringArray(params.metrics);
  if (metrics === void 0 || metrics.length === 0) {
    throw new Error(
      "timeseries: this binding must author `metrics` as a non-empty array of strings \u2014 it is required upstream, and omitting a malformed one would silently read the default metric set"
    );
  }
  const start = asString(params.start);
  const end = asString(params.end);
  if (start === void 0 || end === void 0) {
    throw new Error(
      "timeseries: this binding must author both `start` and `end` \u2014 the window is required upstream, and a half-authored range would read a window the reader never chose"
    );
  }
  const bids = asStringArray(params.bids);
  return {
    start,
    end,
    metrics,
    ...asString(params.grain) !== void 0 ? { grain: asString(params.grain) } : {},
    ...bids !== void 0 && bids.length > 0 ? { bids } : {},
    ...asString(params.group_type) !== void 0 ? { group_type: asString(params.group_type) } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
var MANUFACTURER_SCALAR_MEASURES = {
  machines: (r) => r.machines ?? null,
  tracked_capacity: (r) => r.tracked_capacity ?? null,
  utilisation_pct: (r) => r.utilisation_pct ?? null,
  peak_utilisation_pct: (r) => r.peak_utilisation_pct ?? null,
  hours_per_unit_per_week: (r) => r.hours_per_unit_per_week ?? null
};
var MANUFACTURER_BY_HOUR = "by_hour";
var MANUFACTURER_TABLE = "table";
var MANUFACTURER_COMPARISON_MEASURES = [
  ...Object.keys(MANUFACTURER_SCALAR_MEASURES),
  MANUFACTURER_BY_HOUR,
  MANUFACTURER_TABLE
];
function manufacturerRowIdentity(row, index) {
  if (row.split_bid) return row.split_bid;
  const key = Array.isArray(row.split_key) ? JSON.stringify(row.split_key) : "";
  return key ? `split:${key}` : `split:row-${index}`;
}
var blankToNull = (value) => typeof value === "string" && value.trim() !== "" ? value : null;
var isNoManufacturerRow = (row) => (
  // ⚠️ `=== null` ONLY, and NEVER `undefined`. The contract declares the field required, so `undefined`
  // means the SERVICE IS OLDER THAN 2.5.0 — not that the row is the honest one. Treating the two the
  // same made every row "honest" on a 2.4.0 envelope, which collapsed the labels to one em-dash column
  // and (with the name column suppressed) left the table showing anonymous rows of numbers. The
  // renderer and TL-Query-Service deploy independently, so that window is real. Defaulting to "not the
  // honest row" is the safe direction: it keeps the repeat un-collapsed rather than erasing identity.
  row.manufacturer_bid === null
);
var joinManufacturerAncestry = (manufacturerLabel, ownLabel, splitDepth = 2) => {
  const own = blankToNull(ownLabel);
  const top = blankToNull(manufacturerLabel);
  if (top === null) return own ?? "\u2014";
  if (own === null || splitDepth <= 1 || own === top) return top;
  return `${top} ${own}`;
};
function ancestorLabels(row) {
  const depth = Array.isArray(row.split_key) && row.split_key.length > 0 ? row.split_key.length : 1;
  const out = {};
  const levels = [
    ["manufacturer_label", blankToNull(row.manufacturer_label)],
    ["series_label", blankToNull(row.series_label)],
    ["model_label", blankToNull(row.model_label)]
  ];
  const honest = isNoManufacturerRow(row);
  levels.forEach(([key, value], index) => {
    if (index >= depth) return;
    if (honest && index > 0) return;
    out[key] = value;
  });
  return out;
}
function projectManufacturerComparison(env, measure) {
  const rows = Array.isArray(env.data) ? env.data : [];
  if (measure === MANUFACTURER_BY_HOUR) {
    const byMetric = {
      utilisation_pct: {},
      demand_hours_per_week: {}
    };
    rows.forEach((row, index) => {
      const id = manufacturerRowIdentity(row, index);
      const label = joinManufacturerAncestry(
        row.manufacturer_label ?? null,
        row.label ?? null,
        Array.isArray(row.split_key) && row.split_key.length > 0 ? row.split_key.length : 1
      );
      const points = Array.isArray(row.by_hour) ? row.by_hour : [];
      byMetric.utilisation_pct[id] = {
        meta: { label },
        series: points.map((p) => [p.hod, p.utilisation_pct])
      };
      byMetric.demand_hours_per_week[id] = {
        meta: { label },
        series: points.map((p) => [p.hod, p.demand_hours_per_week])
      };
    });
    return { data: byMetric, stats: env.stats };
  }
  if (measure === MANUFACTURER_TABLE) {
    return {
      data: rows.map((row, index) => ({
        group_bid: manufacturerRowIdentity(row, index),
        group_label: row.label ?? "\u2014",
        values: Object.fromEntries(
          Object.entries(MANUFACTURER_SCALAR_MEASURES).map(([key, read2]) => [key, read2(row)])
        ),
        // The dimension cells (2.5.0). Sparse BY DESIGN — see `ancestorLabels`: an omitted key is
        // "this level does not apply" and a present-but-null one is "BI supplied no label".
        labels: ancestorLabels(row)
      })),
      stats: env.stats
    };
  }
  const read = Object.prototype.hasOwnProperty.call(MANUFACTURER_SCALAR_MEASURES, measure) ? MANUFACTURER_SCALAR_MEASURES[measure] : void 0;
  if (!read) throw new Error(`manufacturer_comparison: unknown measure "${measure}"`);
  return {
    data: rows.map((row, index) => ({
      group_bid: manufacturerRowIdentity(row, index),
      group_label: row.label ?? "\u2014",
      value: read(row)
    })),
    stats: env.stats
  };
}
function manufacturerWireParams(params) {
  const bids = asStringArray(params.bids);
  const manufacturerBids = asStringArray(params.manufacturer_bids);
  return {
    ...bids && bids.length > 0 ? { bids } : {},
    ...manufacturerBids && manufacturerBids.length > 0 ? { manufacturer_bids: manufacturerBids } : {},
    ...asString(params.split_level) !== void 0 ? { split_level: asString(params.split_level) } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
var EQUIPMENT_SIDE_MEASURES = {
  machines: (r) => r.machines ?? null,
  tracked_capacity: (r) => r.tracked_capacity ?? null,
  utilisation_pct: (r) => r.utilisation_pct ?? null,
  peak_utilisation_pct: (r) => r.peak_utilisation_pct ?? null,
  hours_per_unit_per_week: (r) => r.hours_per_unit_per_week ?? null,
  total_demand_hours_per_week: (r) => totalDemandHoursPerWeek(r.by_hour)
};
function totalDemandHoursPerWeek(byHour) {
  if (!Array.isArray(byHour) || byHour.length === 0) return null;
  let total = 0;
  for (const point of byHour) {
    const value = point?.demand_hours_per_week;
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    total += value;
  }
  return total;
}
var EQUIPMENT_CARD = "card";
var EQUIPMENT_BY_HOUR = "by_hour";
var EQUIPMENT_COMPARISON_MEASURES = [EQUIPMENT_CARD, EQUIPMENT_BY_HOUR];
function equipmentSideIdentity(row) {
  return {
    twin_bid: row.twin_bid,
    group_bid: row.group_bid ?? null,
    group_label: row.group_label ?? null,
    group_level: row.group_level ?? null,
    whole_gym: row.whole_gym === true,
    // Carried so a panel can caveat a peak figure computed over less than half the fleet. It is only
    // ever true when there IS a figure, so it never sits beside an em dash.
    peak_partial: row.peak_partial === true,
    peak_coverage: row.peak_coverage ?? null,
    typed_coverage: row.typed_coverage ?? null
  };
}
function selectEquipmentSideRow(rows, twinBid) {
  if (rows.length === 0) return null;
  if (rows.length > 1) {
    throw new Error(
      `equipment_comparison: this side's \`bids\` selected ${rows.length} groups at twin "${twinBid}", and the endpoint deliberately does NOT prune (Cardio + Treadmills are two rows) \u2014 bind at most ONE bid per side, because a side is one (twin, group)`
    );
  }
  return rows[0];
}
function projectEquipmentComparison(env, measure, twinBid) {
  if (!Array.isArray(env.data)) {
    throw new Error(
      "equipment_comparison: response `data` was not a row list \u2014 that is a failed read, not an empty comparison, and this endpoint answers an honest empty with `[]` at 200"
    );
  }
  const rows = env.data;
  const row = selectEquipmentSideRow(rows, twinBid);
  if (row && row.twin_bid !== twinBid) {
    throw new Error(
      `equipment_comparison: the row returned for this side describes twin "${row.twin_bid}", not the requested "${twinBid}" \u2014 the twin selector did not survive the request`
    );
  }
  if (measure === EQUIPMENT_BY_HOUR) {
    const points = Array.isArray(row?.by_hour) ? row.by_hour : [];
    return {
      data: {
        side: row ? equipmentSideIdentity(row) : null,
        series: {
          utilisation_pct: points.map((p) => [p.hod, p.utilisation_pct ?? null]),
          demand_hours_per_week: points.map((p) => [p.hod, p.demand_hours_per_week ?? null])
        }
      },
      stats: env.stats
    };
  }
  if (measure === EQUIPMENT_CARD) {
    return {
      data: {
        side: row ? equipmentSideIdentity(row) : null,
        values: row ? Object.fromEntries(
          Object.entries(EQUIPMENT_SIDE_MEASURES).map(([key, read]) => [key, read(row)])
        ) : null
      },
      stats: env.stats
    };
  }
  throw new Error(`equipment_comparison: unknown measure "${measure}"`);
}
function equipmentWireParams(params) {
  const bids = asStringArray(params.bids);
  if (params.bids !== void 0 && bids === void 0) {
    throw new Error(
      "equipment_comparison: `bids` must be an array of strings \u2014 omit it (or bind `[]`) for whole gym. A malformed value must not widen the read, which is what dropping it would do."
    );
  }
  return {
    ...bids && bids.length > 0 ? { bids } : {},
    ...asFlag(params.debug) ? { debug: true } : {}
  };
}
function abortError() {
  const err = new Error("aborted");
  err.name = "AbortError";
  return err;
}
function createMiddlewareDataProvider(config2) {
  const { middlewareApi, accessToken } = config2;
  const inFlight2 = /* @__PURE__ */ new Map();
  let pending = [];
  let flushScheduled = false;
  function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    queueMicrotask(flushBatch);
  }
  function flushBatch() {
    const batch = pending;
    pending = [];
    flushScheduled = false;
    const byTwin = /* @__PURE__ */ new Map();
    for (const item of batch) {
      if (item.signal?.aborted) {
        item.reject(abortError());
        continue;
      }
      const list = byTwin.get(item.twinBid);
      if (list) list.push(item);
      else byTwin.set(item.twinBid, [item]);
    }
    byTwin.forEach((items, twinBid) => {
      const byKey = /* @__PURE__ */ new Map();
      for (const item of items) {
        const list = byKey.get(item.key);
        if (list) list.push(item);
        else byKey.set(item.key, [item]);
      }
      const requests = [];
      const idToKey = /* @__PURE__ */ new Map();
      let i = 0;
      byKey.forEach((waiters, key) => {
        const id = `b${i++}`;
        idToKey.set(id, key);
        requests.push({ id, endpoint: waiters[0].endpoint, params: waiters[0].params });
      });
      postDashboardBatch(middlewareApi, accessToken, twinBid, requests).then((res) => {
        idToKey.forEach((key, id) => {
          const waiters = byKey.get(key) ?? [];
          const result = res?.results?.[id];
          const endpoint = waiters[0]?.endpoint;
          if (!endpoint) return;
          if (result && result.status >= 200 && result.status < 300) {
            const env = result.body;
            const project = BATCH_PROJECTORS[endpoint];
            let projected = env;
            if (project) {
              try {
                projected = shareSafe(project(env));
              } catch (cause) {
                const err = new Error(`batch item "${endpoint}" returned an unprojectable body`);
                err.cause = cause;
                for (const w of waiters) w.reject(err);
                return;
              }
            }
            for (const w of waiters) w.resolve(projected);
          } else {
            const err = new Error(
              `batch item "${endpoint}" failed (status ${result?.status ?? "missing"})`
            );
            for (const w of waiters) w.reject(err);
          }
        });
      }).catch((err) => {
        byKey.forEach((items2) => {
          for (const w of items2) w.reject(err);
        });
      });
    });
  }
  async function fetchBaselineViewScoped(twinBid, wire) {
    const env = await getBaselineViewScoped(middlewareApi, accessToken, {
      twin_bids: [twinBid],
      ...wire
    });
    return { data: shareSafe(unwrapTwin(env.data, twinBid, "baseline_view")), stats: env.stats };
  }
  async function fetchBaselineSummary(twinBid, wire) {
    const groupBid = wire.bids?.[0];
    if (groupBid === void 0) {
      const env2 = await getBaselineSummary(middlewareApi, accessToken, {
        twin_bids: [twinBid],
        ...wire
      });
      return {
        data: shareSafe(unwrapTwin(env2.data, twinBid, "baseline_summary")),
        stats: env2.stats
      };
    }
    const env = await getBaselineSummary(middlewareApi, accessToken, {
      twin_bids: [twinBid],
      ...wire
    });
    const byGroup = unwrapTwin(env.data, twinBid, "baseline_summary");
    const cell = shareSafe(unwrapGroup(byGroup, groupBid, twinBid, "baseline_summary"));
    return { data: cell, capacitySpare: toCapacitySpare(cell), stats: env.stats };
  }
  async function fetchPeakHours(twinBid, wire) {
    const env = await getPeakHours(middlewareApi, accessToken, { twin_bids: [twinBid], ...wire });
    const byGroup = unwrapTwin(env.data, twinBid, "peak_hours");
    const windows = shareSafe(
      unwrapGroup(byGroup, wire.bids[0], twinBid, "peak_hours")
    );
    return { data: windows, stats: env.stats };
  }
  async function fetchManufacturerComparisonRaw(twinBids, params) {
    return getManufacturerComparison(middlewareApi, accessToken, {
      twin_bids: [...twinBids],
      ...manufacturerWireParams(params)
    });
  }
  function shareSafe(payload) {
    if (process.env.NODE_ENV !== "production" && payload !== null && typeof payload === "object") {
      Object.freeze(payload);
    }
    return payload;
  }
  function perCall(key, run) {
    const existing = inFlight2.get(key);
    if (existing) return existing;
    const p = run().finally(() => {
      inFlight2.delete(key);
    });
    inFlight2.set(key, p);
    return p;
  }
  return {
    fetch(binding, context, signal) {
      const twinBid = resolveBindingTwin(binding, context);
      if (!twinBid) {
        return Promise.reject(
          new Error(
            "createMiddlewareDataProvider: a twin is required \u2014 the binding must name one (`twin_bid`) or the host context must supply `twinBid`"
          )
        );
      }
      if (BATCHABLE.has(binding.endpoint)) {
        const sent = toBatchParams(binding);
        const key = bindingKey(twinBid, { endpoint: binding.endpoint, params: sent });
        return new Promise((resolve, reject) => {
          pending.push({
            twinBid,
            key,
            endpoint: binding.endpoint,
            params: sent,
            signal,
            resolve,
            reject
          });
          scheduleFlush();
        });
      }
      if (binding.endpoint === "baseline_view") {
        let wire;
        try {
          wire = baselineViewWireParams(twinBid, binding.params ?? {});
        } catch (err) {
          return Promise.reject(err);
        }
        return perCall(
          wireDedupKey(twinBid, binding.endpoint, wire),
          () => fetchBaselineViewScoped(twinBid, wire)
        );
      }
      if (binding.endpoint === "baseline_summary") {
        let wire;
        try {
          wire = baselineSummaryWireParams(twinBid, binding.params ?? {});
        } catch (err) {
          return Promise.reject(err);
        }
        return perCall(
          wireDedupKey(twinBid, binding.endpoint, wire),
          () => fetchBaselineSummary(twinBid, wire)
        );
      }
      if (binding.endpoint === "peak_hours") {
        let wire;
        try {
          wire = peakHoursWireParams(twinBid, binding.params ?? {});
        } catch (err) {
          return Promise.reject(err);
        }
        return perCall(
          wireDedupKey(twinBid, binding.endpoint, wire),
          () => fetchPeakHours(twinBid, wire)
        );
      }
      if (binding.endpoint === "manufacturer_comparison") {
        const params = binding.params ?? {};
        const measure = asString(params.measure);
        if (measure === void 0 || !MANUFACTURER_COMPARISON_MEASURES.includes(measure)) {
          return Promise.reject(
            new Error(
              `manufacturer_comparison: this binding must name one \`measure\` the provider can project (got ${measure === void 0 ? "none" : `"${measure}"`}); known measures are ${MANUFACTURER_COMPARISON_MEASURES.join(", ")}`
            )
          );
        }
        const plural = resolveBindingTwins(binding);
        if (plural.kind === "invalid") {
          return Promise.reject(
            new Error(
              `manufacturer_comparison: this binding's \`twin_bids\` cannot be used \u2014 ${plural.why}`
            )
          );
        }
        const twinBids = plural.kind === "twins" ? plural.twins : [twinBid];
        const refusal = comparisonBudgetRefusal(twinBids.length, params);
        if (refusal) return Promise.reject(new Error(refusal));
        const wireKey = wireDedupKey(
          JSON.stringify(twinBids),
          binding.endpoint,
          manufacturerWireParams(params)
        );
        return perCall(wireKey, () => fetchManufacturerComparisonRaw(twinBids, params)).then(
          (env) => projectManufacturerComparison(env, measure)
        );
      }
      if (binding.endpoint === "equipment_comparison") {
        const params = binding.params ?? {};
        const measure = asString(params.measure);
        if (measure === void 0 || !EQUIPMENT_COMPARISON_MEASURES.includes(measure)) {
          return Promise.reject(
            new Error(
              `equipment_comparison: this binding must name one \`measure\` the provider can project (got ${measure === void 0 ? "none" : `"${measure}"`}); known measures are ${EQUIPMENT_COMPARISON_MEASURES.join(", ")}`
            )
          );
        }
        let wire;
        try {
          wire = equipmentWireParams(params);
        } catch (err) {
          return Promise.reject(err);
        }
        const wireKey = wireDedupKey(twinBid, binding.endpoint, wire);
        return perCall(
          wireKey,
          () => getEquipmentComparison(middlewareApi, accessToken, { twin_bids: [twinBid], ...wire })
        ).then((env) => projectEquipmentComparison(env, measure, twinBid));
      }
      if (binding.endpoint === "ranking") {
        const params = binding.params ?? {};
        const plural = resolveBindingTwins(binding);
        if (plural.kind === "invalid") {
          return Promise.reject(
            new Error(`ranking: this binding's \`twin_bids\` cannot be used \u2014 ${plural.why}`)
          );
        }
        const twinBids = plural.kind === "twins" ? plural.twins : [twinBid];
        let wire;
        try {
          wire = rankingWireParams(params);
        } catch (err) {
          return Promise.reject(err);
        }
        const wireKey = wireDedupKey(JSON.stringify(twinBids), binding.endpoint, wire);
        return perCall(
          wireKey,
          () => getRanking(middlewareApi, accessToken, { twin_bids: [...twinBids], ...wire })
        );
      }
      if (binding.endpoint === "timeseries") {
        const params = binding.params ?? {};
        const plural = resolveBindingTwins(binding);
        if (plural.kind === "invalid") {
          return Promise.reject(
            new Error(`timeseries: this binding's \`twin_bids\` cannot be used \u2014 ${plural.why}`)
          );
        }
        const twinBids = plural.kind === "twins" ? plural.twins : [twinBid];
        let wire;
        try {
          wire = timeseriesWireParams(params);
        } catch (err) {
          return Promise.reject(err);
        }
        const wireKey = wireDedupKey(JSON.stringify(twinBids), binding.endpoint, wire);
        return perCall(
          wireKey,
          () => getTimeseriesScoped(middlewareApi, accessToken, { twin_bids: [...twinBids], ...wire })
        );
      }
      if (binding.endpoint === "peak_hours_view") {
        return Promise.reject(
          new Error(
            "createMiddlewareDataProvider: `peak_hours_view` was removed at query-service 2.0.0 \u2014 bind `peak_hours` instead (a GET, `bids` rather than `entity_bid`)"
          )
        );
      }
      return Promise.reject(
        new Error(
          `createMiddlewareDataProvider: no route mapping for endpoint "${binding.endpoint}" (add it in the checkpoint that first binds it)`
        )
      );
    }
  };
}

// src/middleware/dgraph/createNamespace.ts
import axios26 from "axios";
var createNamespace = async (baseUrl, accessToken, data, options) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.dgraph.createNamespace;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await axios26.post(url, data, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    return response.data;
  } catch (error) {
    const e = error;
    console.error(
      "Failed to create namespace:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    throw error;
  }
};

// src/middleware/edge-device/createEdgeDevice.ts
import axios27 from "axios";
var createEdgeDevice = async (baseUrl, accessToken, bID, payload) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${bID}/devices`;
    const record = await axios27.post(url, payload, { headers: headers3 });
    return record;
  } catch (error) {
    console.error("Error creating twin device:", error);
    return void 0;
  }
};

// src/middleware/edge-device/deleteEdgeDevice.ts
import axios28 from "axios";
var deleteEdgeDevice = async (baseUrl, accessToken, bID, deviceId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${bID}/devices/${deviceId}`;
    const record = await axios28.delete(url, { headers: headers3 });
    return record;
  } catch (error) {
    console.error("Error deleting twin device:", error);
    return void 0;
  }
};

// src/middleware/edge-device/getAllActiveEdgeDevices.ts
import axios29 from "axios";
var getAllActiveEdgeDevices = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/devices/active`;
    const response = await axios29.get(url, { headers: headers3 });
    return response?.data;
  } catch (error) {
    console.error("Error fetching all active edge devices:", error);
    return void 0;
  }
};

// src/middleware/edge-device/getEdgeDeviceById.ts
import axios30 from "axios";
var getEdgeDeviceById = async (baseUrl, accessToken, bID, deviceId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${bID}/devices/${deviceId}`;
    const record = await axios30.get(url, { headers: headers3 });
    return record?.data;
  } catch (error) {
    console.error("Error fetching twin device by ID:", error);
    return void 0;
  }
};

// src/middleware/edge-device/getCountAggregation.ts
import axios31 from "axios";
var getCountAggregation = async (baseUrl, accessToken, filters = {}) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const params = new URLSearchParams();
    if (filters.edgeDeviceBid) params.set("edge_device_bid", filters.edgeDeviceBid);
    if (filters.observerBid) params.set("observer_bid", filters.observerBid);
    if (filters.kind) params.set("kind", filters.kind);
    if (filters.limit !== void 0) params.set("limit", String(filters.limit));
    const query = params.toString();
    const url = `${baseUrl}api/twin-sense-count-aggregation${query ? `?${query}` : ""}`;
    const response = await axios31.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return response?.data;
  } catch (error) {
    console.error(
      "Error fetching count aggregation:",
      axios31.isAxiosError(error) ? `${error.response?.status ?? "network"} ${error.message}` : String(error)
    );
    return void 0;
  }
};

// src/middleware/edge-device/getCountAggregationLog.ts
var getCountAggregationLog = async (baseUrl, accessToken, edgeDeviceBid, limit) => (
  // No `kind` filter: this page shows whatever that device produced. A composite carries no edge
  // device, so filtering by one already excludes composites — but if one ever arrives WITH a
  // device it must be visible here rather than silently rendered as an observer, which is why the
  // page has a Kind column.
  getCountAggregation(baseUrl, accessToken, { edgeDeviceBid, limit })
);

// src/middleware/edge-device/getEdgeDevices.ts
import axios32 from "axios";
var getEdgeDevices = async (baseUrl, accessToken, bID) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${bID}/devices`;
    const record = await axios32.get(url, { headers: headers3 });
    return record?.data;
  } catch (error) {
    console.error("Error fetching twin devices:", error);
    return void 0;
  }
};

// src/middleware/edge-device/claimDevice.ts
import axios33 from "axios";
var claimDevice = async (baseUrl, accessToken, twinBid, authCode) => {
  const routes = routes_default(baseUrl);
  try {
    const res = await axios33.post(
      routes.post.twins.claimDevice(twinBid),
      { authCode },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return { ok: true, device: res.data };
  } catch (error) {
    if (axios33.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 429) {
        return {
          ok: false,
          reason: "rate_limited",
          message: "Too many attempts. Please wait a moment and try again."
        };
      }
      if (status === 400) {
        return { ok: false, reason: "invalid", message: "Invalid or already-used claim code." };
      }
      if (status === 403) {
        return {
          ok: false,
          reason: "error",
          message: "You don't have permission to claim a box for this twin."
        };
      }
    }
    return { ok: false, reason: "error", message: "Could not claim the box. Please try again." };
  }
};

// src/middleware/edge-device/provisionDevice.ts
import axios34 from "axios";
var provisionDevice = async (baseUrl, accessToken, input) => {
  const routes = routes_default(baseUrl);
  try {
    const res = await axios34.post(
      routes.post.deviceClaims,
      { mac_address: input.mac_address, bid: input.bid },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return {
      ok: true,
      code: res.data.code,
      mac_address: res.data.mac_address,
      bid: res.data.bid
    };
  } catch (error) {
    if (axios34.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 409) {
        const message = error.response?.data?.message ?? "This box is already provisioned.";
        return { ok: false, reason: "duplicate", message };
      }
      console.error("provisionDevice failed with status:", status);
    } else {
      console.error("provisionDevice failed (non-axios error).");
    }
    return { ok: false, reason: "error", message: "Could not mint the box. Please try again." };
  }
};

// src/middleware/edge-device/getLatestOpStatuses.ts
import axios35 from "axios";
var getLatestOpStatuses = async (baseUrl, accessToken, deviceIds) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-sense-messages/latest-op-statuses`;
    const response = await axios35.post(url, { device_ids: deviceIds }, { headers: headers3 });
    return response?.data;
  } catch (error) {
    console.error("Error fetching latest OpStatus metrics:", error);
    return void 0;
  }
};

// src/middleware/edge-device/deriveDeviceStatus.ts
var DEFAULT_DEVICE_STATUS_THRESHOLDS = {
  offlineThresholdS: 90,
  bootGraceS: 120,
  detectionFreshS: 120
};
var isFresh = (ageS, barS) => typeof ageS === "number" && ageS >= 0 && ageS <= barS;
var deriveDeviceStatus = (metrics, nowMs, thresholds = DEFAULT_DEVICE_STATUS_THRESHOLDS) => {
  const lastMs = metrics?.created_at ? Date.parse(metrics.created_at) : NaN;
  const lastSeenAgeS = Number.isFinite(lastMs) ? Math.max(0, (nowMs - lastMs) / 1e3) : Infinity;
  if (!metrics || lastSeenAgeS > thresholds.offlineThresholdS) {
    return { lifecycle: "OFFLINE", health: "UNKNOWN", reasons: [], lastSeenAgeS };
  }
  const rtspReceived = metrics.rtsp_config_received ?? false;
  const fsReceived = metrics.frame_split_config_received ?? false;
  const observers = metrics.observers_configured ?? 0;
  const configured = rtspReceived && fsReceived && observers > 0;
  let lifecycle;
  if (typeof metrics.uptime_s === "number" && metrics.uptime_s < thresholds.bootGraceS && !configured) {
    lifecycle = "BOOTING";
  } else if (!rtspReceived) {
    lifecycle = "AWAITING_CAMERA_CONFIG";
  } else if (!fsReceived || observers === 0) {
    lifecycle = "AWAITING_FRAMESPLIT_CONFIG";
  } else if (isFresh(metrics.detections_pose_age_s, thresholds.detectionFreshS)) {
    lifecycle = "STREAMING";
  } else {
    lifecycle = "CONFIGURED";
  }
  const reasons = [];
  if (metrics.storage_status === "degraded" || metrics.rootfs_read_only === true || metrics.store_writable === false || metrics.jetstream_persist_ok === false) {
    if (metrics.rootfs_read_only === true) reasons.push("SD card read-only");
    else if (metrics.store_writable === false) reasons.push("storage not writable");
    else if (metrics.jetstream_persist_ok === false) reasons.push("failover buffer off");
    else reasons.push("storage degraded");
  }
  const downServices = [];
  if (metrics.camera_config_active === false) downServices.push("camera_config");
  if (metrics.frame_splitter_active === false) downServices.push("frame_splitter");
  if (metrics.frame_processing_active === false) downServices.push("frame_processing");
  if (metrics.mqtt_bridge_active === false) downServices.push("mqtt_bridge");
  if (downServices.length > 0) reasons.push(`service down: ${downServices.join(", ")}`);
  const camsConfigured = metrics.cameras_configured ?? 0;
  const camsStreaming = metrics.cameras_streaming ?? -1;
  if (camsConfigured > 0 && camsStreaming >= 0 && camsStreaming < camsConfigured) {
    reasons.push(`${camsStreaming}/${camsConfigured} cameras streaming`);
  }
  if (configured) {
    if (metrics.raw_frames_flowing === false) reasons.push("raw frames stalled");
    if (metrics.sampled_frames_flowing === false) reasons.push("sampled frames stalled");
  }
  const health = reasons.length > 0 ? "DEGRADED" : "HEALTHY";
  return { lifecycle, health, reasons, lastSeenAgeS };
};
var toClientStatus = (derived) => {
  if (derived.lifecycle === "OFFLINE") return "Offline";
  if (derived.health === "DEGRADED") return "Needs attention";
  if (derived.lifecycle === "STREAMING") return "Operating normally";
  return "Setting up";
};

// src/middleware/edge-device/getControlEvents.ts
import axios36 from "axios";
var getControlEvents = async (baseUrl, accessToken, deviceId, limit, since, until) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const params = [`device_id=${deviceId}`];
    if (limit !== void 0) {
      params.push(`limit=${limit}`);
    }
    if (since) {
      params.push(`since=${encodeURIComponent(since)}`);
    }
    if (until) {
      params.push(`until=${encodeURIComponent(until)}`);
    }
    const url = `${baseUrl}api/twin-sense-messages/control-events?${params.join("&")}`;
    const response = await axios36.get(url, { headers: headers3 });
    return response?.data;
  } catch (error) {
    console.error("Error fetching control events:", error);
    return void 0;
  }
};

// src/middleware/edge-device/getOpStatusLog.ts
import axios37 from "axios";
var getDecodedOpStatusLog = async (baseUrl, accessToken, deviceId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-sense-messages/op-status-log/decoded?device_id=${deviceId}`;
    const response = await axios37.get(url, { headers: headers3 });
    return response?.data;
  } catch (error) {
    console.error("Error fetching decoded OpStatus log:", error);
    return void 0;
  }
};

// src/middleware/edge-device/updateEdgeDevice.ts
import axios38 from "axios";
var updateEdgeDevice = async (baseUrl, accessToken, bID, deviceId, payload) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${bID}/devices/${deviceId}`;
    const record = await axios38.patch(url, payload, { headers: headers3 });
    return record;
  } catch (error) {
    console.error("Error updating twin device:", error);
    return void 0;
  }
};

// src/middleware/edge-device/device-outbound-message/getFrameSplitConfig.ts
import axios39 from "axios";
var getFrameSplitConfig = async (baseUrl, accessToken, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-sense-configs/${configId}/frame-split-config`;
    const response = await axios39.get(url, { headers: headers3 });
    return response?.data;
  } catch (error) {
    console.error("Error fetching frame split config:", error);
    return void 0;
  }
};

// src/middleware/platform-health/getPlatformHealthTree.ts
import axios40 from "axios";
var getPlatformHealthTree = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/platform-health/tree`;
    const record = await axios40.get(url, { headers: headers3 });
    return record?.data;
  } catch (error) {
    if (axios40.isAxiosError(error)) {
      console.error(
        `Error fetching platform health tree: ${error.message} (status ${error.response?.status ?? "n/a"})`
      );
    } else {
      console.error(
        "Error fetching platform health tree:",
        error instanceof Error ? error.message : error
      );
    }
    return void 0;
  }
};

// src/middleware/platform-health/getPlatformHealthHistory.ts
import axios41 from "axios";
var getPlatformHealthHistory = async (baseUrl, accessToken, window2) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/platform-health/history?window=${encodeURIComponent(window2)}`;
    const record = await axios41.get(url, { headers: headers3 });
    return record?.data;
  } catch (error) {
    if (axios41.isAxiosError(error)) {
      console.error(
        `Error fetching platform health history: ${error.message} (status ${error.response?.status ?? "n/a"})`
      );
    } else {
      console.error(
        "Error fetching platform health history:",
        error instanceof Error ? error.message : error
      );
    }
    return void 0;
  }
};

// src/middleware/google-sheets/googleSheetsBackup.ts
var createSheetBackup = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/google-sheets/create-backup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Failed to create sheet backup: ${response.status} ${errorData}`);
  }
  return response.json();
};
var restoreFromBackup = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/google-sheets/restore-from-backup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Failed to restore from backup: ${response.status} ${errorData}`);
  }
  return response.json();
};
var extractSheetIdFromUrl = (url) => {
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : null;
};
var generateSheet = async (baseUrl, accessToken, twinBid) => {
  const response = await fetch(`${baseUrl}api/google-sheets/generate-sheet`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      twin_bid: twinBid
    })
  });
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Failed to generate sheet: ${response.status} ${errorData}`);
  }
  return response.json();
};
var preserveMetadataDuringFmlUpload = async (baseUrl, accessToken, googleSheetUrl, twinBid, fmlUploadCallback, onUpdate) => {
  try {
    const originalSheetId = extractSheetIdFromUrl(googleSheetUrl);
    if (!originalSheetId) {
      throw new Error("Invalid Google Sheet URL format");
    }
    onUpdate?.("Creating backup of existing Google Sheet...", "info");
    const backupResult = await createSheetBackup(baseUrl, accessToken, {
      originalSheetId,
      twinBid
    });
    onUpdate?.(`Backup created`, "success");
    onUpdate?.("Executing FML upload...", "info");
    const fmlUploadResult = await fmlUploadCallback();
    if (!fmlUploadResult.success) {
      return {
        success: false,
        restoredCount: 0,
        backupSheetId: backupResult.backupSheetId,
        backupSheetUrl: backupResult.backupSheetUrl,
        error: fmlUploadResult.error || "FML upload failed",
        fmlUploadSucceeded: false,
        fmlFileUrl: fmlUploadResult.fmlFileUrl
      };
    }
    console.log(`FML upload completed successfully`);
    try {
      console.log(`Restoring metadata from backup...`);
      onUpdate?.("Restoring metadata from backup...", "info");
      const restoreResult = await restoreFromBackup(baseUrl, accessToken, {
        backupSheetId: backupResult.backupSheetId,
        twinBid
      });
      console.log(`Metadata restored to ${restoreResult.restoredCount} entities`);
      try {
        console.log(`Generating fresh Google Sheet with restored metadata...`);
        onUpdate?.("Generating fresh Google Sheet with restored metadata...", "info");
        const newSheetResult = await generateSheet(baseUrl, accessToken, twinBid);
        console.log(`Fresh sheet generated: ${newSheetResult.sheet_url}`);
        return {
          success: true,
          restoredCount: restoreResult.restoredCount,
          backupSheetId: backupResult.backupSheetId,
          backupSheetUrl: backupResult.backupSheetUrl,
          newSheetUrl: newSheetResult.sheet_url,
          newSheetId: newSheetResult.sheet_id,
          fmlUploadSucceeded: true,
          fmlFileUrl: fmlUploadResult.fmlFileUrl
        };
      } catch (error) {
        console.error("Sheet generation failed:", error);
        return {
          success: false,
          restoredCount: restoreResult.restoredCount,
          backupSheetId: backupResult.backupSheetId,
          backupSheetUrl: backupResult.backupSheetUrl,
          fmlUploadSucceeded: true,
          error: error instanceof Error ? error.message : "Failed to generate new sheet",
          fmlFileUrl: fmlUploadResult.fmlFileUrl
        };
      }
    } catch (error) {
      console.error("Restoration failed:", error);
      return {
        success: false,
        restoredCount: 0,
        backupSheetId: backupResult.backupSheetId,
        backupSheetUrl: backupResult.backupSheetUrl,
        fmlUploadSucceeded: true,
        error: error instanceof Error ? error.message : "Failed to restore metadata",
        fmlFileUrl: fmlUploadResult.fmlFileUrl
      };
    }
  } catch (error) {
    console.error("Metadata preservation failed:", error);
    return {
      success: false,
      restoredCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
      fmlUploadSucceeded: false,
      fmlFileUrl: void 0
    };
  }
};

// src/middleware/hero-metric/deleteHeroMetric.ts
var deleteHeroMetric = async (baseUrl, accessToken, metricId) => {
  const config2 = {
    url: `${baseUrl}api/hero-metrics/${metricId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/hero-metric/getHeroMetricModes.ts
import axios42 from "axios";
var getHeroMetricModes = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/hero-metrics/modes`;
    const res = await axios42.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching hero metric modes:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/hero-metric/getHeroMetricConfig.ts
import axios43 from "axios";
var getHeroMetricConfig = async (baseUrl, accessToken, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/hero-metrics/${metricId}/config`;
    const res = await axios43.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching hero metric config:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/hero-metric/getHeroMetricConfigs.ts
import axios44 from "axios";
var getHeroMetricConfigs = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/hero-metrics/configs`;
    const res = await axios44.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching hero metric configs:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/hero-metric/putHeroMetricConfig.ts
var putHeroMetricConfig = async (baseUrl, accessToken, metricId, configPayload) => {
  const config2 = {
    url: `${baseUrl}api/hero-metrics/${metricId}/config`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: configPayload,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/hero-metric/createHeroMetricConfig.ts
var createHeroMetricConfig = async (baseUrl, accessToken, configPayload) => {
  const config2 = {
    url: `${baseUrl}api/hero-metrics/config`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: configPayload,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/legal/acceptLegal.ts
import axios45 from "axios";
var acceptTerms = async (baseUrl, accessToken, required) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.terms.acceptTerms;
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  const data = {};
  for (const doc of required) {
    if (doc.document === "acceptableUse") {
      data.acceptableUseVersion = doc.version;
      data.acceptableUseAcceptedAt = timestamp;
    } else if (doc.document === "saasStandardTerms") {
      data.saasStandardTerms = doc.version;
      data.saasStandardTermsAcceptedAt = timestamp;
    } else if (doc.document === "dataProcessing") {
      data.dataProcessing = doc.version;
      data.dataProcessingAcceptedAt = timestamp;
    }
  }
  try {
    await axios45.post(url, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      }
    });
    return true;
  } catch (error) {
    return false;
  }
};

// src/middleware/legal/getLegalStatus.ts
import axios46 from "axios";
var getLegalStatus = async (baseUrl, accessToken) => {
  const routes = routes_default(baseUrl);
  const { data } = await axios46.get(routes.get.terms.getStatus, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  });
  return data;
};

// src/middleware/legal/getLegalNotices.ts
import axios47 from "axios";
var getLegalNotices = async (baseUrl, accessToken) => {
  const routes = routes_default(baseUrl);
  try {
    const { data } = await axios47.get(routes.get.terms.getNotices, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      }
    });
    return data?.notices ?? [];
  } catch (error) {
    console.error("getLegalNotices: failed to fetch legal notices (returning none):", error);
    return [];
  }
};

// src/middleware/legal/getLegalDocument.ts
var REVALIDATE_SECONDS = 300;
var FETCH_TIMEOUT_MS = 5e3;
var isLegalDocumentContent = (v) => {
  const d = v;
  return !!d && typeof d.document === "string" && typeof d.version === "number" && typeof d.title === "string" && typeof d.contentHtml === "string";
};
var isLegalDocumentVersionSummary = (v) => {
  const d = v;
  return !!d && typeof d.document === "string" && typeof d.version === "number" && typeof d.title === "string";
};
var getLegalDocument = async (baseUrl, document2, version) => {
  const routes = routes_default(baseUrl);
  const url = version === void 0 ? routes.get.legalDocuments.getCurrent(document2) : routes.get.legalDocuments.getVersion(document2, version);
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    next: { revalidate: REVALIDATE_SECONDS },
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS)
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`getLegalDocument(${document2}, ${version ?? "current"}) failed: ${res.status}`);
  }
  const body = await res.json();
  return isLegalDocumentContent(body) ? body : null;
};
var fetchCurrentLegalVersions = async (baseUrl) => {
  const routes = routes_default(baseUrl);
  try {
    const res = await fetch(routes.get.legalDocuments.getCurrentVersions, {
      headers: { Accept: "application/json" },
      next: { revalidate: REVALIDATE_SECONDS },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS)
    });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body) ? body.filter(isLegalDocumentVersionSummary) : [];
  } catch {
    return [];
  }
};

// src/middleware/legal/getLegalDocumentsAdmin.ts
import axios48 from "axios";
var getLegalDocumentsAdmin = async (baseUrl, accessToken) => {
  const routes = routes_default(baseUrl);
  const res = await axios48.get(routes.get.legalDocuments.getAdmin, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  });
  return res.data;
};

// src/middleware/legal/createLegalDraft.ts
var createLegalDraft = async (baseUrl, accessToken, payload) => {
  const routes = routes_default(baseUrl);
  const config2 = {
    url: routes.post.legalDocuments.createDraft,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/legal/updateLegalDraft.ts
var updateLegalDraft = async (baseUrl, accessToken, id, payload) => {
  const routes = routes_default(baseUrl);
  const config2 = {
    url: routes.put.legalDocuments.updateDraft(id),
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/legal/deleteLegalDraft.ts
var deleteLegalDraft = async (baseUrl, accessToken, id) => {
  const routes = routes_default(baseUrl);
  const config2 = {
    url: routes.delete.legalDocuments.deleteDraft(id),
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/legal/publishLegalDraft.ts
var publishLegalDraft = async (baseUrl, accessToken, id) => {
  const routes = routes_default(baseUrl);
  const config2 = {
    url: routes.post.legalDocuments.publishDraft(id),
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/users/syncUserOnLogin.ts
import axios49 from "axios";
var syncUserOnLogin = async (baseUrl, accessToken) => {
  const routes = routes_default(baseUrl);
  try {
    await axios49.post(
      routes.post.users.loginSync,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return true;
  } catch {
    return false;
  }
};

// src/middleware/manufacturer-hierarchy/importManufacturerHierarchy.ts
import axios50 from "axios";

// src/middleware/deadlineSignal.ts
var deadlineSignal = (ms) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
};

// src/middleware/manufacturer-hierarchy/importManufacturerHierarchy.ts
var importManufacturerHierarchy = async (baseUrl, accessToken, data) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const deadline = deadlineSignal(BI_IMPORT_TIMEOUT_MS);
  try {
    const res = await axios50.post(`${baseUrl}api/manufacturer-hierarchy/import`, data, {
      headers: headers3,
      timeout: BI_IMPORT_TIMEOUT_MS,
      // Wall-clock bound; axios's `timeout` is socket inactivity only.
      signal: deadline.signal
    });
    return res.data;
  } catch (error) {
    console.error("Error importing manufacturer hierarchy:", describeRequestError(error));
    throw error;
  } finally {
    deadline.clear();
  }
};

// src/middleware/manufacturer-hierarchy/checkReferences.ts
import axios51 from "axios";
var ISSUES = [null, "NO_MATCH_MANUFACTURER", "NO_MATCH_EQUIP_TYPE"];
var answerProblem = (asked, data) => {
  const references = data?.references;
  if (!Array.isArray(references)) return "no references";
  const wanted = new Set(asked);
  const seen = /* @__PURE__ */ new Set();
  for (const entry of references) {
    if (!entry || typeof entry.reference_id !== "string") return "an entry with no reference_id";
    if (!ISSUES.includes(entry.issue)) return "an unknown issue";
    if (!wanted.has(entry.reference_id)) return "a reference that was not asked";
    if (seen.has(entry.reference_id)) return "a reference answered twice";
    seen.add(entry.reference_id);
  }
  return seen.size === wanted.size ? null : "a reference left unanswered";
};
var checkCatalogueReferences = async (baseUrl, accessToken, referenceIds, signal) => {
  const deadline = deadlineSignal(MIDDLEWARE_REQUEST_TIMEOUT_MS);
  const controller = new AbortController();
  const abort = () => controller.abort();
  deadline.signal.addEventListener("abort", abort);
  signal?.addEventListener("abort", abort);
  if (signal?.aborted) abort();
  try {
    const res = await axios51.post(
      `${baseUrl}api/manufacturer-hierarchy/reference-check`,
      { reference_ids: referenceIds },
      {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        signal: controller.signal
      }
    );
    const problem = answerProblem(referenceIds, res.data);
    if (problem) {
      console.warn("[launch-bi-check] checking references against BI failed:", res.status, problem);
      return { status: "failed", httpStatus: res.status };
    }
    return {
      status: "ready",
      references: res.data.references
    };
  } catch (error) {
    if (signal?.aborted) return { status: "failed" };
    const described = describeRequestError(error);
    console.warn(
      "[launch-bi-check] checking references against BI failed:",
      deadline.signal.aborted ? { ...described, message: `no answer within ${MIDDLEWARE_REQUEST_TIMEOUT_MS} ms` } : described
    );
    return {
      status: "failed",
      ...described.status !== void 0 ? { httpStatus: described.status } : {}
    };
  } finally {
    deadline.clear();
    deadline.signal.removeEventListener("abort", abort);
    signal?.removeEventListener("abort", abort);
  }
};

// src/middleware/bi-import/parseBiImportFailure.ts
import axios52 from "axios";
import { BI_IMPORT_REASONS } from "@repo/frontend-types";
var isReason = (value) => typeof value === "string" && Object.prototype.hasOwnProperty.call(BI_IMPORT_REASONS, value);
var parseBiImportFailure = (error, stage) => {
  if (axios52.isAxiosError(error)) {
    const response = error.response;
    if (!response) {
      return { stage, reason: "other", message: error.message, outcomeUnknown: true };
    }
    const body = response.data ?? {};
    const message = typeof body.message === "string" ? body.message : error.message;
    if (isReason(body.reason)) {
      return {
        stage,
        reason: body.reason,
        message,
        outcomeUnknown: false,
        status: response.status
      };
    }
    return {
      stage,
      reason: "other",
      message,
      outcomeUnknown: response.status >= 500,
      status: response.status
    };
  }
  return {
    stage,
    reason: "other",
    message: error instanceof Error ? error.message : String(error),
    outcomeUnknown: false
  };
};

// src/middleware/camera/getCameras.ts
import axios53 from "axios";
var getCameras = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios53.get(`${baseUrl}api/cameras`, { headers: headers3 });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Error fetching cameras:", error);
    return [];
  }
};

// src/middleware/camera/getCamerasOrThrow.ts
import axios54 from "axios";
var getCamerasOrThrow = async (baseUrl, accessToken) => {
  const res = await axios54.get(`${baseUrl}api/cameras`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  if (!Array.isArray(res.data)) {
    throw new Error("Camera catalogue: the response body was not a list of cameras");
  }
  return res.data;
};

// src/middleware/camera/getCameraById.ts
import axios55 from "axios";
var getCameraById = async (baseUrl, accessToken, cameraId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios55.get(`${baseUrl}api/cameras/${cameraId}`, { headers: headers3 });
    return res.data ?? null;
  } catch (error) {
    console.error("Error getting camera by ID:", error);
    return null;
  }
};

// src/middleware/camera/createCamera.ts
import axios56 from "axios";
var createCamera = async (baseUrl, accessToken, payload) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    return await axios56.post(`${baseUrl}api/cameras`, payload, { headers: headers3 });
  } catch (error) {
    console.error("Error creating camera:", error);
    return void 0;
  }
};

// src/middleware/camera/updateCamera.ts
import axios57 from "axios";
var updateCamera = async (baseUrl, accessToken, cameraId, payload) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    return await axios57.patch(`${baseUrl}api/cameras/${cameraId}`, payload, { headers: headers3 });
  } catch (error) {
    console.error("Error updating camera:", error);
    return false;
  }
};

// src/middleware/camera/deleteCamera.ts
import axios58 from "axios";
var deleteCamera = async (baseUrl, accessToken, cameraId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios58.delete(`${baseUrl}api/cameras/${cameraId}`, { headers: headers3 });
    return true;
  } catch (error) {
    console.error("Error deleting camera:", error);
    return false;
  }
};

// src/middleware/equipment-catalog/findManufacturerCatalog.ts
import axios59 from "axios";

// src/middleware/equipment-catalog/catalogueRow.ts
function toHierarchy(row) {
  return {
    entity_bid: row.reference_id ?? "",
    model_name: row.product ?? "",
    product_line: row.product_line ?? "",
    manufacturer_name: row.manufacturer ?? "",
    model_file: row.model_3d ?? void 0,
    reference_id: row.reference_id ?? void 0,
    equipment_type: row.equipment_type ?? void 0,
    equipment_subcategory: row.equipment_subcategory ?? void 0,
    sku: row.sku ?? void 0,
    equipment_variation: row.equipment_variation ?? void 0
  };
}
var parseDefaultCapacity = (raw) => {
  let value = null;
  if (typeof raw === "number") value = Number.isInteger(raw) ? raw : null;
  else if (typeof raw === "string" && /^\d+$/.test(raw.trim())) value = Number(raw.trim());
  return value !== null && value > 0 && value <= 2147483647 ? value : null;
};
function toIdentifyProduct(row) {
  const line = row.product_line?.trim();
  return {
    referenceId: row.reference_id ?? "",
    product: row.product ?? "",
    productLine: line ? line : null,
    manufacturer: row.manufacturer ?? null,
    capacity: parseDefaultCapacity(row.default_capacity),
    hasModel: !!row.model_3d && row.model_3d.trim() !== ""
  };
}

// src/middleware/equipment-catalog/findManufacturerCatalog.ts
var CATALOGUE_FILTER_KEYS = [
  "manufacturer",
  "product_line",
  "equipment_subcategory",
  "equipment_type",
  "equipment_variation"
];
var facetsFrom = (raw) => Object.fromEntries(
  CATALOGUE_FILTER_KEYS.map((key) => {
    const value = raw?.[key];
    return [
      key,
      Array.isArray(value) ? value.filter((v) => typeof v === "string") : []
    ];
  })
);
var availabilityFrom = (raw) => Object.fromEntries(
  CATALOGUE_FILTER_KEYS.map((key) => [key, raw?.[key] !== false])
);
var findManufacturerCatalog = async (baseUrl, accessToken, { q, limit, offset } = {}) => {
  const params = {};
  const query = (q ?? "").trim();
  if (query) params.q = query;
  if (limit !== void 0) params.limit = limit;
  if (offset !== void 0) params.offset = offset;
  const res = await axios59.get(`${baseUrl}api/manufacturer-products/find`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params,
    /*
     * Bounded, like every other middleware-proxy read, and through the repo's one named constant
     * rather than an inline literal. axios defaults to NO timeout, and an unbounded search is not
     * merely slow: the picker's loading state never clears, so the panel sits on a spinner with
     * no message and no Retry — the dead end the Combobox adoption exists to remove, re-created
     * by a hung socket.
     */
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  const body = res.data ?? {};
  if (!Array.isArray(body.rows) || typeof body.total !== "number") {
    throw new Error(
      "Manufacturer catalogue: /find answered 200 with no usable rows/total \u2014 refusing to report a count it did not give."
    );
  }
  const rows = body.rows;
  const usable = rows.map(toHierarchy).filter((r) => r.entity_bid);
  if (usable.length !== rows.length) {
    console.warn(
      `Manufacturer catalogue: dropped ${rows.length - usable.length} row(s) with no reference_id`
    );
  }
  return {
    rows: usable,
    // The count the page came from, NOT the size of the page — and never `usable.length`, which
    // would silently restate the list rather than the catalogue. Guaranteed a number by the
    // envelope check above; there is deliberately no fallback, because every value one could
    // fall back to is a lie about the catalogue.
    total: body.total,
    facets: facetsFrom(body.facets),
    available: availabilityFrom(body.available)
  };
};

// src/middleware/equipment-catalog/getCatalogueProductByReference.ts
import axios60 from "axios";
var CATALOGUE_REFERENCE_UNKNOWN = "CATALOGUE_REFERENCE_UNKNOWN";
var text = (value) => typeof value === "string" && value.trim() !== "" ? value : null;
var TIMEOUT_CODES = /* @__PURE__ */ new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_CANCELED"]);
var failureCause = (error, status) => {
  if (status === 404) return { status: "not-deployed" };
  if (typeof status === "number") return { status };
  const code = error?.code;
  return { status: typeof code === "string" && TIMEOUT_CODES.has(code) ? "timeout" : "network" };
};
var getCatalogueProductByReference = async (baseUrl, accessToken, referenceId) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MIDDLEWARE_REQUEST_TIMEOUT_MS);
  try {
    const res = await axios60.get(
      `${baseUrl}api/manufacturer-products/reference/${encodeURIComponent(referenceId)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        },
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        signal: controller.signal
      }
    );
    const row = res?.data;
    if (!row || typeof row !== "object" || typeof row.reference_id !== "string") {
      return { kind: "failed", cause: { status: "malformed" } };
    }
    return {
      kind: "found",
      product: {
        referenceId: row.reference_id,
        manufacturer: text(row.manufacturer),
        productLine: text(row.product_line),
        product: text(row.product),
        equipmentType: text(row.equipment_type),
        sku: text(row.sku)
      }
    };
  } catch (error) {
    const response = error?.response;
    const status = response?.status;
    const data = response?.data;
    if (status === 404 && data?.code === CATALOGUE_REFERENCE_UNKNOWN) {
      return { kind: "unknown", referenceId };
    }
    if (status === 400) {
      const message = Array.isArray(data?.message) ? data.message.filter((part) => typeof part === "string").join(" ") : data?.message;
      if (typeof message === "string" && message.trim() !== "") {
        return { kind: "invalid", message };
      }
    }
    return { kind: "failed", cause: failureCause(error, status) };
  } finally {
    clearTimeout(timer);
  }
};

// src/middleware/equipment-catalog/getManufacturerCatalog.ts
import axios61 from "axios";
var getManufacturerCatalog = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const url = `${baseUrl}api/manufacturer-products`;
  const res = await axios61.get(url, {
    headers: headers3,
    params: { order: "manufacturer.asc,product_line.asc,product.asc" },
    /*
     * Bounded, like both siblings in this folder. This is the LARGEST read in the app — the whole
     * catalogue, ~6,000 rows over a dozen server round trips — and axios defaults to no timeout, so
     * a black-holed socket left the Library on "Loading library…" for the rest of the session with
     * no message and no retry. The middleware's own `getCatalog` has a deadline for exactly this
     * reason; the client half had none.
     */
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.map(toHierarchy).filter((r) => r.entity_bid);
};

// src/middleware/equipment-catalog/getManufacturerProductsByManufacturer.ts
import axios62 from "axios";

// src/ttlCache.ts
var createTtlCache = (ttlMs) => {
  const entries = /* @__PURE__ */ new Map();
  return {
    read: (key, load, options) => {
      const now = Date.now();
      const existing = entries.get(key);
      if (!options?.refresh && existing && existing.expiresAt > now) return existing.value;
      const value = load();
      entries.set(key, { value, expiresAt: now + ttlMs });
      void value.catch(() => {
        if (entries.get(key)?.value === value) entries.delete(key);
      });
      return value;
    },
    clear: () => entries.clear()
  };
};

// src/middleware/equipment-catalog/getManufacturerProductsByManufacturer.ts
var catalogueCache = createTtlCache(6e4);
var getManufacturerProductsByManufacturer = (baseUrl, accessToken, manufacturer, options) => readCached(baseUrl, accessToken, manufacturer, options).then((rows) => rows.map(toHierarchy));
var getIdentifyProductsByManufacturer = (baseUrl, accessToken, manufacturer, options) => readCached(baseUrl, accessToken, manufacturer, options).then((rows) => rows.map(toIdentifyProduct));
var readCached = (baseUrl, accessToken, manufacturer, options) => catalogueCache.read(
  `${baseUrl}|${manufacturer}`,
  () => readManufacturerProducts(baseUrl, accessToken, manufacturer),
  options
);
var readManufacturerProducts = async (baseUrl, accessToken, manufacturer) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const url = `${baseUrl}api/manufacturer-products/manufacturer/${encodeURIComponent(manufacturer)}`;
  const res = await axios62.get(url, {
    headers: headers3,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.filter((row) => !!row.reference_id);
};

// src/middleware/metric/getMetricTypesAndMetrics.ts
import axios63 from "axios";
var getMetricTypesAndMetrics = async (baseUrl, accessToken, orgBID) => {
  const routes = routes_default(baseUrl);
  let data = null;
  const url = `${routes.get.dgraph.getMetricTypesAndMetrics(orgBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios63.get(url, {
      headers: headers3,
      // Bounded, like the six metric-registry wrappers (plan D6). Without it a hung upstream
      // never rejects, so this wrapper never resolves `null` and the reference pane's
      // read-failed branch — which is gated on `dgraph === null && !loading` — is suppressed
      // forever: an eternal "Loading the DGraph catalogue…" with no error and no recovery.
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    data = res.data;
  } catch (error) {
    console.error(
      "Error fetching DGraph metric types:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
  }
  return data;
};

// src/middleware/metric/getMetrics.ts
import axios64 from "axios";
var getMetrics = async (baseUrl, accessToken, orgBID) => {
  const routes = routes_default(baseUrl);
  let data = null;
  const url = `${routes.get.metrics.getMetrics(orgBID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios64.get(url, {
      headers: headers3
    });
    data = res.data;
  } catch (error) {
  }
  return data;
};

// src/middleware/metric/getSupabaseMetrics.ts
import axios65 from "axios";
var getSupabaseMetrics = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-metrics/supabase`;
    const res = await axios65.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching Supabase metrics:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/metric-registry/getMetricRegistry.ts
import axios66 from "axios";
var getMetricRegistry = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/metric-registry`;
    const res = await axios66.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching the metric registry:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/metric-registry/getMetricRegistryEntry.ts
import axios67 from "axios";
var getMetricRegistryEntry = async (baseUrl, accessToken, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/metric-registry/${metricId}`;
    const res = await axios67.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching metric registry entry:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/metric-registry/getMetricRegistryEnums.ts
import axios68 from "axios";
var getMetricRegistryEnums = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/metric-registry/enums`;
    const res = await axios68.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching metric registry enums:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/metric-registry/createMetricRegistryEntry.ts
var createMetricRegistryEntry = async (baseUrl, accessToken, input) => {
  const config2 = {
    url: `${baseUrl}api/metric-registry`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: input,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/metric-registry/putMetricRegistryEntry.ts
var putMetricRegistryEntry = async (baseUrl, accessToken, metricId, input) => {
  const config2 = {
    url: `${baseUrl}api/metric-registry/${metricId}`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: input,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/metric-registry/deleteMetricRegistryEntry.ts
import axios69 from "axios";
var deleteMetricRegistryEntry = async (baseUrl, accessToken, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/metric-registry/${metricId}`;
    await axios69.delete(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return { error: null };
  } catch (error) {
    const response = error?.response;
    const body = response?.data;
    console.error(
      "Error deleting the metric registry entry:",
      response?.status,
      body?.message ?? error?.message
    );
    return {
      error: {
        message: body?.message ?? error?.message ?? "http request failed",
        status: response?.status,
        // Present only on a 409. `undefined` on any other failure, so a caller can tell
        // "in use, here is what by" from "the write failed".
        metric: body?.metric,
        blocked_by: body?.blocked_by
      }
    };
  }
};

// src/middleware/nola/nola-complex-metric/createNolaComplexMetric.ts
var createNolaComplexMetric = async (baseUrl, accessToken, complexMetricPayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-complex-metrics`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: complexMetricPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-complex-metric/deleteNolaComplexMetric.ts
var deleteNolaComplexMetric = async (baseUrl, accessToken, configId, metricId) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-complex-metrics/${metricId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-complex-metric/getNolaComplexMetricById.ts
import axios70 from "axios";
var getNolaComplexMetricsById = async (baseUrl, accessToken, configId, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-complex-metrics/${metricId}`;
    const res = await axios70.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching notification by ID:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-complex-metric/getNolaComplexMetrics.ts
import axios71 from "axios";
var getNolaComplexMetrics = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/nola-complex-metrics`;
    const res = await axios71.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching complex metrics:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-complex-metric/getNolaComplexMetricsByConfigId.ts
import axios72 from "axios";
var getNolaComplexMetricsByConfigId = async (baseUrl, accessToken, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-complex-metrics`;
    const res = await axios72.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching simple metrics by config ID:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-complex-metric/updateNolaComplexMetric.ts
var updateNolaComplexMetric = async (baseUrl, accessToken, configId, metricId, complexMetricPayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-complex-metrics/${metricId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: complexMetricPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-config/activateNolaConfig.ts
import axios73 from "axios";
var activateNolaConfig = async (baseUrl, accessToken, twinId, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/nola-configs/${configId}/activate`;
    const res = await axios73.patch(url, {}, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error activating nola config:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-config/createExistingNolaConfig.ts
import axios74 from "axios";
var createExistingNolaConfig = async (baseUrl, accessToken, twinBID, configData) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinBID}/nola-configs/existing`;
    const res = await axios74.post(url, configData, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error creating existing nola config:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-config/createNolaConfig.ts
var createNolaConfig = async (baseUrl, accessToken, twinId, payload) => {
  const config2 = {
    url: `${baseUrl}/twins/${twinId}/nola-configs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-config/createNolaSkeletonConfig.ts
import axios75 from "axios";
var createNolaSkeletonConfig = async (baseUrl, accessToken, twinBID, configData) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinBID}/nola-configs/skeleton`;
    const res = await axios75.post(url, configData, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error creating skeleton nola config:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-config/deleteNolaConfig.ts
var deleteNolaConfig = async (baseUrl, accessToken, twinId, configId) => {
  const config2 = {
    url: `${baseUrl}api/twins/${twinId}/nola-configs/${configId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-config/getNolaConfigById.ts
import axios76 from "axios";
var getNolaConfigById = async (baseUrl, accessToken, twinId, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/nola-configs/${configId}`;
    const res = await axios76.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching nola configs:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-config/getNolaConfigs.ts
import axios77 from "axios";
var getNolaConfigs = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/nola-configs`;
    const res = await axios77.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching nola configs:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-config/updateNolaConfig.ts
var updateNolaConfig = async (baseUrl, accessToken, twinId, configId, payload) => {
  const config2 = {
    url: `${baseUrl}/twins/${twinId}/nola-configs/${configId} `,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-service/bulkCreateServices.ts
import axios78 from "axios";
var bulkCreateServices = async (baseUrl, accessToken, configId, requestData) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-services/bulk`;
    const res = await axios78.post(url, requestData, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error creating bulk services:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-service/createNolaService.ts
var createNolaService = async (baseUrl, accessToken, servicePayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-services`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: servicePayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-service/deleteNolaService.ts
var deleteNolaService = async (baseUrl, accessToken, configId, serviceId, cascadeDelete = false) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-services/${serviceId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params: {
      cascadeDelete
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  if (error) {
    console.error("Error deleting nola service:", error);
    return void 0;
  }
  return data;
};

// src/middleware/nola/nola-service/getNolaService.ts
import axios79 from "axios";
var getNolaService = async (baseUrl, accessToken, configId, serviceId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-services/${serviceId}`;
    const res = await axios79.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching nola service by configId and serviceId:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-service/getNolaServices.ts
import axios80 from "axios";
var getNolaServices = async (baseUrl, accessToken, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-services`;
    const res = await axios80.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching notification by ID:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-service/getPolygons.ts
import axios81 from "axios";
var getPolygons = async (baseUrl, accessToken, configId, serviceId, nodeUUID) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-services/${serviceId}/polygons?nodeUUID=${encodeURIComponent(nodeUUID)}`;
    const res = await axios81.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching polygons:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-service/signOffNolaService.ts
var signOffNolaService = async (baseUrl, accessToken, configId, serviceId) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-services/${serviceId}/sign-off`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-service/updateNolaService.ts
var updateNolaService = async (baseUrl, accessToken, configId, serviceId, servicePayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-services/${serviceId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: servicePayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-simple-metric/createNolaSimpleMetric.ts
var createNolaSimpleMetric = async (baseUrl, accessToken, simpleMetricPayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-simple-metrics`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: simpleMetricPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-simple-metric/deleteNolaSimpleMetric.ts
var deleteNolaSimpleMetric = async (baseUrl, accessToken, configId, metricId) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-simple-metrics/${metricId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nola-simple-metric/getNolaSimpleMetricById.ts
import axios82 from "axios";
var getNolaSimpleMetricById = async (baseUrl, accessToken, configId, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-simple-metrics/${metricId}`;
    const res = await axios82.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching notification by ID:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-simple-metric/getNolaSimpleMetrics.ts
import axios83 from "axios";
var getNolaSimpleMetrics = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/nola-simple-metrics`;
    const res = await axios83.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching simple metrics:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-simple-metric/getNolaSimpleMetricsByConfigId.ts
import axios84 from "axios";
var getNolaSimpleMetricsByConfigId = async (baseUrl, accessToken, configId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/nola-configs/${configId}/nola-simple-metrics`;
    const res = await axios84.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching simple metrics by config ID:", error);
    return void 0;
  }
};

// src/middleware/nola/nola-simple-metric/updateNolaSimpleMetric.ts
var updateNolaSimpleMetric = async (baseUrl, accessToken, configId, metricId, simpleMetricPayload) => {
  const config2 = {
    url: `${baseUrl}api/nola-configs/${configId}/nola-simple-metrics/${metricId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: simpleMetricPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaCategories.ts
var getNolaCategories = async (baseUrl, accessToken) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/categories`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaCategoryMetricLatestValues.ts
var getNolaCategoryMetricLatestValues = async (baseUrl, accessToken, bID, categoryID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/category-count/${bID}/${categoryID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaCategoryMetricsHistoricalValues.ts
var getNolaCategoryMetricsHistoricalValues = async (baseUrl, accessToken, bID, categoryID, filter) => {
  const params = new URLSearchParams({ filter });
  const url = `${baseUrl}api/nolahq/category-trend/${bID}/${categoryID}?${params.toString()}`;
  const config2 = {
    url,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaFrame.ts
var getNolaFrame = async (baseUrl, accessToken, twinId, nodeId) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/frame/${twinId}/${nodeId}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaHealthCheck.ts
var getNolaHealthCheck = async (baseUrl) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/health`,
    method: "GET",
    headers: {
      "content-type": "application/json"
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaHealthCheckAuth.ts
var getNolaHealthCheckAuth = async (baseUrl, accessToken) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/health-auth`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaLocationCategories.ts
var getNolaLocationCategories = async (baseUrl, accessToken, locationId) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/locations/${locationId}/categories`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaLocationCategoryCount.ts
var getNolaLocationCategoryCount = async (baseUrl, accessToken, locationId, categoryId) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/locations/${locationId}/categories/${categoryId}/count`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaLocations.ts
var getNolaLocations = async (baseUrl, accessToken) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/locations`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaMetricLatestValue.ts
var getNolaMetricLatestValue = async (baseUrl, accessToken, bID, metricID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/metric-latest/${bID}/${metricID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaMetricsByCategory.ts
var getNolaMetricsByCategory = async (baseUrl, accessToken, bID, categoryID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/metrics/${bID}/${categoryID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaNetwork.ts
var getNolaNetwork = async (baseUrl, accessToken, twinId) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/network/${twinId}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaNetworkStatus.ts
var getNolaNetworkStatus = async (baseUrl, accessToken, bID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/network-status/${bID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaProcessor.ts
var getNolaProcessor = async (baseUrl, accessToken, bID, serviceID, processorID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/processors/${bID}/${serviceID}/${processorID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaProcessors.ts
var getNolaProcessors = async (baseUrl, accessToken, bID, serviceID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/processors/${bID}/${serviceID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaVenueSectors.ts
var getNolaVenueSectors = async (baseUrl, accessToken, bid) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/venue-sectors/${bid}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/getNolaVenueServices.ts
var getNolaVenueServices = async (baseUrl, accessToken, bID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/venue-services/${bID}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/nola/nolahq/resetNolaLocation.ts
var resetNolaLocation = async (baseUrl, accessToken, bID) => {
  const config2 = {
    url: `${baseUrl}api/nolahq/${bID}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/notification/getNotificationById.ts
import axios85 from "axios";
var getNotificationById = async (baseUrl, accessToken, notificationId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/notifications/${notificationId}`;
    const res = await axios85.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching notification by ID:", error);
    return void 0;
  }
};

// src/middleware/notification/getNotifications.ts
import axios86 from "axios";
var getNotifications = async (baseUrl, accessToken, twinId, isRead) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    let url = `${baseUrl}api/notifications`;
    const queryParams = new URLSearchParams();
    if (twinId !== void 0) {
      if (typeof twinId === "string") {
        queryParams.append("twin_bid", twinId);
      } else {
        queryParams.append("twin_id", twinId.toString());
      }
    }
    if (isRead !== void 0) {
      queryParams.append("is_read", isRead.toString());
    }
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
    const res = await axios86.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return void 0;
  }
};

// src/middleware/notification/getNotificationsByOrganisation.ts
import axios87 from "axios";
var getNotificationsByOrganisation = async (baseUrl, accessToken, organisationBid, isRead) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    let url = `${baseUrl}api/notifications`;
    const queryParams = new URLSearchParams();
    queryParams.append("organisation_bid", organisationBid);
    if (isRead !== void 0) {
      queryParams.append("is_read", isRead.toString());
    }
    url += `?${queryParams.toString()}`;
    const res = await axios87.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching organisation notifications:", error);
    return void 0;
  }
};

// src/middleware/notification/markNotificationAsRead.ts
var markNotificationAsRead = async (baseUrl, accessToken, notificationId, readAt = (/* @__PURE__ */ new Date()).toISOString()) => {
  try {
    const url = `${baseUrl}api/notifications/${notificationId}/read-at`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        read_at: readAt,
        channel: "WEB_APP"
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// src/middleware/notification/markNotificationAsUnread.ts
var markNotificationAsUnread = async (baseUrl, accessToken, notificationId) => {
  try {
    const url = `${baseUrl}api/notifications/${notificationId}/mark-unread`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        channel: "WEB_APP"
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// src/middleware/notification/markAllNotificationsAsRead.ts
var markAllNotificationsAsRead = async (baseUrl, accessToken, organisationBid) => {
  const url = `${baseUrl}api/notifications/read-all?organisation_bid=${encodeURIComponent(
    organisationBid
  )}`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({})
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }
  return await response.json();
};

// src/middleware/notification/sendNotification.ts
import axios88 from "axios";
var sendNotification = async (baseUrl, accessToken, data) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios88.post(`${baseUrl}api/notifications/send`, data, {
      headers: headers3
    });
    return res.data;
  } catch (error) {
    console.error("Error sending notification:", error);
    return void 0;
  }
};

// src/middleware/notification-subscription/getSubscriptionCatalog.ts
var getSubscriptionCatalog = async (baseUrl, accessToken) => {
  const response = await fetch(`${baseUrl}api/notification-subscriptions/catalog`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    signal: AbortSignal.timeout(15e3)
  });
  if (!response.ok) {
    throw new Error(`Failed to load notification catalog (status ${response.status}).`);
  }
  return response.json();
};

// src/middleware/notification-subscription/getMySubscriptions.ts
var getMySubscriptions = async (baseUrl, accessToken) => {
  const response = await fetch(`${baseUrl}api/notification-subscriptions`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    signal: AbortSignal.timeout(15e3)
  });
  if (!response.ok) {
    throw new Error(`Failed to load notification subscriptions (status ${response.status}).`);
  }
  return response.json();
};

// src/middleware/notification-subscription/putSubscription.ts
var putSubscription = async (baseUrl, accessToken, scenario, body) => {
  const response = await fetch(
    `${baseUrl}api/notification-subscriptions/${encodeURIComponent(scenario)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15e3)
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to save subscription (status ${response.status}).`);
  }
};

// src/middleware/notification-subscription/deleteSubscription.ts
var deleteSubscription = async (baseUrl, accessToken, scenario) => {
  const response = await fetch(
    `${baseUrl}api/notification-subscriptions/${encodeURIComponent(scenario)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      signal: AbortSignal.timeout(15e3)
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to remove subscription (status ${response.status}).`);
  }
};

// src/middleware/organisation/createOrganisation.ts
import axios89 from "axios";
var createOrganisation = async (baseUrl, accessToken, data, options) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.organisations.createOrganisation;
  let success = false;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios89.post(url, data, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/organisation/getOrgUrlsAndMetadata.ts
import axios90 from "axios";
var getOrgUrlsAndMetaData = async (baseUrl, envName, accessToken) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.get.organisations.getOrgUrlsAndMetaData}${envName}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  return await axios90.get(url, {
    headers: headers3
  });
};

// src/middleware/organisation/getOrganisationById.ts
import axios91 from "axios";
var getOrganisationById = async (baseUrl, accessToken, bID, options) => {
  const routes = routes_default(baseUrl);
  let organisation = null;
  const url = `${routes.get.organisations.getOrganisationById(bID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios91.get(url, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    organisation = res.data;
  } catch (error) {
  }
  return organisation;
};

// src/middleware/organisation/getOrganisations.ts
import axios92 from "axios";
var getOrganisations = async (baseUrl, accessToken, options) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/organisations?select=id,name,bid&order=name.asc`;
    const res = await axios92.get(url, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    return res.data;
  } catch (error) {
    const e = error;
    if (e?.code === "ERR_CANCELED") return void 0;
    console.error(
      "Error fetching organisations:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return void 0;
  }
};

// src/middleware/organisation/getTwins.ts
import axios93 from "axios";
var getTwins = async (baseUrl, accessToken, bID, options) => {
  const routes = routes_default(baseUrl);
  let organisation = null;
  const url = `${routes.get.organisations.getTwins(bID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios93.get(url, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    organisation = res.data;
  } catch (error) {
    const e = error;
    console.error(
      `Error fetching twins for organisation ${bID}:`,
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
  }
  return organisation;
};

// src/middleware/organisation/updateOrganisation.ts
import axios94 from "axios";
var updateOrganisation = async (baseUrl, accessToken, data, options) => {
  const routes = routes_default(baseUrl);
  const url = routes.patch.organisations.updateOrganisationById(data.bid);
  let success = false;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const result = await axios94.patch(url, data, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    return result;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/customer-api-key/getCustomerApiKeys.ts
import axios95 from "axios";
var getCustomerApiKeys = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/customer-api-keys`;
    const res = await axios95.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching customer API keys:", error);
    return void 0;
  }
};

// src/middleware/customer-api-key/getCustomerApiKeyById.ts
import axios96 from "axios";
var getCustomerApiKeyById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/customer-api-keys/${id}`;
    const res = await axios96.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching customer API key:", error);
    return void 0;
  }
};

// src/middleware/customer-api-key/createCustomerApiKey.ts
var createCustomerApiKey = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/customer-api-keys`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/customer-api-key/updateCustomerApiKey.ts
var updateCustomerApiKey = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/customer-api-keys/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/customer-api-key/deleteCustomerApiKey.ts
var deleteCustomerApiKey = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/customer-api-keys/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/sensor/getSensors.ts
var getSensors = async (baseUrl, accessToken, orgBID, entityBID) => {
  const params = new URLSearchParams({
    org_bid: orgBID,
    entity_bid: entityBID
  });
  try {
    const response = await fetch(`${baseUrl}api/dgraph/sensors?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      }
    });
    if (!response.ok) {
      console.error(`Failed to fetch sensors: HTTP ${response.status}`);
      return null;
    }
    const result = await response.json();
    return result?.data || result;
  } catch (error) {
    console.error("Error fetching sensors:", error);
    return null;
  }
};

// src/middleware/sensor/sensorApi.ts
var getSensorsByEntityBID = async (baseUrl, accessToken, orgBID, entityBID) => {
  const params = new URLSearchParams({
    org_bid: orgBID,
    entity_bid: entityBID
  });
  const response = await fetch(`${baseUrl}api/dgraph/sensors?${params}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  const result = await response.json();
  return result?.data || result;
};
var getSensorVersions = async (baseUrl, accessToken, orgBID, sensorBID) => {
  const params = new URLSearchParams({
    org_bid: orgBID,
    sensor_bid: sensorBID
  });
  try {
    const response = await fetch(`${baseUrl}api/dgraph/sensors/versions?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      }
    });
    if (!response.ok) {
      console.error(`Failed to fetch sensor versions: HTTP ${response.status}`);
      return null;
    }
    const result = await response.json();
    return result?.data || [];
  } catch (error) {
    console.error("Error fetching sensor versions:", error);
    return null;
  }
};
var upsertSensors = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/dgraph/sensors/upsert`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

// src/middleware/sensor-versioning/sensorVersioningApi.ts
var versionAndDeleteSensors = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/dgraph/sensors/version-and-delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};
var rollbackSensors = async (baseUrl, accessToken, request) => {
  const response = await fetch(`${baseUrl}api/dgraph/sensors/rollback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};
var queryVersionedSensors = async (baseUrl, accessToken, orgBid, versionSuffix, originalSensorBid) => {
  const params = new URLSearchParams({ org_bid: orgBid });
  if (versionSuffix) {
    params.append("version_suffix", versionSuffix);
  }
  if (originalSensorBid) {
    params.append("original_sensor_bid", originalSensorBid);
  }
  const response = await fetch(`${baseUrl}api/dgraph/sensors/versioned?${params}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

// src/middleware/spatial-hierarchy/importSpatialHierarchy.ts
import axios97 from "axios";
var importSpatialHierarchy = async (baseUrl, accessToken, data) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const deadline = deadlineSignal(BI_IMPORT_TIMEOUT_MS);
  try {
    const res = await axios97.post(`${baseUrl}api/spatial-hierarchy/import`, data, {
      headers: headers3,
      timeout: BI_IMPORT_TIMEOUT_MS,
      // Wall-clock bound; axios's `timeout` is socket inactivity only.
      signal: deadline.signal
    });
    return res.data;
  } catch (error) {
    console.error("Error importing spatial hierarchy:", describeRequestError(error));
    throw error;
  } finally {
    deadline.clear();
  }
};

// src/middleware/twin/createTwin.ts
import axios98 from "axios";

// src/middleware/describeFailure.ts
var describeFailure = (error, fallback) => {
  const e = error;
  const httpStatus = e?.response?.status;
  const serverMessage = typeof e?.response?.data?.message === "string" ? e.response.data.message : void 0;
  const reason = typeof e?.response?.data?.reason === "string" ? e.response.data.reason : void 0;
  return {
    error: serverMessage ?? e?.message ?? fallback,
    ...httpStatus !== void 0 ? { httpStatus } : {},
    ...reason !== void 0 ? { reason } : {}
  };
};

// src/middleware/twin/createTwin.ts
var problemsIn = (body) => {
  const errors = body?.validationErrors;
  if (!Array.isArray(errors)) return [];
  return errors.flatMap(
    (entry) => typeof entry?.field === "string" && Array.isArray(entry.errors) ? entry.errors.filter((message) => typeof message === "string").map((message) => ({ field: entry.field, message })) : []
  );
};
var createTwin = async (baseUrl, accessToken, data) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.twins.createTwin;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios98.post(url, data, {
      headers: headers3,
      timeout: MIDDLEWARE_WRITE_TIMEOUT_MS,
      signal: AbortSignal.timeout(MIDDLEWARE_WRITE_TIMEOUT_MS)
    });
    return { kind: "created", twin: res.data };
  } catch (error) {
    const failure = describeFailure(error, String(error));
    const status = failure.httpStatus;
    console.error("Error creating twin:", status ?? "(no status)", failure.error);
    if (status === 400 || status === 409) {
      const data2 = error?.response?.data;
      const problems = problemsIn(data2);
      if (problems.length > 0) {
        return { kind: "refused", status, problems };
      }
    }
    return { kind: "failed", ...status !== void 0 && { status } };
  }
};

// src/middleware/twin/checkTwinAvailability.ts
import axios99 from "axios";
var boundedSignal = (caller) => {
  const deadline = AbortSignal.timeout(MIDDLEWARE_REQUEST_TIMEOUT_MS);
  if (!caller) return deadline;
  return typeof AbortSignal.any === "function" ? AbortSignal.any([caller, deadline]) : caller;
};
var checkTwinAvailability = async (baseUrl, accessToken, query, options = {}) => {
  const routes = routes_default(baseUrl);
  const params = {
    organisation_id: query.organisationId,
    bid: query.bid,
    name: query.name,
    except_bid: query.exceptBid
  };
  try {
    const res = await axios99.get(routes.get.twins.availability, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      params,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      signal: boundedSignal(options.signal)
    });
    return res.data;
  } catch (error) {
    if (options.signal?.aborted) {
      return void 0;
    }
    const failure = describeFailure(error, String(error));
    console.error(
      "Error checking twin availability:",
      failure.httpStatus ?? "(no status)",
      failure.error
    );
    return void 0;
  }
};

// src/middleware/twin/dGraphQuery.ts
import axios100 from "axios";
import { isDgraphNamespaceError, extractOrgBidFromDgraphError } from "@repo/front-end-utils";
var dGraphQuery = async (baseUrl, accessToken, orgBID, data, options) => {
  const routes = routes_default(baseUrl);
  let res = null;
  const url = `${routes.dGraphProxy}?org_bid=${orgBID}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    res = await axios100.post(url, data, {
      headers: headers3,
      // Spread conditionally so a caller passing nothing sends a byte-identical axios config —
      // which is the compatibility claim, and is asserted in `dGraphQuery.test.ts` rather than here.
      ...options?.signal ? { signal: options.signal } : {},
      ...options?.timeout != null ? { timeout: options.timeout } : {}
    });
  } catch (error) {
    const namespaceAbsent = isDgraphNamespaceError(error);
    if (namespaceAbsent) {
      const orgBid = extractOrgBidFromDgraphError(error);
      if (orgBid) {
        if (typeof window !== "undefined" && window.__showDgraphError) {
          window.__showDgraphError(orgBid);
        }
      }
    }
    const e = error;
    const log = namespaceAbsent ? console.info : console.error;
    log("dGraphQuery error:", e?.response?.status ?? "(no response)", e?.message ?? "");
    try {
      options?.onFailure?.({
        namespaceAbsent,
        status: e?.response?.status,
        message: e?.message
      });
    } catch {
    }
  }
  return res;
};

// src/middleware/twin/deleteTwin.ts
import axios101 from "axios";
var deleteTwin = async (baseUrl, accessToken, bID) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.delete.twins.deleteTwin(bID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios101.delete(url, {
      headers: headers3
    });
    return true;
  } catch (error) {
  }
  return false;
};

// src/middleware/twin/dgqDeleteTwinEntity.ts
import { extractBIDs } from "@repo/front-end-utils";

// src/middleware/twin/dgqFetchTwin.ts
var QUERY_TWIN_ENTITY_STRING = `
    fragment EntityFragment on TwinEntity {
        id
        bID
        name
        shortName
        description
        tags
        tracked
        mapOrigin {
            latitude
            longitude
        }
        nonFunctional
        aggregateMetrics
        geoType {
            name
            displayName
            description
        }
        type {
            name
        }
        sensors {
            id
            bID
            __typename
            metricType {
                id
                name
                description
                metrics {
                    name
                    dataType
                    description
                    unit
                }
            }
        }
        coordinateSystem
        geoType {
            name
        }
        location {
            latitude
            longitude
        }
        centre {
            latitude
            longitude
        }
        altitude
        boundaries {
            polygons {
                coordinates {
                    points {
                        latitude
                        longitude
                    }
                }
            }
        }
        rotationX
        rotationY
        rotationZ
        modelFile
        capacity
        maker
        makerID
        definitions
    }

    query QueryTwinEntity($bID: String) {
        queryTwinEntity(filter: { bID: { eq: $bID } }) {
            organisation {
                id
                name
                accounts {
                    id
                    email
                }
            }
            ...EntityFragment
            children {
                ...EntityFragment
                children {
                    ...EntityFragment
                    children {
                        ...EntityFragment
                        children {
                            ...EntityFragment
                            children {
                                ...EntityFragment
                                children {
                                    ...EntityFragment
                                    children {
                                        ...EntityFragment
                                        children {
                                            ...EntityFragment
                                            children {
                                                ...EntityFragment
                                            }
                                        }
                                    }
                                }
                            }
                        }
                            
                    }
                }
            }
        }
    }
`;
var dgqFetchTwinOutcome = async (baseUrl, accessToken, orgBID, twinBID, options) => {
  try {
    const res = await dGraphQuery(
      baseUrl,
      accessToken,
      orgBID,
      {
        query: QUERY_TWIN_ENTITY_STRING,
        variables: { bID: twinBID },
        operationName: "QueryTwinEntity"
      },
      options
    );
    if (!res) return { status: "failed", error: "No response from Dgraph." };
    const errors = res.data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return { status: "failed", error: errors[0]?.message ?? "Dgraph returned errors." };
    }
    const entities = res.data?.data?.queryTwinEntity;
    if (!Array.isArray(entities)) {
      return { status: "failed", error: "Unexpected response shape from Dgraph." };
    }
    return entities.length > 0 ? { status: "found", twin: entities[0] } : { status: "absent" };
  } catch (error) {
    const e = error;
    console.error(
      "Error fetching twin:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return {
      status: "failed",
      error: error instanceof Error ? error.message : "Failed to fetch twin from Dgraph."
    };
  }
};
var dgqFetchTwin = async (baseUrl, accessToken, orgBID, twinBID) => {
  const outcome = await dgqFetchTwinOutcome(baseUrl, accessToken, orgBID, twinBID);
  return outcome.status === "found" ? outcome.twin : null;
};

// src/middleware/twin/dgqDeleteTwinEntity.ts
var deleteTwinEntity = async (baseUrl, accessToken, orgBID, twinBID) => {
  const res = await dGraphQuery(baseUrl, accessToken, orgBID, {
    query: "mutation DeleteTwinEntity($bID: String!) { deleteTwinEntity(filter: { bID: { eq: $bID } }) { msg numUids } }",
    variables: { bID: twinBID },
    operationName: "DeleteTwinEntity"
  });
  if (!res) {
    console.error("Error deleting twin entity: No response from Dgraph");
    return { success: false, error: "No response from Dgraph. Server may be unavailable." };
  }
  if (res.data && "errors" in res.data && res.data.errors && res.data.errors.length > 0) {
    const errorMessage = res.data.errors[0]?.message || "Unknown error from Dgraph";
    console.error("Error deleting twin entity:", res.data.errors);
    return { success: false, error: errorMessage };
  }
  return { success: true };
};
var dgqDeleteTwinEntity = async (baseUrl, accessToken, orgBID, twinBID, cascade = true) => {
  try {
    if (cascade) {
      const outcome = await dgqFetchTwinOutcome(baseUrl, accessToken, orgBID, twinBID);
      if (outcome.status === "failed") {
        console.error(`Error reading twin ${twinBID} before cascade delete:`, outcome.error);
        return {
          success: false,
          error: `Could not read the existing model before deleting it: ${outcome.error}`
        };
      }
      const bIDs = outcome.status === "found" ? extractBIDs(outcome.twin) : [];
      for (let index = 0; index < bIDs.length; index++) {
        const bID = bIDs[index];
        const result = await deleteTwinEntity(baseUrl, accessToken, orgBID, bID);
        if (!result.success) {
          console.error(`Error deleting twin entity ${bID}:`, result.error);
          return { success: false, error: result.error };
        }
      }
    } else {
      const result = await deleteTwinEntity(baseUrl, accessToken, orgBID, twinBID);
      if (!result.success) {
        console.error(`Error deleting twin entity ${twinBID}:`, result.error);
        return { success: false, error: result.error };
      }
    }
    return { success: true };
  } catch (error) {
    const e = error;
    console.error(
      "Error deleting twin:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to delete twin"
    };
  }
};

// src/middleware/twin/dgqGetTwinModels.ts
var dgqGetTwinModels = async (baseUrl, accessToken, orgBID, name) => {
  try {
    const res = await dGraphQuery(baseUrl, accessToken, orgBID, {
      query: `query queryTwinEntity($name: String) { queryTwinEntity(filter: { name: { regexp: $name } }) {
                id
                bID
                name
            }
                }`,
      variables: { name: `/^${name}/` },
      operationName: "queryTwinEntity"
    });
    if (res && res.data.data.queryTwinEntity.length > 0) {
      return res.data.data.queryTwinEntity;
    }
    return [];
  } catch (error) {
    console.error("Error fetching twin models:", error);
    return [];
  }
};

// src/middleware/twin/dgqInsertTwin.ts
var dgqInsertTwin = async (baseUrl, accessToken, orgBID, tree) => {
  try {
    const res = await dGraphQuery(baseUrl, accessToken, orgBID, {
      query: "mutation addTwinEntity($input: [AddTwinEntityInput!]!) { addTwinEntity(input: $input, upsert: true) { numUids } }",
      variables: { input: tree },
      operationName: "addTwinEntity"
    });
    if (!res) {
      console.error("Error inserting twin: No response from Dgraph");
      return { success: false, error: "No response from Dgraph. Server may be unavailable." };
    }
    if (res.data && "errors" in res.data && res.data.errors && res.data.errors.length > 0) {
      const errorMessage = res.data.errors[0]?.message || "Unknown error from Dgraph";
      console.error("Error inserting twin:", res.data.errors);
      return { success: false, error: errorMessage };
    }
    return { success: true };
  } catch (error) {
    console.error("Error inserting twin:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to insert twin"
    };
  }
};

// src/middleware/twin/readOpeningHours.ts
import axios102 from "axios";
import {
  LAUNCH_HOURS_REASONS
} from "@repo/frontend-types";
var REASONS = LAUNCH_HOURS_REASONS;
var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
var isDateOrNull = (value) => value === null || typeof value === "string" && DATE_RE.test(value);
var isLaunchHours = (value) => {
  if (value === null || typeof value !== "object") return false;
  const v = value;
  return typeof v.ready === "boolean" && typeof v.reason === "string" && REASONS.includes(v.reason) && v.ready === (v.reason === "covered" || v.reason === "upcoming") && isDateOrNull(v.today) && isDateOrNull(v.from);
};
var openingHoursShapeProblem = (data) => {
  if (!data || !Array.isArray(data.versions)) return "no versions";
  if (data.launchHours === void 0)
    return "no launchHours (a middleware older than this viewer?)";
  if (!isLaunchHours(data.launchHours)) return "launchHours malformed";
  const authored = data.authored;
  if (!authored || typeof authored.etag !== "string" || !Array.isArray(authored.versions)) {
    return "authored malformed";
  }
  return null;
};
var readOpeningHours = async (baseUrl, accessToken, bID, signal) => {
  const deadline = deadlineSignal(MIDDLEWARE_REQUEST_TIMEOUT_MS);
  const controller = new AbortController();
  const abort = () => controller.abort();
  deadline.signal.addEventListener("abort", abort);
  signal?.addEventListener("abort", abort);
  if (signal?.aborted) abort();
  try {
    const res = await axios102.get(routes_default(baseUrl).get.twins.readOpeningHours(bID), {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      signal: controller.signal
    });
    const data = res.data;
    const problem = openingHoursShapeProblem(data);
    if (problem) {
      console.error("[launch-hours] reading opening hours failed:", res.status, problem);
      return { status: "failed", httpStatus: res.status };
    }
    return { status: "ready", hours: data };
  } catch (error) {
    if (signal?.aborted) return { status: "failed" };
    const e = error;
    console.error(
      "[launch-hours] reading opening hours failed:",
      e?.response?.status ?? "(no status)",
      deadline.signal.aborted ? `no answer within ${MIDDLEWARE_REQUEST_TIMEOUT_MS} ms` : e?.message ?? String(error)
    );
    return {
      status: "failed",
      ...e?.response?.status !== void 0 ? { httpStatus: e.response.status } : {}
    };
  } finally {
    deadline.clear();
    deadline.signal.removeEventListener("abort", abort);
    signal?.removeEventListener("abort", abort);
  }
};

// src/middleware/twin/openingHoursWrite.ts
import axios103 from "axios";
import {
  OPENING_HOURS_CHANGED_CODE
} from "@repo/frontend-types";
var REFUSAL_STATUSES = /* @__PURE__ */ new Set([400, 404, 409, 422]);
var messageOf = (data) => {
  if (data === null || typeof data !== "object") return null;
  const m = data.message;
  if (typeof m === "string" && m.trim() !== "") return m;
  if (Array.isArray(m) && m.every((x) => typeof x === "string") && m.length > 0) return m.join("; ");
  return null;
};
var sendOpeningHoursWrite = async (request, accessToken) => {
  const deadline = deadlineSignal(MIDDLEWARE_REQUEST_TIMEOUT_MS);
  const config2 = {
    headers: { Authorization: `Bearer ${accessToken}` },
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal: deadline.signal
  };
  try {
    const res = request.method === "post" ? await axios103.post(request.url, request.body, config2) : await axios103.delete(request.url, config2);
    const data = res.data;
    const problem = openingHoursShapeProblem(data);
    if (problem) {
      console.error(`[opening-hours] ${request.what} failed:`, res.status, problem);
      return { status: "failed", httpStatus: res.status };
    }
    return { status: "saved", hours: data };
  } catch (error) {
    const e = error;
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (status === 409 && data?.code === OPENING_HOURS_CHANGED_CODE) {
      return { status: "changed" };
    }
    const message = messageOf(data);
    if (status !== void 0 && REFUSAL_STATUSES.has(status) && message !== null) {
      return { status: "refused", httpStatus: status, message };
    }
    console.error(
      `[opening-hours] ${request.what} failed:`,
      status ?? "(no status)",
      deadline.signal.aborted ? `no answer within ${MIDDLEWARE_REQUEST_TIMEOUT_MS} ms` : message ?? e?.message ?? String(error)
    );
    return { status: "failed", ...status !== void 0 ? { httpStatus: status } : {} };
  } finally {
    deadline.clear();
  }
};
var saveOpeningHours = (baseUrl, accessToken, bID, body) => sendOpeningHoursWrite(
  {
    what: `saving ${bID}`,
    method: "post",
    url: routes_default(baseUrl).post.twins.saveOpeningHours(bID),
    body
  },
  accessToken
);
var removeStandardHours = (baseUrl, accessToken, bID, { effectiveDateFrom, expectedEtag }) => sendOpeningHoursWrite(
  {
    what: `removing ${bID}'s hours from ${effectiveDateFrom}`,
    method: "delete",
    url: routes_default(baseUrl).delete.twins.removeStandardHours(
      bID,
      effectiveDateFrom,
      expectedEtag
    )
  },
  accessToken
);
var removeSpecialPeriod = (baseUrl, accessToken, bID, { effectiveDateFrom, expectedEtag }) => sendOpeningHoursWrite(
  {
    what: `removing ${bID}'s special period from ${effectiveDateFrom}`,
    method: "delete",
    url: routes_default(baseUrl).delete.twins.removeSpecialPeriod(
      bID,
      effectiveDateFrom,
      expectedEtag
    )
  },
  accessToken
);

// src/middleware/twin/getTwinById.ts
import axios104 from "axios";
var getTwinById = async (baseUrl, accessToken, bID, options) => {
  const routes = routes_default(baseUrl);
  let twin = null;
  const url = `${routes.get.twins.getTwinById(bID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios104.get(url, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout },
      ...options?.signal && { signal: options.signal }
    });
    twin = res.data;
  } catch (error) {
    const e = error;
    console.error(
      `Error fetching twin by id ${bID}:`,
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return null;
  }
  return twin;
};

// src/middleware/twin/getTwinConfig.ts
import axios105 from "axios";
var getTwinConfig = async (baseUrl, accessToken, twinBID, include) => {
  const routes = routes_default(baseUrl);
  let url = routes.get.twins.getConfig(twinBID);
  if (include && include.length > 0) {
    url += `?include=${include.join(",")}`;
  }
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await axios105.get(url, {
      headers: headers3
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch twin config for ${twinBID}:`, error);
    return null;
  }
};

// src/middleware/twin/getEquipmentClassifications.ts
import axios106 from "axios";
var getEquipmentClassifications = async (baseUrl, accessToken, twinBID) => {
  const routes = routes_default(baseUrl);
  const url = routes.get.twins.getEquipmentClassifications(twinBID);
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await axios106.get(url, { headers: headers3, timeout: 2e4 });
    return response.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`Failed to fetch equipment classifications for ${twinBID}: ${message}`);
    return null;
  }
};

// src/middleware/twin/updateTwinCapabilities.ts
import axios107 from "axios";
var updateTwinCapabilities = async (baseUrl, accessToken, twinBID, payload) => {
  const routes = routes_default(baseUrl);
  if (!twinBID) {
    throw new Error("Unable to update twin capabilities. Missing bID.");
  }
  const url = routes.put.twins.updateCapabilities(twinBID);
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await axios107.put(url, payload, {
      headers: headers3,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    return response.data;
  } catch (error) {
    console.error(
      `\u274C Twin capabilities update failed for ${twinBID}:`,
      error.response?.status,
      error.response?.data,
      error.message
    );
    throw error;
  }
};

// src/middleware/twin/getTwinConfigHelpers.ts
var getTwinPreferencesOptimized = async (baseUrl, accessToken, twinBID) => {
  const config2 = await getTwinConfig(baseUrl, accessToken, twinBID, ["preferences"]);
  return config2?.preferences || null;
};
var getTwinHeroMetricsOptimized = async (baseUrl, accessToken, twinBID) => {
  const config2 = await getTwinConfig(baseUrl, accessToken, twinBID, ["hero_metrics"]);
  return config2?.hero_metrics || [];
};
var getTwinIndicatorConfigOptimized = async (baseUrl, accessToken, twinBID) => {
  const config2 = await getTwinConfig(baseUrl, accessToken, twinBID, ["indicator_configs"]);
  return config2?.indicator_configs || [];
};
var getTwinDashboardConfig = async (baseUrl, accessToken, twinBID) => {
  const config2 = await getTwinConfig(baseUrl, accessToken, twinBID, [
    "preferences",
    "hero_metrics"
  ]);
  return {
    preferences: config2?.preferences || null,
    hero_metrics: config2?.hero_metrics || []
  };
};
var getTwinCompleteConfig = async (baseUrl, accessToken, twinBID) => {
  return getTwinConfig(baseUrl, accessToken, twinBID);
};

// src/middleware/twin/getTwinEntityById.ts
import axios108 from "axios";
var getTwinEntityById = async (baseUrl, accessToken, bID) => {
  const routes = routes_default(baseUrl);
  let organisation = null;
  const url = `${routes.get.twinEntity.getTwinEntityById(bID)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios108.get(url, {
      headers: headers3
    });
    organisation = res.data;
  } catch (error) {
    console.error("Error fetching twin entity by ID:", error);
  }
  return organisation;
};

// src/middleware/twin/getTwinPreferences.ts
import axios109 from "axios";
var getTwinPreferences = async (baseUrl, accessToken, twinBID) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.get.twins.getConfig(twinBID)}?select=preferences`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await axios109.get(url, {
      headers: headers3
    });
    return response.data.preferences;
  } catch (error) {
    console.error(`Failed to fetch twin preferences for ${twinBID}:`, error);
    return null;
  }
};

// src/middleware/twin/getTwins.ts
import axios110 from "axios";
var getTwins2 = async (baseUrl, accessToken, queryParams) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins${queryParams ? `?${queryParams}` : ""}`;
    const res = await axios110.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    const e = error;
    console.error(
      "Error fetching twins:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return void 0;
  }
};

// src/middleware/twin/updateTwin.ts
import axios111 from "axios";
var updateTwin = async (baseUrl, accessToken, twinBID, data, options) => {
  const routes = routes_default(baseUrl);
  if (!twinBID) {
    throw new Error("Unable to update twin. Missing bID.");
  }
  const url = routes.patch.twins.updateTwinById(twinBID);
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const ifUnchanged = options?.ifUnchanged;
    const body = ifUnchanged && Object.keys(ifUnchanged).length > 0 ? { ...data, if_unchanged: ifUnchanged } : data;
    const result = await axios111.patch(url, body, {
      headers: headers3,
      ...options?.timeout != null && { timeout: options.timeout }
    });
    return result;
  } catch (error) {
    console.error(
      "\u274C Twin update failed:",
      error.response?.status,
      error.response?.data,
      error.message
    );
    throw error;
  }
};

// src/middleware/twin/twinChanged.ts
var twinChangedOf = (error) => {
  const response = error?.response;
  if (response?.status !== 409) return null;
  const data = response.data;
  if (data?.code !== "twin_changed") return null;
  const listed = Array.isArray(data.validationErrors) ? data.validationErrors : [];
  const fields = listed.map((item) => item?.field).filter((field) => typeof field === "string");
  const modifiedAt = typeof data.modified_at === "string" ? data.modified_at : null;
  return { fields, modifiedAt };
};

// src/middleware/twin/twinLifecycleStage.ts
import { TWIN_LIFECYCLE_RANK } from "@repo/frontend-types";
var LIFECYCLE_REQUEST_TIMEOUT_MS = 1e4;
var LOG = "[lifecycle]";
var rankOf = (stage) => typeof stage === "string" && Object.prototype.hasOwnProperty.call(TWIN_LIFECYCLE_RANK, stage) ? TWIN_LIFECYCLE_RANK[stage] : void 0;
var advanceTwinLifecycleStage = (current, target) => {
  if (!current) return target;
  const currentRank = rankOf(current);
  if (currentRank === void 0) return null;
  const targetRank = rankOf(target);
  if (targetRank === void 0) return null;
  return targetRank > currentRank ? target : null;
};
var describeError = (error) => {
  const e = error;
  return { status: e?.response?.status, message: e?.message ?? String(error) };
};
var runAdvance = async (baseUrl, accessToken, twinBID, target) => {
  try {
    for (let attempt = 0; ; attempt++) {
      const twin = await getTwinById(baseUrl, accessToken, twinBID, {
        timeout: LIFECYCLE_REQUEST_TIMEOUT_MS
      });
      if (!twin) {
        console.warn(`${LOG} could not read twin ${twinBID}; leaving its stage unchanged`);
        return false;
      }
      const next = advanceTwinLifecycleStage(twin.lifecycle_stage, target);
      if (!next) return true;
      try {
        await updateTwin(
          baseUrl,
          accessToken,
          twinBID,
          { lifecycle_stage: next },
          {
            timeout: LIFECYCLE_REQUEST_TIMEOUT_MS,
            ifUnchanged: { lifecycle_stage: twin.lifecycle_stage ?? null }
          }
        );
        return true;
      } catch (error) {
        if (attempt === 0 && twinChangedOf(error)) continue;
        throw error;
      }
    }
  } catch (error) {
    const { status, message } = describeError(error);
    console.warn(
      `${LOG} could not advance ${twinBID} to "${target}":`,
      status ?? "(no status)",
      message
    );
    return false;
  }
};
var inFlight = /* @__PURE__ */ new Map();
var persistTwinLifecycleStage = (baseUrl, accessToken, twinBID, target) => {
  const previousSettled = inFlight.get(twinBID) ?? Promise.resolve();
  const chained = previousSettled.then(() => runAdvance(baseUrl, accessToken, twinBID, target)).catch(() => false);
  inFlight.set(twinBID, chained);
  void chained.finally(() => {
    if (inFlight.get(twinBID) === chained) inFlight.delete(twinBID);
  });
  return chained;
};

// src/middleware/twin/thresholdAlerts/addThresholdAlertRules.ts
import axios112 from "axios";
var addThresholdAlertRuleset = async (baseUrl, accessToken, bID, payload) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.twins.addThresholdAlertRuleset(bID);
  let record;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    record = await axios112.post(url, payload, {
      headers: headers3
    });
  } catch (error) {
  }
  return record;
};

// src/middleware/twin/thresholdAlerts/deleteThresholdAlertRuleset.ts
import axios113 from "axios";
var deleteThresholdAlertRuleset = async (baseUrl, accessToken, bID, rulesetId) => {
  const routes = routes_default(baseUrl);
  const url = routes.delete.twins.deleteThresholdAlertRuleset(bID, rulesetId);
  let record;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    record = await axios113.delete(url, {
      headers: headers3
    });
  } catch (error) {
  }
  return record;
};

// src/middleware/twin/thresholdAlerts/getThresholdAlertRulesets.ts
import axios114 from "axios";
var getThresholdAlertRulesets = async (baseUrl, accessToken, bID) => {
  const routes = routes_default(baseUrl);
  const url = routes.get.twins.getThresholdAlertRulesets(bID);
  let record;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    record = await axios114.get(url, {
      headers: headers3
    });
  } catch (error) {
  }
  return record?.data;
};

// src/middleware/twin/thresholdAlerts/updateThresholdAlertRuleset.ts
import axios115 from "axios";
var updateThresholdAlertRuleset = async (baseUrl, accessToken, bID, rulesetId, payload) => {
  const routes = routes_default(baseUrl);
  const url = routes.patch.twins.updateThresholdAlertRuleset(bID, rulesetId);
  let record;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    record = await axios115.patch(url, payload, {
      headers: headers3
    });
  } catch (error) {
  }
  return record;
};

// src/middleware/twin-sense/rtsp-endpoints/addRTSPEndpoint.ts
import axios116 from "axios";
var addRTSPEndpoint = async (baseUrl, accessToken, twinId, payload) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.twins.addRTSPEndpoint(twinId);
  let record;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    record = await axios116.post(url, payload, {
      headers: headers3,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
  } catch (error) {
  }
  return record;
};

// src/middleware/twin-sense/rtsp-endpoints/captureFrame.ts
import axios117 from "axios";
var CaptureFramesPurpose = /* @__PURE__ */ ((CaptureFramesPurpose2) => {
  CaptureFramesPurpose2["ObserverFrame"] = "ObserverFrame";
  CaptureFramesPurpose2["SkeletonDetection"] = "SkeletonDetection";
  CaptureFramesPurpose2["RTSPCaptureRun"] = "RTSPCaptureRun";
  return CaptureFramesPurpose2;
})(CaptureFramesPurpose || {});
var captureFrame = async (baseUrl, accessToken, twinId, rtspEndpointId, configId, purpose, options = {}) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.twins.captureFrame(twinId, rtspEndpointId);
  const res = await axios117.post(
    url,
    {
      ...configId != null ? { configId } : {},
      purpose,
      annotation_run_id: options.annotation_run_id ?? null,
      interval: options.interval ?? null,
      limit: options.limit ?? null,
      frame_id: options.frame_id ?? null
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      /*
       * Bounded. Unbounded, this one poisoned a SHARED cache: Camera Feeds' feed-frame store (retired
       * by `camera-feeds-page-panel`) wrote `{ status: 'loading' }` for the endpoint before awaiting
       * it and skipped an endpoint already loading, so an unsettled POST parked that camera for the
       * rest of the session with no error and no retry. Every remaining caller still awaits it with
       * nothing else bounding the wait.
       *
       * `docs/coding-standards.md` §"Outbound calls and list reads" — the same budget the sibling
       * reads in this folder already carry.
       */
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    }
  );
  return res.data;
};

// src/middleware/twin-sense/rtsp-endpoints/deleteRTSPEndpoint.ts
import axios118 from "axios";
var deleteRTSPEndpoint = async (baseUrl, accessToken, twinId, rtspID) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.delete.twins.deleteRTSPEndpoint(twinId, rtspID)}`;
  let success = false;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const result = await axios118.delete(url, {
      headers: headers3
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/twin-sense/rtsp-endpoints/getRTSPEndpoints.ts
import axios119 from "axios";
var getRTSPEndpoints = async (baseUrl, accessToken, twinId) => {
  const routes = routes_default(baseUrl);
  let rtspEndpoints = [];
  const url = `${routes.get.twins.getRTSPEndpoints(twinId)}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios119.get(url, {
      headers: headers3
    });
    rtspEndpoints = res.data;
  } catch (error) {
  }
  return rtspEndpoints;
};

// src/middleware/twin-sense/rtsp-endpoints/getRTSPEndpointById.ts
var getRTSPEndpointById = async (baseUrl, accessToken, twinId, rtspId) => {
  try {
    const endpoints = await getRTSPEndpoints(baseUrl, accessToken, twinId);
    if (endpoints && Array.isArray(endpoints)) {
      const endpoint = endpoints.find((ep) => ep.id === rtspId);
      return endpoint || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting RTSP endpoint by ID:", error);
    return null;
  }
};

// src/middleware/twin-sense/rtsp-endpoints/getRTSPEndpointsOrThrow.ts
import axios120 from "axios";
var getRTSPEndpointsOrThrow = async (baseUrl, accessToken, twinId) => {
  const routes = routes_default(baseUrl);
  const res = await axios120.get(routes.get.twins.getRTSPEndpoints(twinId), {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    /*
     * Bounded, because a screen BLOCKS on this read: unbounded (axios's default), a stalled
     * connection leaves the Observers stage showing a loading state for ever, with no way for the
     * operator to tell a slow twin from a broken one — which is the precise failure the throwing
     * variant of this function exists to prevent, arriving by the back door.
     *
     * `docs/coding-standards.md` §"Outbound calls and list reads" — apply
     * `MIDDLEWARE_REQUEST_TIMEOUT_MS` when you touch one of this package's reads. The sibling
     * TwinSense read `getTwinSenseConfigsOutcome` already does.
     */
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/twin-sense/rtsp-endpoints/getRTSPStatus.ts
import axios121 from "axios";
var getRTSPStatus = async (baseUrl, accessToken, twinId) => {
  const routes = routes_default(baseUrl);
  const res = await axios121.get(routes.get.twins.getRTSPStatus(twinId), {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    // 30s outbound cap (MIDDLEWARE_REQUEST_TIMEOUT_MS), the value declared once in
    // docs/coding-standards.md "Outbound calls and list reads". This call previously passed no
    // timeout at all, so axios's default (0 = wait for ever) applied. That matters more than it
    // looks on a POLLED read: react-query's refetchInterval does not fire while a fetch is in
    // flight, so ONE hung request stops the poll permanently and the surface silently freezes on
    // its last value. Added by setup-app-uplift-box-truth, which puts a third caller on it.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};

// src/middleware/twin-sense/rtsp-endpoints/streamFrames.ts
import axios122 from "axios";
var EnsureLatestStreamFrameStatus = /* @__PURE__ */ ((EnsureLatestStreamFrameStatus2) => {
  EnsureLatestStreamFrameStatus2["Stored"] = "stored";
  EnsureLatestStreamFrameStatus2["Capturing"] = "capturing";
  EnsureLatestStreamFrameStatus2["NotConfigured"] = "not_configured";
  return EnsureLatestStreamFrameStatus2;
})(EnsureLatestStreamFrameStatus || {});
var ensureLatestStreamFrame = async (baseUrl, accessToken, twinId, rtspEndpointId, signal, force = false) => {
  const routes = routes_default(baseUrl);
  const res = await axios122.post(
    routes.post.twins.ensureLatestStreamFrame(twinId, rtspEndpointId, force),
    void 0,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal,
      // Axios defaults to NO timeout and this repo sets no global default, so an unbounded call
      // here would hang a limiter slot until Azure's 230s front-end timeout — with the wall fanning
      // out 30-wide, six hung sockets deadlock the whole pool. `MIDDLEWARE_REQUEST_TIMEOUT_MS`
      // exists because that exact failure has already happened once in this package.
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    }
  );
  return res.data;
};
var getLatestStreamFrame = async (baseUrl, accessToken, twinId, rtspEndpointId, signal) => {
  const routes = routes_default(baseUrl);
  const res = await axios122.get(routes.get.twins.getLatestStreamFrame(twinId, rtspEndpointId), {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
    // Axios defaults to NO timeout; a table fans this out per row through a six-slot limiter, so
    // one hung socket would park a slot indefinitely.
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};
var fetchStreamFrame = async (baseUrl, accessToken, twinId, rtspEndpointId, frameId, signal, size) => {
  const routes = routes_default(baseUrl);
  const response = await axios122.get(
    routes.get.twins.getStreamFrame(twinId, rtspEndpointId, frameId, size),
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      responseType: "blob",
      signal,
      // Axios defaults to NO timeout. This sits inside the wall's 30-iteration poll, so an unbounded
      // call would park a tile, and one of six concurrency slots, indefinitely.
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      // 404 is an expected outcome (not stored yet); every other 4xx/5xx still throws.
      validateStatus: (status) => status >= 200 && status < 300 || status === 404
    }
  );
  if (response.status === 404) return null;
  const blob = response.data;
  const contentType = response.headers?.["content-type"]?.toString() || blob.type || "image/jpeg";
  return { blob, contentType };
};

// src/middleware/twin-sense/rtsp-endpoints/thumbnailRuns.ts
import axios123 from "axios";
var startThumbnailRun = async (baseUrl, accessToken, twinId, includeDisabled, signal) => {
  const routes = routes_default(baseUrl);
  const res = await axios123.post(
    routes.post.twins.startThumbnailRun(twinId),
    { include_disabled: includeDisabled },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal,
      // Axios defaults to NO timeout and this repo sets no global default.
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    }
  );
  return res.data;
};
var getThumbnailRun = async (baseUrl, accessToken, twinId, runId, signal) => {
  const routes = routes_default(baseUrl);
  const res = await axios123.get(routes.get.twins.getThumbnailRun(twinId, runId), {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    // 404 is an expected answer (the run is gone); every other 4xx/5xx still throws.
    validateStatus: (status) => status >= 200 && status < 300 || status === 404
  });
  if (res.status === 404) return null;
  return res.data;
};

// src/middleware/twin-sense/rtsp-endpoints/updateRTSPEndpoint.ts
import axios124 from "axios";
var updateRTSPEndpoint = async (baseUrl, accessToken, twinId, rtspId, payload) => {
  const routes = routes_default(baseUrl);
  const url = routes.patch.twins.updateRTSPEndpoint(twinId, rtspId.toString());
  let success = false;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const result = await axios124.patch(url, payload, {
      headers: headers3,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    return result;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/twin-sense/rtsp-endpoints/pushRTSPConfig.ts
import axios125 from "axios";
var pushRTSPConfig = async (baseUrl, accessToken, twinId, boxIds) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.twins.pushRTSPConfig(twinId);
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    return await axios125.post(url, { boxIds }, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
  } catch (error) {
    return void 0;
  }
};

// src/middleware/twin-sense/rtsp-endpoints/streamBalance.ts
import axios126 from "axios";
var authHeaders = (accessToken) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${accessToken}`
});
var measureStreamBalance = async (baseUrl, accessToken, twinId) => {
  const url = routes_default(baseUrl).post.twins.measureStreamBalance(twinId);
  const res = await axios126.post(
    url,
    {},
    { headers: authHeaders(accessToken), timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS }
  );
  return res.data;
};
var getStreamBalancePlan = async (baseUrl, accessToken, twinId, measuredSince) => {
  const url = routes_default(baseUrl).get.twins.getStreamBalancePlan(twinId);
  const res = await axios126.get(url, {
    headers: authHeaders(accessToken),
    params: measuredSince ? { measured_since: measuredSince } : void 0,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  return res.data;
};
var applyStreamBalancePlan = async (baseUrl, accessToken, twinId, planToken, moves) => {
  const url = routes_default(baseUrl).post.twins.applyStreamBalancePlan(twinId);
  try {
    const res = await axios126.post(
      url,
      { plan_token: planToken, moves },
      { headers: authHeaders(accessToken), timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS }
    );
    return { kind: "ok", result: res.data };
  } catch (error) {
    const status = axios126.isAxiosError(error) ? error.response?.status : void 0;
    if (status === 409) {
      return { kind: "stale" };
    }
    if (status === 400) {
      return { kind: "invalid" };
    }
    return { kind: "unknown" };
  }
};

// src/middleware/twin-sense/ssh-connection/createSshConnection.ts
var createSshConnection = async (baseUrl, accessToken, connectionPayload) => {
  const config2 = {
    url: `${baseUrl}api/ssh-connections`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: connectionPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/ssh-connection/deleteSshConnection.ts
var deleteSshConnection = async (baseUrl, accessToken, connectionId) => {
  const config2 = {
    url: `${baseUrl}api/ssh-connections/${connectionId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/ssh-connection/getSshConnection.ts
import axios127 from "axios";
var getSshConnection = async (baseUrl, accessToken, connectionId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/ssh-connections/${connectionId}`;
    const res = await axios127.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching SSH connection by ID:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/ssh-connection/getSshConnectionByPort.ts
import axios128 from "axios";
var getSshConnectionByPort = async (baseUrl, accessToken, twinId, tunnelPort) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/ssh-connections/port/${tunnelPort}`;
    const res = await axios128.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching SSH connection by port:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/ssh-connection/getSshConnections.ts
import axios129 from "axios";
var getSshConnections = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/ssh-connections`;
    const res = await axios129.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching SSH connections:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/ssh-connection/testSshConnection.ts
var testSshConnection = async (baseUrl, accessToken, connectionId) => {
  const config2 = {
    url: `${baseUrl}api/ssh-connections/${connectionId}/test`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/ssh-connection/updateSshConnection.ts
var updateSshConnection = async (baseUrl, accessToken, connectionId, connectionPayload) => {
  const config2 = {
    url: `${baseUrl}api/ssh-connections/${connectionId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: connectionPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-composite-measure/getTwinSenseCompositeMeasures.ts
import axios130 from "axios";
var getTwinSenseCompositeMeasures = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/twin-sense-composite-measures`;
    const res = await axios130.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching composite measure:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/twin-sense-configuration/createTwinSenseConfig.ts
var createTwinSenseConfig = async (baseUrl, accessToken, twinId, payload) => {
  const config2 = {
    url: `${baseUrl}/twins/${twinId}/twin-sense-configs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-configuration/getTwinSenseConfigs.ts
import axios131 from "axios";
var getTwinSenseConfigsOutcome = async (baseUrl, accessToken, twinId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/twin-sense-configs`;
    const res = await axios131.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    if (!Array.isArray(res.data)) {
      return { status: "failed", error: "Unexpected response shape from the twin-sense API." };
    }
    return res.data.length > 0 ? { status: "found", configs: res.data } : { status: "absent" };
  } catch (error) {
    const e = error;
    console.error(
      "Error fetching nola configs:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return {
      status: "failed",
      error: error instanceof Error ? error.message : "Failed to fetch TwinSense configurations.",
      ...e?.response?.status !== void 0 ? { httpStatus: e.response.status } : {}
    };
  }
};
var getTwinSenseConfigs = async (baseUrl, accessToken, twinId) => {
  const outcome = await getTwinSenseConfigsOutcome(baseUrl, accessToken, twinId);
  if (outcome.status === "found") return outcome.configs;
  return outcome.status === "absent" ? [] : void 0;
};

// src/middleware/twin-sense/twin-sense-configuration/updateTwinSenseConfig.ts
var updateTwinSenseConfig = async (baseUrl, accessToken, twinId, configId, payload) => {
  const config2 = {
    url: `${baseUrl}/twins/${twinId}/twin-sense-configs/${configId} `,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-message/createTwinSenseMessage.ts
var createTwinSenseMessage = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/twin-sense-messages`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-message/uploadObserverFrame.ts
import axios132 from "axios";
var uploadObserverFrame = async (baseUrl, accessToken, edgeDeviceBid, frameId, file, onUploadProgress) => {
  try {
    const formData = new FormData();
    formData.append("edge_device_bid", edgeDeviceBid);
    formData.append("frame_id", frameId);
    formData.append("image", file);
    const response = await axios132.post(
      `${baseUrl}api/twin-sense-messages/observer-frame`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percent = Math.round(progressEvent.loaded * 100 / progressEvent.total);
            onUploadProgress(percent);
          }
        }
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (axios132.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";
      if (response && response.statusText) {
        message = response.statusText;
      }
      if (axiosError.message) {
        message = axiosError.message;
      }
      if (response && response.data && response.data.message) {
        message = response.data.message;
      }
      return { data: null, error: { message } };
    }
    return { data: null, error: { message: error.message } };
  }
};

// src/middleware/twin-sense/twin-sense-message/fetchSkeletonDetectionFrame.ts
import axios133 from "axios";
var fetchSkeletonDetectionFrame = async (baseUrl, accessToken, edgeDeviceBid, frameId, signal) => {
  try {
    const response = await axios133.get(
      `${baseUrl}api/twin-sense-messages/skeleton-detection-frame/${encodeURIComponent(edgeDeviceBid)}/${encodeURIComponent(frameId)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: "blob",
        signal,
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        validateStatus: (status) => status >= 200 && status < 300 || status === 404
      }
    );
    if (response.status === 404) return null;
    const blob = response.data;
    const contentType = response.headers["content-type"]?.toString() || blob.type || "image/jpeg";
    return { blob, contentType };
  } catch (error) {
    if (axios133.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// src/middleware/twin-sense/twin-sense-message/checkSkeletonDetections.ts
import axios134 from "axios";
import { normaliseServerMessage } from "@repo/front-end-utils";
var UPSTREAM_CODE = /^[A-Za-z0-9_]{1,16}$/;
var UNREACHABLE_MESSAGE = "Camera activity couldn't be checked \u2014 we couldn't reach the server.";
var UNAVAILABLE_FALLBACK = "Camera activity could not be checked.";
var checkSkeletonDetections = async (baseUrl, accessToken, twinId, options = {}) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  const params = { twinId: String(twinId) };
  if (options.rtspBid !== void 0) params.rtspBid = options.rtspBid;
  if (options.sinceMs !== void 0) params.sinceMs = String(options.sinceMs);
  try {
    const res = await axios134.get(
      `${baseUrl}api/twin-sense-messages/skeleton-detections/check`,
      { headers: headers3, params, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS }
    );
    if (!res.data || typeof res.data.received !== "boolean") {
      console.error("Skeleton-detection check answered 200 with an unusable body:", res.data);
      return { status: "unreachable", message: UNREACHABLE_MESSAGE };
    }
    return { status: "checked", result: res.data };
  } catch (error) {
    const { status, message } = describeRequestError(error);
    console.error("Skeleton-detection check failed:", status ?? "(no response)", message);
    const response = axios134.isAxiosError(error) ? error.response : void 0;
    const body = response?.data;
    if (response?.status === 503 && typeof body?.reason === "string" && body.reason !== "") {
      const code = typeof body.code === "string" && UPSTREAM_CODE.test(body.code) ? body.code : null;
      return {
        status: "unavailable",
        reason: body.reason,
        code,
        // The repo's single rule for turning a Nest envelope into a renderable sentence —
        // collapsed, capped at 300 characters, cut on code points. Not re-implemented here: it is
        // one function precisely because three copies of it had already drifted.
        message: normaliseServerMessage(body.message, UNAVAILABLE_FALLBACK)
      };
    }
    return { status: "unreachable", message: UNREACHABLE_MESSAGE };
  }
};

// src/middleware/twin-sense/twin-sense-message/uploadSkeletonDetectionFrame.ts
import axios135 from "axios";
var uploadSkeletonDetectionFrame = async (baseUrl, accessToken, edgeDeviceBid, frameId, file, onUploadProgress) => {
  try {
    const formData = new FormData();
    formData.append("edge_device_bid", edgeDeviceBid);
    formData.append("frame_id", frameId);
    formData.append("image", file);
    const response = await axios135.post(
      `${baseUrl}api/twin-sense-messages/skeleton-detection-frame`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percent = Math.round(progressEvent.loaded * 100 / progressEvent.total);
            onUploadProgress(percent);
          }
        }
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (axios135.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";
      if (response && response.statusText) {
        message = response.statusText;
      }
      if (axiosError.message) {
        message = axiosError.message;
      }
      if (response && response.data && response.data.message) {
        message = response.data.message;
      }
      return { data: null, error: { message } };
    }
    return { data: null, error: { message: error.message } };
  }
};

// src/middleware/twin-sense/heatmap/getHeatmap.ts
import axios136 from "axios";
var getHeatmap = async (baseUrl, accessToken, rtspBid) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/heatmap`;
    const res = await axios136.get(url, {
      headers: headers3,
      params: { rtsp_bid: rtspBid }
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching heatmap:", error);
    return void 0;
  }
};

// src/middleware/heat-grid/getHeatGridCameraOutcome.ts
import axios137 from "axios";
import { HEAT_GRID_CELL_ENCODING } from "@repo/front-end-utils";
var HEAT_GRID_READ_REASONS = {
  cameraNotFound: "This camera isn\u2019t known to the heat history",
  forbidden: "Your role cannot read heat history",
  timedOut: `The request timed out after ${Math.round(MIDDLEWARE_REQUEST_TIMEOUT_MS / 1e3)} s`,
  unreachable: "The request did not reach the server",
  unreadable: "The heat data couldn\u2019t be read",
  serviceError: (status) => `Service error (${status})`
};
var isHeatGridResponse = (data, part) => {
  if (typeof data !== "object" || data === null) return false;
  const body = data;
  const coverage = body.coverage;
  if (typeof coverage !== "object" || coverage === null || !Number.isFinite(coverage.daysRequested) || !Number.isFinite(coverage.daysBinned) || !Array.isArray(coverage.missing)) {
    return false;
  }
  if (!Number.isFinite(body.frames)) return false;
  const cells = body.parts?.[part];
  return typeof cells === "object" && cells !== null && cells.encoding === HEAT_GRID_CELL_ENCODING && Number.isInteger(cells.cells) && cells.cells >= 0 && Number.isFinite(cells.total) && typeof cells.data === "string";
};
var getHeatGridCameraOutcome = async (baseUrl, accessToken, rtspBid, window2, part, signal) => {
  try {
    const url = `${baseUrl}api/heat-grid/cameras/${encodeURIComponent(rtspBid)}`;
    const res = await axios137.get(url, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      params: { window: window2, parts: part },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      signal
    });
    if (!isHeatGridResponse(res.data, part)) {
      const got = res.data?.parts?.[part];
      console.warn(
        "Unexpected heat grid response shape for stream",
        rtspBid,
        typeof res.data,
        part,
        got?.encoding
      );
      return {
        status: "failed",
        code: "unreadable-response",
        reason: HEAT_GRID_READ_REASONS.unreadable
      };
    }
    return { status: "ready", value: res.data };
  } catch (error) {
    const e = error;
    const httpStatus = e?.response?.status;
    if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
      return { status: "cancelled" };
    }
    const serverCode = typeof e?.response?.data?.code === "string" ? e.response.data.code : void 0;
    console.warn(
      "Error fetching heat grid for stream",
      rtspBid,
      httpStatus ?? "(no status)",
      serverCode ?? "",
      e?.message ?? String(error)
    );
    if (httpStatus === 404) {
      return {
        status: "failed",
        code: "camera-not-found",
        reason: HEAT_GRID_READ_REASONS.cameraNotFound,
        httpStatus,
        serverCode
      };
    }
    if (httpStatus === 401 || httpStatus === 403) {
      return {
        status: "failed",
        code: "forbidden",
        reason: HEAT_GRID_READ_REASONS.forbidden,
        httpStatus
      };
    }
    if (httpStatus !== void 0) {
      return {
        status: "failed",
        code: "server-error",
        reason: HEAT_GRID_READ_REASONS.serviceError(httpStatus),
        httpStatus,
        serverCode
      };
    }
    if (e?.code === "ECONNABORTED") {
      return { status: "failed", code: "timeout", reason: HEAT_GRID_READ_REASONS.timedOut };
    }
    return { status: "failed", code: "network", reason: HEAT_GRID_READ_REASONS.unreachable };
  }
};

// src/middleware/observer-proposals/getObserverProposalsOutcome.ts
import axios138 from "axios";
var OBSERVER_PROPOSALS_READ_REASONS = {
  forbidden: "Your role cannot read suggestions",
  planNotFound: "The saved plan wasn\u2019t found",
  timedOut: `The request timed out after ${Math.round(MIDDLEWARE_REQUEST_TIMEOUT_MS / 1e3)} s`,
  unreachable: "The request did not reach the server",
  unreadable: "The suggestions couldn\u2019t be read",
  serviceError: (status) => `Service error (${status})`
};
var isPoint = (p) => typeof p === "object" && p !== null && Number.isFinite(p.x) && Number.isFinite(p.y);
var MACHINE_STATES = ["no-heat", "name-uncertain", "settled", "better", "not-watched"];
var MACHINE_REASONS = [
  "no-heat-region",
  "tied-with",
  "agrees",
  "drawn-holds",
  "better-part",
  "better-place",
  "suggested"
];
var isNullableNumber = (v) => v === null || Number.isFinite(v);
var isNamedBy = (v) => v === "calibration" || v === null;
var isProposal = (p) => {
  if (typeof p !== "object" || p === null) return false;
  const {
    observer,
    targetName,
    confidence,
    nameUncertain,
    confident,
    evidence,
    candidates,
    existing
  } = p;
  if (typeof observer !== "object" || observer === null) return false;
  const o = observer;
  return typeof o.refid === "string" && typeof o.trackSide === "string" && Array.isArray(o.points) && o.points.every(isPoint) && (targetName === null || typeof targetName === "string") && Number.isFinite(confidence) && typeof nameUncertain === "boolean" && typeof confident === "boolean" && typeof evidence === "object" && evidence !== null && Number.isFinite(evidence.partScore) && isNamedBy(evidence.namedBy) && Array.isArray(candidates) && Array.isArray(existing);
};
var isOutline = (o) => {
  if (typeof o !== "object" || o === null) return false;
  const { view, points } = o;
  return Number.isInteger(view) && view >= 0 && Array.isArray(points) && points.every(isPoint);
};
var isMachine = (m) => {
  if (typeof m !== "object" || m === null) return false;
  const r = m;
  return typeof r.targetRefid === "string" && (r.targetName === null || typeof r.targetName === "string") && typeof r.state === "string" && MACHINE_STATES.includes(r.state) && typeof r.reason === "string" && MACHINE_REASONS.includes(r.reason) && (r.proposalRefid === null || typeof r.proposalRefid === "string") && Array.isArray(r.drawnObserverRefids) && r.drawnObserverRefids.every((x) => typeof x === "string") && isNullableNumber(r.drawnScore) && Array.isArray(r.outlines) && r.outlines.every(isOutline);
};
var isCameraResult = (r) => isNamedBy(r.namedBy) && Array.isArray(r.proposals) && r.proposals.every(isProposal) && Number.isFinite(r.visibleMachines) && (r.machines === null || Array.isArray(r.machines) && r.machines.every(isMachine)) && (r.machinesUnavailable === null || r.machinesUnavailable === "not-calibrated") && Array.isArray(r.unexplainedHeat) && r.unexplainedHeat.every(isProposal) && Number.isFinite(r.unexplainedHeatTotal);
var isProposalsResponse = (data) => {
  if (typeof data !== "object" || data === null) return false;
  const body = data;
  if (!Array.isArray(body.cameras)) return false;
  if (!Array.isArray(body.camerasNotInPlan)) return false;
  if (!Array.isArray(body.warnings)) return false;
  if (!Number.isFinite(body.confidentThreshold)) return false;
  return body.cameras.every(
    (c) => typeof c === "object" && c !== null && typeof c.rtspEndpointBid === "string" && (c.status === "ok" || c.status === "skipped" || c.status === "failed") && Array.isArray(c.warnings) && (c.detail === null || c.detail === void 0 || typeof c.detail === "string") && // The walk-through card says how many days the heat came from.
    Number.isFinite(c.daysBinned) && (c.result === null || typeof c.result === "object" && isCameraResult(c.result))
  );
};
var getObserverProposalsOutcome = async (baseUrl, accessToken, planId, rtspBid, window2, signal) => {
  try {
    const url = `${baseUrl}api/twin-plans/${planId}/observer-proposals`;
    const res = await axios138.post(
      url,
      { cameras: [rtspBid], window: window2 },
      {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        signal
      }
    );
    if (!isProposalsResponse(res.data)) {
      console.warn("Unexpected observer proposals response shape for plan", planId, rtspBid);
      return {
        status: "failed",
        code: "unreadable-response",
        reason: OBSERVER_PROPOSALS_READ_REASONS.unreadable
      };
    }
    return { status: "ready", value: res.data };
  } catch (error) {
    const e = error;
    const httpStatus = e?.response?.status;
    if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
      return { status: "cancelled" };
    }
    if (httpStatus === 403) {
      return {
        status: "failed",
        code: "forbidden",
        reason: OBSERVER_PROPOSALS_READ_REASONS.forbidden,
        httpStatus
      };
    }
    if (httpStatus === 404) {
      return {
        status: "failed",
        code: "plan-not-found",
        reason: OBSERVER_PROPOSALS_READ_REASONS.planNotFound,
        httpStatus
      };
    }
    if (httpStatus !== void 0) {
      return {
        status: "failed",
        code: "server-error",
        reason: OBSERVER_PROPOSALS_READ_REASONS.serviceError(httpStatus),
        httpStatus
      };
    }
    if (e?.code === "ECONNABORTED") {
      return {
        status: "failed",
        code: "timeout",
        reason: OBSERVER_PROPOSALS_READ_REASONS.timedOut
      };
    }
    return {
      status: "failed",
      code: "network",
      reason: OBSERVER_PROPOSALS_READ_REASONS.unreachable
    };
  }
};

// src/middleware/twin-sense/twin-sense-statement/getConfigurationStatement.ts
import axios139 from "axios";
var looksLikeAStatement = (data) => typeof data === "object" && data !== null && Array.isArray(data.observers) && Array.isArray(data.composites) && Array.isArray(data.issues);
var getConfigurationStatementOutcome = async (baseUrl, accessToken, twinId, configId, signal) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/twin-sense-configs/${configId}/statement`;
    const res = await axios139.get(url, {
      headers: headers3,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      ...signal ? { signal } : {}
    });
    if (!looksLikeAStatement(res.data)) {
      console.error(
        "Unexpected configuration statement response shape for twin",
        twinId,
        "config",
        configId,
        typeof res.data
      );
      return { status: "failed", error: "Unexpected response shape from the twin-sense API." };
    }
    return { status: "found", statement: res.data };
  } catch (error) {
    const e = error;
    if (e?.name === "CanceledError" || e?.name === "AbortError" || signal?.aborted) {
      return { status: "failed", error: "cancelled" };
    }
    console.error(
      "Error fetching the configuration statement for twin",
      twinId,
      "config",
      configId,
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return {
      status: "failed",
      error: error instanceof Error ? error.message : "Failed to fetch the configuration statement.",
      ...e?.response?.status !== void 0 ? { httpStatus: e.response.status } : {}
    };
  }
};

// src/middleware/twin-sense/twin-sense-statement/fileConfigurationStatement.ts
import axios140 from "axios";
var looksLikeAFilingResult = (data) => typeof data === "object" && data !== null && typeof data.configId === "number" && typeof data.versionNumber === "number" && typeof data.observersWritten === "number" && typeof data.compositesWritten === "number" && // ⚠ `isActive` is read two lines below and branched on. Omitted from this guard, a 200 missing
// the field (an older deploy, a response-shaping proxy) reads as `!== true` and reports a filing
// that actually succeeded as one the twin was never switched to — sending the operator to
// activate a version that is already active, and skipping the edge push.
typeof data.isActive === "boolean";
var readFaults = (data) => {
  const raw = data?.faults;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const faults = raw.filter(
    (f) => typeof f === "object" && f !== null && typeof f.kind === "string" && typeof f.detail === "string"
  );
  return { faults, sent: raw.length };
};
var KNOWN_CODES = {
  refused: true,
  "not-written": true,
  "written-not-switched": true,
  "rollback-incomplete": true,
  "rollback-blocked-active": true,
  "bid-allocation-failed": true,
  "bid-carry-read-failed": true,
  "bid-collision": true,
  "version-collision": true
};
var CLEAN_FAILURE_CODES = /* @__PURE__ */ new Set([
  "not-written",
  "bid-allocation-failed",
  "bid-carry-read-failed",
  "bid-collision",
  "version-collision"
]);
var readCode = (data) => {
  const code = data?.code;
  return typeof code === "string" && code in KNOWN_CODES ? code : void 0;
};
var readMessage = (data) => {
  const message = data?.message;
  if (typeof message === "string" && message.length > 0) return message;
  if (Array.isArray(message) && message.length > 0) {
    const sentences = message.filter((m) => typeof m === "string");
    if (sentences.length > 0) return sentences.join(" ");
  }
  return null;
};
var CLEAN_FAILURE_PATTERNS = [
  // Rolled back after a write step failed. ⚠ Requires "nothing was activated AND" — the
  // ROLLBACK-INCOMPLETE sentence also contains "the twin is still measuring the configuration it
  // was", and matching that alone would classify a partial version left on the twin as a clean
  // failure. (That case is recognised earlier anyway; this keeps the two independent.)
  /nothing was activated and the twin is still measuring/i,
  // Failed before writing anything: the pre-write reads.
  /^could not read the .+ for this twin\.$/i,
  /^could not read the twin for this filing\.$/i,
  // Refused at PostgREST's silent 1,000-row cap, before anything is written. (Its wording says
  // "dry run" on the filing path too — a server-side wording defect, recorded for the backlog.)
  /read hit the row cap$/i
];
var isCleanServerFailure = (message) => !!message && CLEAN_FAILURE_PATTERNS.some((p) => p.test(message.trim()));
var isWrittenNotSwitched = (message) => !!message && /Configuration version \d+ was written/.test(message);
var isRollbackIncomplete = (message) => !!message && /partial configuration version \d+ could not be removed/i.test(message);
var fileConfigurationStatementOutcome = async (baseUrl, accessToken, twinId, statement) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twins/${twinId}/twin-sense-configs/statement`;
    const res = await axios140.post(url, statement, {
      headers: headers3,
      timeout: MIDDLEWARE_WRITE_TIMEOUT_MS
    });
    if (!looksLikeAFilingResult(res.data)) {
      console.error(
        "Unexpected filing response shape for twin",
        twinId,
        typeof res.data,
        // The status, not the body: a 200 from something that is not the middleware can carry
        // anything, and this line travels in screenshots.
        res.status
      );
      return { status: "unknown", error: "Unexpected response shape from the twin-sense API." };
    }
    if (res.data.isActive !== true) {
      console.error(
        "[go-live] the filing returned a version the twin was not switched to \u2014 twin",
        twinId,
        "configuration",
        res.data.configId
      );
      return {
        status: "written-not-switched",
        message: `Configuration version ${res.data.versionNumber} (id ${res.data.configId}) was written with ${res.data.observersWritten} observer(s), but the twin was not switched to it. Check this twin's active configuration and activate version ${res.data.versionNumber} if none is active.`
      };
    }
    return { status: "filed", result: res.data };
  } catch (error) {
    const e = error;
    const status = e?.response?.status;
    const body = e?.response?.data;
    const serverMessage = readMessage(body);
    const code = readCode(body);
    if (status === 400) {
      const read = readFaults(body);
      if (read) {
        console.warn("[go-live] filing refused", {
          twinId,
          faultsSent: read.sent,
          faults: read.faults
        });
        if (read.faults.length < read.sent) {
          console.warn("[go-live] the refusal carried faults this client could not read", {
            twinId,
            sent: read.sent,
            readable: read.faults.length
          });
        }
        return {
          status: "refused",
          message: serverMessage ?? "This configuration cannot be filed.",
          faults: read.faults
        };
      }
      console.warn("[go-live] filing refused with no fault list", { twinId, serverMessage });
      return {
        status: "refused",
        message: serverMessage ?? "This configuration cannot be filed.",
        faults: serverMessage ? [{ kind: "empty-statement", detail: serverMessage }] : []
      };
    }
    if (code === "rollback-incomplete" || code === "rollback-blocked-active") {
      console.error(
        "[go-live] filing failed AND its rollback failed \u2014 a partial version is left behind on twin",
        twinId,
        code,
        serverMessage
      );
      return {
        status: "rollback-incomplete",
        message: serverMessage ?? "A partial configuration version was left on this twin."
      };
    }
    if (code === "written-not-switched") {
      console.error(
        "[go-live] a version was written but the twin could not be switched to it \u2014 twin",
        twinId,
        serverMessage
      );
      return {
        status: "written-not-switched",
        message: serverMessage ?? "A configuration version was written but not activated."
      };
    }
    if (isRollbackIncomplete(serverMessage)) {
      console.error(
        "[go-live] filing failed AND its rollback failed \u2014 a partial version is left behind on twin",
        twinId,
        serverMessage
      );
      return { status: "rollback-incomplete", message: serverMessage };
    }
    if (isWrittenNotSwitched(serverMessage)) {
      console.error(
        "[go-live] a version was written but the twin could not be switched to it \u2014 twin",
        twinId,
        serverMessage
      );
      return { status: "written-not-switched", message: serverMessage };
    }
    const message = error instanceof Error ? error.message : "Failed to file the configuration.";
    if (typeof status === "number" && status >= 400 && status < 500) {
      console.error("[go-live] the filing was rejected", twinId, status, serverMessage ?? message);
      return { status: "failed", error: message, serverMessage, httpStatus: status, code };
    }
    if (!(code !== void 0 && CLEAN_FAILURE_CODES.has(code)) && !isCleanServerFailure(serverMessage)) {
      console.error("[go-live] the filing outcome is UNKNOWN for twin", twinId, message);
      return { status: "unknown", error: message };
    }
    console.error("Error filing the configuration statement for twin", twinId, status, message);
    return {
      status: "failed",
      error: message,
      serverMessage,
      // Either `code` or a matched sentence came off a response body, so a status came with it.
      // The fallback is defensive only; it never fires.
      httpStatus: status ?? 500,
      code
    };
  }
};

// src/middleware/twin-launch/recordLaunch.ts
import axios141 from "axios";
var looksLikeAResponse = (data) => {
  const d = data;
  return !!d && typeof d === "object" && typeof d.launch?.id === "number" && typeof d.lock?.serverNow === "string";
};
var recordLaunch = async (baseUrl, accessToken, twinId, input) => {
  try {
    const res = await axios141.post(`${baseUrl}api/twins/${twinId}/launches`, input, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      // The server bounds the whole request at 30 s; this backstop sits above it so the server
      // always answers first (see httpTimeout.ts).
      timeout: MIDDLEWARE_WRITE_TIMEOUT_MS
    });
    if (!looksLikeAResponse(res.data)) {
      return { status: "unknown", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "recorded", response: res.data };
  } catch (error) {
    const e = error;
    const httpStatus = e?.response?.status;
    const serverMessage = typeof e?.response?.data?.message === "string" ? e.response.data.message : void 0;
    const message = serverMessage ?? e?.message ?? "Failed to record the launch.";
    console.error("[go-live] recording the launch failed:", httpStatus ?? "(no status)", message);
    const indeterminate = httpStatus === void 0 || [502, 503, 504].includes(httpStatus);
    return {
      status: indeterminate ? "unknown" : "failed",
      error: message,
      ...httpStatus !== void 0 ? { httpStatus } : {}
    };
  }
};

// src/middleware/twin-launch/getLatestLaunch.ts
import axios142 from "axios";
var getLatestLaunch = async (baseUrl, accessToken, twinId) => {
  try {
    const res = await axios142.get(`${baseUrl}api/twins/${twinId}/launches/latest`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    const data = res.data;
    if (!data || typeof data.lock?.serverNow !== "string" || !("launch" in data)) {
      return { status: "failed", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "found", latest: data };
  } catch (error) {
    const e = error;
    console.warn(
      "[launch-lock] reading the latest launch failed:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return {
      status: "failed",
      error: error instanceof Error ? error.message : "Failed to read the latest launch.",
      ...e?.response?.status !== void 0 ? { httpStatus: e.response.status } : {}
    };
  }
};

// src/middleware/twin-launch/claimLaunch.ts
import axios143 from "axios";
var claimLaunch = async (baseUrl, accessToken, twinId, input) => {
  try {
    const res = await axios143.post(`${baseUrl}api/twins/${twinId}/launches/claim`, input, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    const data = res.data;
    if (!data || data.status !== "claimed" && data.status !== "locked" || typeof data.lock?.serverNow !== "string" || typeof data.launch?.launchUuid !== "string") {
      return { status: "failed", error: "Unexpected response shape from the launch history API." };
    }
    return { status: data.status, response: data };
  } catch (error) {
    const e = error;
    console.error(
      "[go-live] claiming the twin for a launch failed:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    const status = e?.response?.status;
    const body = e?.response?.data;
    if (status === 422 && body?.code === "opening-hours-not-ready" && isLaunchHours(body.launchHours)) {
      return { status: "refused", launchHours: body.launchHours };
    }
    if (status === 503 && body?.code === "opening-hours-unchecked") return { status: "unchecked" };
    return {
      status: "failed",
      error: error instanceof Error ? error.message : "Failed to claim the twin for a launch.",
      ...e?.response?.status !== void 0 ? { httpStatus: e.response.status } : {}
    };
  }
};
var releaseLaunch = async (baseUrl, accessToken, twinId, launchUuid) => {
  try {
    const res = await axios143.delete(`${baseUrl}api/twins/${twinId}/launches/${launchUuid}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    return res.data?.released === true;
  } catch (error) {
    const e = error;
    console.warn(
      "[go-live] giving back the launch claim failed; it will expire on its own:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return false;
  }
};

// src/middleware/twin-launch/productionLaunch.ts
import axios144 from "axios";
var getProductionLaunch = async (baseUrl, accessToken, twinId) => {
  try {
    const res = await axios144.get(`${baseUrl}api/twins/${twinId}/launches/production`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    const data = res.data;
    if (!data || typeof data.inProgress !== "boolean" || !("launch" in data)) {
      return { status: "failed", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "found", production: data };
  } catch (error) {
    const failure = describeFailure(error, "Failed to read the production launch.");
    console.warn(
      "[draft-choice] production read failed:",
      failure.httpStatus ?? "(no status)",
      failure.error
    );
    return { status: "failed", ...failure };
  }
};
var getLaunchPlan = async (baseUrl, accessToken, twinId, launchUuid) => {
  try {
    const res = await axios144.get(
      `${baseUrl}api/twins/${twinId}/launches/${encodeURIComponent(launchUuid)}/plan`,
      {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
      }
    );
    const data = res.data;
    if (!data || typeof data.launchUuid !== "string" || !data.plan || typeof data.plan !== "object") {
      return { status: "failed", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "found", launch: data };
  } catch (error) {
    const failure = describeFailure(error, "Failed to read the launch's plan.");
    console.warn(
      "[draft-choice] launch plan read failed:",
      failure.httpStatus ?? "(no status)",
      failure.error
    );
    return { status: "failed", ...failure };
  }
};

// src/middleware/twin-launch/launchHistory.ts
import axios145 from "axios";
var getLaunchHistory = async (baseUrl, accessToken, twinId) => {
  try {
    const res = await axios145.get(`${baseUrl}api/twins/${twinId}/launches`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    const data = res.data;
    if (!data || !Array.isArray(data.launches) || typeof data.inProgress !== "boolean" || typeof data.serverNow !== "string" || typeof data.claimTimeoutSeconds !== "number" || !("productionLaunchUuid" in data)) {
      return { status: "failed", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "found", history: data };
  } catch (error) {
    const failure = describeFailure(error, "Failed to read the launch history.");
    console.warn(
      "[launch-history] list read failed:",
      failure.httpStatus ?? "(no status)",
      failure.error
    );
    return { status: "failed", ...failure };
  }
};

// src/middleware/twin-launch/setLaunchBiOutcome.ts
import axios146 from "axios";
var setLaunchBiOutcome = async (baseUrl, accessToken, twinId, launchUuid, input) => {
  const deadline = deadlineSignal(BI_OUTCOME_SAVE_TIMEOUT_MS);
  try {
    const res = await axios146.put(
      `${baseUrl}api/twins/${twinId}/launches/${encodeURIComponent(launchUuid)}/bi-outcome`,
      input,
      {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        timeout: BI_OUTCOME_SAVE_TIMEOUT_MS,
        // Wall-clock bound; axios's `timeout` is socket inactivity only.
        signal: deadline.signal
      }
    );
    const data = res.data;
    if (!data || typeof data.launch?.id !== "number") {
      return { status: "unknown", error: "Unexpected response shape from the launch history API." };
    }
    return { status: "saved", response: data };
  } catch (error) {
    const e = error;
    const httpStatus = e?.response?.status;
    if (httpStatus === 409 && e?.response?.data?.code === "bi-outcome-final") {
      return { status: "final" };
    }
    const serverMessage = typeof e?.response?.data?.message === "string" ? e.response.data.message : void 0;
    const message = serverMessage ?? e?.message ?? "Failed to record the launch's BI result.";
    console.error(
      "[go-live] recording the launch's BI result failed:",
      httpStatus ?? "(no status)",
      message
    );
    const indeterminate = httpStatus === void 0 || [502, 503, 504].includes(httpStatus);
    return {
      status: indeterminate ? "unknown" : "failed",
      error: message,
      ...httpStatus !== void 0 ? { httpStatus } : {}
    };
  } finally {
    deadline.clear();
  }
};

// src/middleware/twin-sense/twin-sense-observer/createTwinSenseObserver.ts
var createTwinSenseObserver = async (baseUrl, accessToken, configId, servicePayload, frameFile) => {
  const formData = new FormData();
  Object.entries(servicePayload).forEach(([key, value]) => {
    if (value !== void 0 && value !== null) {
      formData.append(key, String(value));
    }
  });
  if (frameFile) {
    formData.append("frame", frameFile);
  }
  const config2 = {
    url: `${baseUrl}api/twin-sense-configs/${configId}/twin-sense-observers`,
    method: "POST",
    headers: {
      // Don't set content-type - axios will set it with boundary for multipart
      Authorization: `Bearer ${accessToken}`
    },
    data: formData
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-observer/getTwinSenseObserverByBid.ts
import axios147 from "axios";
var getTwinSenseObserverByBid = async (baseUrl, accessToken, observerBid) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-sense-observers/bid/${encodeURIComponent(observerBid)}`;
    const res = await axios147.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching observer by BID:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/twin-sense-retention-schedule/createRetentionSchedule.ts
var createRetentionSchedule = async (baseUrl, accessToken, schedulePayload) => {
  const config2 = {
    url: `${baseUrl}api/twin-sense-retention-schedules`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: schedulePayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-retention-schedule/deleteRetentionSchedule.ts
var deleteRetentionSchedule = async (baseUrl, accessToken, scheduleId) => {
  const config2 = {
    url: `${baseUrl}api/twin-sense-retention-schedules/${scheduleId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-sense/twin-sense-retention-schedule/getRetentionScheduleById.ts
import axios148 from "axios";
var getRetentionScheduleById = async (baseUrl, accessToken, scheduleId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-sense-retention-schedules/${scheduleId}`;
    const res = await axios148.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching retention schedule by ID:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/twin-sense-retention-schedule/getRetentionSchedules.ts
import axios149 from "axios";
var getRetentionSchedules = async (baseUrl, accessToken, deviceId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const params = deviceId ? { deviceId } : {};
    const url = `${baseUrl}api/twin-sense-retention-schedules`;
    const res = await axios149.get(url, { headers: headers3, params });
    return res.data;
  } catch (error) {
    console.error("Error fetching retention schedules:", error);
    return void 0;
  }
};

// src/middleware/twin-sense/twin-sense-retention-schedule/updateRetentionSchedule.ts
var updateRetentionSchedule = async (baseUrl, accessToken, scheduleId, schedulePayload) => {
  const config2 = {
    url: `${baseUrl}api/twin-sense-retention-schedules/${scheduleId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: schedulePayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/annotation-run/createAnnotationRun.ts
var createAnnotationRun = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/annotation-run/deleteAnnotationRun.ts
var deleteAnnotationRun = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/model/getModels.ts
import axios150 from "axios";
var getModels = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/models`;
    const res = await axios150.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching models:", error);
    return void 0;
  }
};

// src/middleware/model-file/getModelFiles.ts
import axios151 from "axios";
var listingCache = createTtlCache(6e4);
var getModelFiles = (baseUrl, accessToken, options) => (
  // Keyed on the base URL alone: this listing is the same for every authorised caller, so a value
  // read with one operator's token is the value the next would have read.
  listingCache.read(baseUrl, () => readModelFiles(baseUrl, accessToken), options)
);
var readModelFiles = async (baseUrl, accessToken) => {
  const url = `${baseUrl}api/model-files`;
  const res = await axios151.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  });
  const files = res.data?.files;
  if (!Array.isArray(files) || files.some((name) => typeof name !== "string")) {
    console.error("GET /api/model-files answered a body that is not the documented envelope");
    throw new Error("GET /api/model-files returned no `files` array of strings");
  }
  return files;
};

// src/middleware/live-twin-models/liveTwinModels.ts
import axios152 from "axios";
var LiveTwinModelsError = class extends Error {
  constructor(message, status, outcomeUnknown, aborted) {
    super(message);
    this.status = status;
    this.outcomeUnknown = outcomeUnknown;
    this.aborted = aborted;
    this.name = "LiveTwinModelsError";
  }
};
var toError = (error, what) => {
  if (axios152.isCancel?.(error) || error?.name === "CanceledError") {
    return new LiveTwinModelsError(`${what} was cancelled.`, null, false, true);
  }
  if (axios152.isAxiosError(error) && error.response) {
    const body = error.response.data;
    const message = Array.isArray(body?.message) ? body?.message.join(" ") : typeof body?.message === "string" && body.message ? body.message : `${what} failed (HTTP ${error.response.status}).`;
    const status = error.response.status;
    const definite = status < 500 || status === 503;
    return new LiveTwinModelsError(message, status, !definite, false);
  }
  return new LiveTwinModelsError(`${what} got no answer from the server.`, null, true, false);
};
var headers = (accessToken) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${accessToken}`
});
var getLiveTwinModelDrift = async (baseUrl, accessToken, options = {}) => {
  let data;
  try {
    const response = await axios152.get(
      `${baseUrl}api/live-twin-models/drift`,
      {
        headers: headers(accessToken),
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        ...options.signal ? { signal: options.signal } : {}
      }
    );
    data = response.data;
  } catch (error) {
    throw toError(error, "Reading the live twins");
  }
  const body = data;
  if (!body || !Array.isArray(body.twins) || typeof body.unreadableCount !== "number") {
    throw new LiveTwinModelsError(
      "The live twins report came back in an unexpected shape.",
      null,
      false,
      false
    );
  }
  return body;
};
var updateLiveTwinModels = async (baseUrl, accessToken, twinId, machines) => {
  let data;
  try {
    const response = await axios152.post(
      `${baseUrl}api/live-twin-models/${encodeURIComponent(String(twinId))}/update`,
      { machines },
      { headers: headers(accessToken), timeout: MIDDLEWARE_WRITE_TIMEOUT_MS }
    );
    data = response.data;
  } catch (error) {
    throw toError(error, "Updating the models");
  }
  const body = data;
  if (!body || !Array.isArray(body.results)) {
    throw new LiveTwinModelsError(
      "The update came back in an unexpected shape; it may have partly applied.",
      null,
      true,
      false
    );
  }
  return body;
};

// src/middleware/annotation-run/getAnnotationRunById.ts
import axios153 from "axios";
var getAnnotationRunById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs/${id}`;
    const res = await axios153.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching annotation run:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/getAnnotationRuns.ts
import axios154 from "axios";
var getAnnotationRuns = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs`;
    const res = await axios154.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching annotation runs:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/updateAnnotationRun.ts
var updateAnnotationRun = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/annotation-run/getAnnotationRunFrames.ts
import axios155 from "axios";
var getAnnotationRunFrames = async (baseUrl, accessToken, annotationRunId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs/${annotationRunId}/frames`;
    const res = await axios155.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching annotation run frames:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/createAnnotationRunFrame.ts
var createAnnotationRunFrame = async (baseUrl, accessToken, annotationRunId, payload) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs/${annotationRunId}/frames`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/annotation-run/deleteAnnotationRunFrame.ts
var deleteAnnotationRunFrame = async (baseUrl, accessToken, annotationRunId, frameId) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs/${annotationRunId}/frames/${frameId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/annotation-run/uploadAnnotationRunFrame.ts
import axios156 from "axios";
var uploadAnnotationRunFrame = async (baseUrl, accessToken, annotationRunId, file, onUploadProgress, rtspCaptureRunId) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (rtspCaptureRunId != null) {
      formData.append("rtsp_capture_run_id", String(rtspCaptureRunId));
    }
    const response = await axios156.post(
      `${baseUrl}api/annotation-runs/${annotationRunId}/frames/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percent = Math.round(progressEvent.loaded * 100 / progressEvent.total);
            onUploadProgress(percent);
          }
        }
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (axios156.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";
      if (response && response.statusText) {
        message = response.statusText;
      }
      if (axiosError.message) {
        message = axiosError.message;
      }
      if (response && response.data && response.data.message) {
        message = response.data.message;
      }
      return { data: null, error: { message } };
    }
    return { data: null, error: { message: error.message } };
  }
};

// src/middleware/annotation-run/getAnnotationRunFrameById.ts
import axios157 from "axios";
var getAnnotationRunFrameById = async (baseUrl, accessToken, annotationRunId, frameId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs/${annotationRunId}/frames/${frameId}`;
    const res = await axios157.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching annotation run frame:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/getAnnotationRunFrameOutputs.ts
import axios158 from "axios";
var getAnnotationRunFrameOutputs = async (baseUrl, accessToken, annotationRunId, frameId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs/${annotationRunId}/frames/${frameId}/outputs`;
    const res = await axios158.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching annotation run frame outputs:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/getAnnotationRunFrameImage.ts
import axios159 from "axios";
var getAnnotationRunFrameImage = async (baseUrl, accessToken, annotationRunId, frameId) => {
  const headers3 = {
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/annotation-runs/${annotationRunId}/frames/${frameId}/image`;
    const res = await axios159.get(url, { headers: headers3, responseType: "blob" });
    return URL.createObjectURL(res.data);
  } catch (error) {
    console.error("Error fetching annotation run frame image:", error);
    return void 0;
  }
};

// src/middleware/annotation-run/updateAnnotationRunFrame.ts
var updateAnnotationRunFrame = async (baseUrl, accessToken, annotationRunId, frameId, payload) => {
  const config2 = {
    url: `${baseUrl}api/annotation-runs/${annotationRunId}/frames/${frameId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture/getEvalCapture.ts
var getEvalCapture = async (baseUrl, accessToken, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-captures/${encodeURIComponent(frameId)}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture/requestEvalCaptures.ts
var requestEvalCaptures = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-captures`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture/listEvalCaptures.ts
var listEvalCaptures = async (baseUrl, accessToken, query = {}) => {
  const config2 = {
    url: `${baseUrl}api/eval-captures`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params: query
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture-run/requestEvalCaptureRun.ts
var requestEvalCaptureRun = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-capture-runs`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture-run/listEvalCaptureRuns.ts
var listEvalCaptureRuns = async (baseUrl, accessToken, query = {}) => {
  const config2 = {
    url: `${baseUrl}api/eval-capture-runs`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params: query
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture-run/getEvalCaptureRun.ts
var getEvalCaptureRun = async (baseUrl, accessToken, runUuid) => {
  const config2 = {
    url: `${baseUrl}api/eval-capture-runs/${encodeURIComponent(runUuid)}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-capture-run/cancelEvalCaptureRun.ts
var cancelEvalCaptureRun = async (baseUrl, accessToken, runUuid) => {
  const config2 = {
    url: `${baseUrl}api/eval-capture-runs/${encodeURIComponent(runUuid)}/cancel`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/upsertEvalGroundTruth.ts
var upsertEvalGroundTruth = async (baseUrl, accessToken, frameId, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-ground-truth/${frameId}`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalGroundTruth.ts
var getEvalGroundTruth = async (baseUrl, accessToken, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-ground-truth/${frameId}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/upsertEvalGroundTruthPersons.ts
var upsertEvalGroundTruthPersons = async (baseUrl, accessToken, frameId, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-ground-truth-persons/${frameId}`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalGroundTruthPersons.ts
var getEvalGroundTruthPersons = async (baseUrl, accessToken, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-ground-truth-persons/${frameId}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/upsertEvalFrameLabel.ts
var upsertEvalFrameLabel = async (baseUrl, accessToken, frameId, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-frame-labels/${frameId}`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalFrameLabel.ts
var getEvalFrameLabel = async (baseUrl, accessToken, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-frame-labels/${frameId}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalFrameLabellingContext.ts
var getEvalFrameLabellingContext = async (baseUrl, accessToken, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-frame-labels/${frameId}/context`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data: data ?? null, error };
};

// src/middleware/eval-validation-set/createEvalValidationSet.ts
var createEvalValidationSet = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalValidationSets.ts
var getEvalValidationSets = async (baseUrl, accessToken) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalValidationSetById.ts
var getEvalValidationSetById = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets/${id}`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/addEvalValidationSetMembers.ts
var addEvalValidationSetMembers = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets/${id}/members`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/removeEvalValidationSetMember.ts
var removeEvalValidationSetMember = async (baseUrl, accessToken, id, frameId) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets/${id}/members/${frameId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/freezeEvalValidationSet.ts
var freezeEvalValidationSet = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets/${id}/freeze`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-validation-set/getEvalValidationSetCandidates.ts
var getEvalValidationSetCandidates = async (baseUrl, accessToken, query = {}) => {
  const config2 = {
    url: `${baseUrl}api/eval-validation-sets/candidates`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params: query
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-metrics/createEvalMetricRun.ts
var createEvalMetricRun = async (baseUrl, accessToken, validationSetId) => {
  const config2 = {
    url: `${baseUrl}api/eval-metric-runs`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: { validation_set_id: validationSetId }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-metrics/getEvalMetricRuns.ts
var getEvalMetricRuns = async (baseUrl, accessToken, validationSetId) => {
  const query = validationSetId === void 0 ? "" : `?validation_set_id=${encodeURIComponent(String(validationSetId))}`;
  const config2 = {
    url: `${baseUrl}api/eval-metric-runs${query}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-metrics/getEvalMetricRun.ts
var getEvalMetricRun = async (baseUrl, accessToken, runId) => {
  const config2 = {
    url: `${baseUrl}api/eval-metric-runs/${encodeURIComponent(String(runId))}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-usage-metrics/createEvalUsageMetricRun.ts
var createEvalUsageMetricRun = async (baseUrl, accessToken, validationSetId) => {
  const data = { validation_set_id: validationSetId };
  const config2 = {
    url: `${baseUrl}api/eval-usage-metric-runs`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data
  };
  const { data: body, error } = await callExternalApi({ config: config2 });
  return { data: body, error };
};

// src/middleware/eval-usage-metrics/getEvalUsageMetricRuns.ts
var getEvalUsageMetricRuns = async (baseUrl, accessToken, validationSetId) => {
  const query = validationSetId === void 0 ? "" : `?validation_set_id=${encodeURIComponent(String(validationSetId))}`;
  const config2 = {
    url: `${baseUrl}api/eval-usage-metric-runs${query}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-usage-metrics/getEvalUsageMetricRun.ts
var getEvalUsageMetricRun = async (baseUrl, accessToken, runId) => {
  const config2 = {
    url: `${baseUrl}api/eval-usage-metric-runs/${encodeURIComponent(String(runId))}`,
    method: "GET",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/eval-assembly/assembleEvalValidationSet.ts
var assembleEvalValidationSet = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/eval-assembly`,
    method: "POST",
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/createTrainingRun.ts
var createTrainingRun = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/training-runs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/deleteTrainingRun.ts
var deleteTrainingRun = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/training-runs/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/getTrainingRunById.ts
import axios160 from "axios";
var getTrainingRunById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/training-runs/${id}`;
    const res = await axios160.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching training run:", error);
    return void 0;
  }
};

// src/middleware/training-run/getTrainingRuns.ts
import axios161 from "axios";
var getTrainingRuns = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/training-runs`;
    const res = await axios161.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching training runs:", error);
    return void 0;
  }
};

// src/middleware/training-run/updateTrainingRun.ts
var updateTrainingRun = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/training-runs/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/getTrainingRunFrames.ts
import axios162 from "axios";
var getTrainingRunFrames = async (baseUrl, accessToken, trainingRunId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/training-runs/${trainingRunId}/frames`;
    const res = await axios162.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching training run frames:", error);
    return void 0;
  }
};

// src/middleware/training-run/createTrainingRunFrame.ts
var createTrainingRunFrame = async (baseUrl, accessToken, trainingRunId, payload) => {
  const config2 = {
    url: `${baseUrl}api/training-runs/${trainingRunId}/frames`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/deleteTrainingRunFrame.ts
var deleteTrainingRunFrame = async (baseUrl, accessToken, trainingRunId, frameId) => {
  const config2 = {
    url: `${baseUrl}api/training-runs/${trainingRunId}/frames/${frameId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/training-run/bulkCreateTrainingRunFrames.ts
var bulkCreateTrainingRunFrames = async (baseUrl, accessToken, trainingRunId, payload) => {
  const config2 = {
    url: `${baseUrl}api/training-runs/${trainingRunId}/frames/bulk`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/model-version/getModelVersions.ts
import axios163 from "axios";
var getModelVersions = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/model-versions`;
    const res = await axios163.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching model versions:", error);
    return void 0;
  }
};

// src/middleware/model-version/createModelVersion.ts
import axios164 from "axios";
var createModelVersion = async (baseUrl, accessToken, modelId, file, onUploadProgress, trainingRunId) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("model_id", String(modelId));
    if (trainingRunId != null) {
      formData.append("training_run_id", String(trainingRunId));
    }
    const response = await axios164.post(`${baseUrl}api/model-versions`, formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percent = Math.round(progressEvent.loaded * 100 / progressEvent.total);
          onUploadProgress(percent);
        }
      }
    });
    return { data: response.data, error: null };
  } catch (error) {
    if (axios164.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";
      if (response && response.statusText) {
        message = response.statusText;
      }
      if (axiosError.message) {
        message = axiosError.message;
      }
      if (response && response.data && response.data.message) {
        message = response.data.message;
      }
      return { data: null, error: { message } };
    }
    return { data: null, error: { message: error.message } };
  }
};

// src/middleware/model-version/archiveModelVersion.ts
var archiveModelVersion = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/model-versions/${id}/archive`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/getTestSets.ts
import axios165 from "axios";
var getTestSets = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-sets`;
    const res = await axios165.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test sets:", error);
    return void 0;
  }
};

// src/middleware/test-set/getTestSetById.ts
import axios166 from "axios";
var getTestSetById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-sets/${id}`;
    const res = await axios166.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test set:", error);
    return void 0;
  }
};

// src/middleware/test-set/createTestSet.ts
var createTestSet = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-sets`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/updateTestSet.ts
var updateTestSet = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-sets/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/deleteTestSet.ts
var deleteTestSet = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/test-sets/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/getTestSetFrames.ts
import axios167 from "axios";
var getTestSetFrames = async (baseUrl, accessToken, testSetId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-sets/${testSetId}/frames`;
    const res = await axios167.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test set frames:", error);
    return void 0;
  }
};

// src/middleware/test-set/getTestSetFrameById.ts
import axios168 from "axios";
var getTestSetFrameById = async (baseUrl, accessToken, testSetId, frameId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-sets/${testSetId}/frames/${frameId}`;
    const res = await axios168.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test set frame:", error);
    return void 0;
  }
};

// src/middleware/test-set/createTestSetFrame.ts
var createTestSetFrame = async (baseUrl, accessToken, testSetId, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-sets/${testSetId}/frames`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/bulkCreateTestSetFrames.ts
var bulkCreateTestSetFrames = async (baseUrl, accessToken, testSetId, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-sets/${testSetId}/frames/bulk`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-set/deleteTestSetFrame.ts
var deleteTestSetFrame = async (baseUrl, accessToken, testSetId, frameId) => {
  const config2 = {
    url: `${baseUrl}api/test-sets/${testSetId}/frames/${frameId}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-run/getTestRuns.ts
import axios169 from "axios";
var getTestRuns = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-runs`;
    const res = await axios169.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test runs:", error);
    return void 0;
  }
};

// src/middleware/test-run/getTestRunById.ts
import axios170 from "axios";
var getTestRunById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/test-runs/${id}`;
    const res = await axios170.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching test run:", error);
    return void 0;
  }
};

// src/middleware/test-run/createTestRun.ts
var createTestRun = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-runs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-run/updateTestRun.ts
var updateTestRun = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/test-runs/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/test-run/deleteTestRun.ts
var deleteTestRun = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/test-runs/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/rtsp-capture-run/getRtspCaptureRuns.ts
import axios171 from "axios";
var getRtspCaptureRuns = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/rtsp-capture-runs`;
    const res = await axios171.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching RTSP capture runs:", error);
    return void 0;
  }
};

// src/middleware/rtsp-capture-run/getRtspCaptureRunById.ts
import axios172 from "axios";
var getRtspCaptureRunById = async (baseUrl, accessToken, id) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/rtsp-capture-runs/${id}`;
    const res = await axios172.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error("Error fetching RTSP capture run:", error);
    return void 0;
  }
};

// src/middleware/rtsp-capture-run/createRtspCaptureRun.ts
var createRtspCaptureRun = async (baseUrl, accessToken, payload) => {
  const config2 = {
    url: `${baseUrl}api/rtsp-capture-runs`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/rtsp-capture-run/updateRtspCaptureRun.ts
var updateRtspCaptureRun = async (baseUrl, accessToken, id, payload) => {
  const config2 = {
    url: `${baseUrl}api/rtsp-capture-runs/${id}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: payload
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/rtsp-capture-run/deleteRtspCaptureRun.ts
var deleteRtspCaptureRun = async (baseUrl, accessToken, id) => {
  const config2 = {
    url: `${baseUrl}api/rtsp-capture-runs/${id}`,
    method: "DELETE",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/user/changePassword.ts
import axios173 from "axios";
var changePassword = async (baseUrl, accessToken, password) => {
  const routes = routes_default(baseUrl);
  const url = routes.put.users.updateProfile;
  let success = false;
  const data = {
    password
  };
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios173.put(url, data, {
      headers: headers3
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/user/createUser.ts
import axios174 from "axios";
var createUser = async (baseUrl, accessToken, organisation, firstName, lastName, email, password, role) => {
  const routes = routes_default(baseUrl);
  const url = routes.post.users.createUser;
  let success = false;
  const data = {
    organisation,
    firstname: firstName,
    lastname: lastName,
    email,
    password,
    role
  };
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios174.post(url, data, {
      headers: headers3
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/user/deleteUser.ts
import axios175 from "axios";
var deleteUser = async (baseUrl, accessToken, id) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.delete.users}${id}`;
  let success = false;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios175.delete(url, {
      headers: headers3
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/user/getUserById.ts
import axios176 from "axios";
var getUserById = async (baseUrl, accessToken, userId) => {
  const routes = routes_default(baseUrl);
  let user = null;
  const url = `${routes.get.users.getUserById}/${userId}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios176.get(url, {
      headers: headers3
    });
    user = res.data;
  } catch (error) {
  }
  return user;
};

// src/middleware/user/getUsers.ts
import axios177 from "axios";
var getUsers = async (baseUrl, accessToken, organisationAndEnvName, page, limit, searchQuery, sort, order) => {
  const routes = routes_default(baseUrl);
  let users = [];
  let querystring = `?organisation=${organisationAndEnvName}`;
  if (page > 0) {
    querystring = `${querystring}${querystring.includes(`=`) ? `&` : `?`}page=${page}`;
  }
  if (limit && limit > 0) {
    querystring = `${querystring}${querystring.includes(`=`) ? `&` : `?`}limit=${limit}`;
  }
  if (searchQuery && searchQuery.length >= 3) {
    querystring = `${querystring}${querystring.includes(`=`) ? `&` : `?`}q=${searchQuery}`;
  }
  if (sort) {
    querystring = `${querystring}${querystring.includes(`=`) ? `&` : `?`}sort=${sort}`;
  }
  if (order) {
    querystring = `${querystring}${querystring.includes(`=`) ? `&` : `?`}order=${order}`;
  }
  const url = `${routes.get.users.getUsers}${querystring}`;
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const res = await axios177.get(url, {
      headers: headers3
    });
    users = res.data;
  } catch (error) {
  }
  return users;
};

// src/middleware/user/updateProfile.ts
import axios178 from "axios";
var updateProfile = async (baseUrl, accessToken, emailAddress, firstName, lastName) => {
  const routes = routes_default(baseUrl);
  const url = routes.put.users.updateProfile;
  let success = false;
  const trimmedFirstName = firstName?.trim() ?? "";
  const trimmedLastName = lastName?.trim() ?? "";
  const data = {
    firstname: trimmedFirstName,
    email: emailAddress,
    name: [trimmedFirstName, trimmedLastName].filter(Boolean).join(" ")
  };
  if (trimmedLastName) {
    data.lastname = trimmedLastName;
  }
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const result = await axios178.put(url, data, {
      headers: headers3
    });
    return result;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/user/updateUserById.ts
import axios179 from "axios";
var updateUserById = async (baseUrl, accessToken, email, firstName, lastName, role, userId) => {
  const routes = routes_default(baseUrl);
  const url = `${routes.put.users.updateUserById}${userId}`;
  let success = false;
  const data = {
    email,
    firstname: firstName,
    lastname: lastName,
    name: `${firstName} ${lastName}`,
    roles: [role]
  };
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    await axios179.put(url, data, {
      headers: headers3
    });
    success = true;
  } catch (error) {
    success = false;
  }
  return success;
};

// src/middleware/twin-plan/getTwinPlans.ts
import axios180 from "axios";
var getTwinPlans = async (baseUrl, accessToken, twinBID, opts = {}) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-plans`;
    const res = await axios180.get(url, { headers: headers3, params: { twinBID }, signal: opts.signal });
    return res.data;
  } catch (error) {
    if (opts.signal?.aborted) return void 0;
    const e = error;
    console.error(
      "Error fetching twin plans:",
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return void 0;
  }
};

// src/middleware/twin-plan/getTwinPlanById.ts
import axios181 from "axios";
var getTwinPlanById = async (baseUrl, accessToken, planId, opts = {}) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-plans/${planId}`;
    const res = await axios181.get(url, { headers: headers3, signal: opts.signal });
    return res.data;
  } catch (error) {
    if (opts.signal?.aborted) return void 0;
    const e = error;
    console.error(
      `Error fetching twin plan ${planId}:`,
      e?.response?.status ?? "(no status)",
      e?.message ?? String(error)
    );
    return void 0;
  }
};

// src/middleware/twin-plan/createTwinPlan.ts
var createTwinPlan = async (baseUrl, accessToken, planPayload) => {
  const config2 = {
    url: `${baseUrl}api/twin-plans`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: planPayload,
    // Bounded, as every other middleware write is: `callExternalApi` sets no timeout and axios
    // defaults to none, and the wizard serialises plan saves behind one another — so a single
    // hung save would park every later Save, feed-link autosave and Go Live for the life of the
    // page (`/review-code camera-observer-setup-save-refusal`, 2026-09-12).
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-plan/updateTwinPlan.ts
var updateTwinPlan = async (baseUrl, accessToken, planId, planPayload) => {
  const config2 = {
    url: `${baseUrl}api/twin-plans/${planId}`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: planPayload,
    // Bounded, as every other middleware write is: `callExternalApi` sets no timeout and axios
    // defaults to none, and the wizard serialises plan saves behind one another — so a single
    // hung save would park every later Save, feed-link autosave and Go Live for the life of the
    // page (`/review-code camera-observer-setup-save-refusal`, 2026-09-12).
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-plan/droppedDraft.ts
import axios182 from "axios";
var REASONS2 = {
  plan_changed: true,
  launch_in_progress: true,
  production_changed: true,
  slot_other_plan: true,
  slot_changed: true,
  superseded: true,
  not_found: true,
  plan_not_found: true
};
var NOT_SENT = "not_sent";
var headers2 = (accessToken) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${accessToken}`
});
var looksLikeASwap = (data) => {
  const d = data;
  return !!d && typeof d.modifiedAt === "string" && typeof d.droppedAt === "string";
};
var swap = async (url, body, accessToken, shapeOk) => {
  try {
    const res = await axios182.post(url, body, {
      headers: headers2(accessToken),
      // The server bounds the whole request at 30 s; this sits above it so the server answers first.
      timeout: MIDDLEWARE_WRITE_TIMEOUT_MS
    });
    if (!shapeOk(res.data)) {
      return { status: "unknown", error: "Unexpected response shape from the draft API." };
    }
    return { status: "swapped", response: res.data };
  } catch (error) {
    const failure = describeFailure(error, "The draft could not be switched.");
    console.warn(
      "[draft-choice] swap failed:",
      failure.httpStatus ?? "(no status)",
      failure.reason ?? "(no reason)",
      failure.error
    );
    const { httpStatus } = failure;
    const notSent = httpStatus === 503 && failure.reason === NOT_SENT;
    if (httpStatus === void 0 || httpStatus === 502 || httpStatus === 504 || httpStatus === 503 && !notSent) {
      return {
        status: "unknown",
        error: failure.error,
        ...httpStatus !== void 0 ? { httpStatus } : {}
      };
    }
    if (failure.reason && Object.prototype.hasOwnProperty.call(REASONS2, failure.reason) && [404, 409, 410].includes(httpStatus)) {
      return {
        status: "refused",
        reason: failure.reason,
        error: failure.error,
        httpStatus
      };
    }
    return { status: "failed", error: failure.error, httpStatus };
  }
};
var getDroppedDraft = async (baseUrl, accessToken, planId) => {
  try {
    const res = await axios182.get(`${baseUrl}api/twin-plans/${planId}/dropped-draft`, {
      headers: headers2(accessToken),
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
    });
    const data = res.data;
    if (data === null || data === "" || data === void 0) {
      return { status: "found", droppedDraft: null };
    }
    if (typeof data.droppedAt !== "string") {
      return { status: "failed", error: "Unexpected response shape from the draft API." };
    }
    return { status: "found", droppedDraft: data };
  } catch (error) {
    const { reason: _reason, ...failure } = describeFailure(
      error,
      "Failed to read the dropped draft."
    );
    console.warn(
      "[draft-choice] dropped-draft read failed:",
      failure.httpStatus ?? "(no status)",
      failure.error
    );
    return { status: "failed", ...failure };
  }
};
var replaceDraft = (baseUrl, accessToken, planId, input) => swap(`${baseUrl}api/twin-plans/${planId}/replace-draft`, input, accessToken, looksLikeASwap);
var looksLikeARestore = (data) => looksLikeASwap(data) && !!data.layout && typeof data.layout === "object";
var restoreDroppedDraft = (baseUrl, accessToken, planId, input) => swap(
  `${baseUrl}api/twin-plans/${planId}/restore-dropped-draft`,
  input,
  accessToken,
  looksLikeARestore
);

// src/middleware/twin-plan/uploadPlanUnderlayImage.ts
import axios183 from "axios";
var uploadPlanUnderlayImage = async (baseUrl, accessToken, twinBid, image, signal) => {
  const form = new FormData();
  const filename = image.type === "image/png" ? "underlay.png" : "underlay.jpg";
  form.append("image", image, filename);
  try {
    const response = await axios183.post(
      `${baseUrl}api/twin-plans/underlays/${encodeURIComponent(twinBid)}`,
      form,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
        ...signal ? { signal } : {}
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (axios183.isAxiosError(error)) {
      const status = error.response?.status;
      const body = error.response?.data;
      const message = typeof body?.message === "string" && body.message ? body.message : error.message;
      return { data: null, error: { message, ...status != null ? { status } : {} } };
    }
    return { data: null, error: { message: error?.message ?? String(error) } };
  }
};

// src/middleware/twin-plan/fetchPlanUnderlayImage.ts
import axios184 from "axios";
var UNDERLAY_NOT_STORED_MESSAGE = "No stored image with that reference for this twin.";
var messageOf2 = async (body) => {
  try {
    const text2 = typeof body === "string" ? body : body && typeof body.text === "function" ? await body.text() : "";
    const parsed = JSON.parse(text2);
    return typeof parsed.message === "string" ? parsed.message : null;
  } catch {
    return null;
  }
};
var fetchPlanUnderlayImage = async (baseUrl, accessToken, twinBid, imageRef, signal) => {
  const response = await axios184.get(
    `${baseUrl}api/twin-plans/underlays/${encodeURIComponent(twinBid)}/${encodeURIComponent(imageRef)}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      responseType: "blob",
      signal,
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      validateStatus: (status) => status >= 200 && status < 300 || status === 404
    }
  );
  if (response.status !== 404) return response.data;
  const message = await messageOf2(response.data);
  if (message === UNDERLAY_NOT_STORED_MESSAGE) {
    console.warn("[planner] floor plan PDF is not in storage", { twinBid, imageRef });
    return null;
  }
  throw new Error(message ?? `The floor plan image could not be read (404) for twin ${twinBid}.`);
};

// src/middleware/twin-plan/externaliseUnderlayImages.ts
var UNDERLAY_SAVE_MESSAGES = {
  failed: "The floor plan PDF could not be stored, so the plan was not saved. Your changes are still here \u2014 save again to retry.",
  unsupported: "A floor plan PDF image is not a JPEG or PNG, so the plan was not saved. Remove that PDF and import it again.",
  tooLarge: "A floor plan PDF image is larger than 10 MB, so the plan was not saved. Import it again at a smaller size.",
  twinNotFound: "This twin could not be found, so its floor plan PDF could not be stored and the plan was not saved.",
  forbidden: "You do not have permission to store floor plan images, so the plan was not saved.",
  twinUnknown: "This plan's twin is not known yet, so its floor plan PDF could not be stored and the plan was not saved. Select the plan's twin and save again."
};
var UNDERLAY_IMPORT_MESSAGES = {
  failed: "The PDF could not be stored, so it was not added. Try again.",
  unsupported: "This PDF page could not be stored as an image. Choose another file.",
  tooLarge: "This PDF page is larger than 10 MB once rendered. Crop it to a smaller area and try again.",
  twinNotFound: "This twin could not be found, so the PDF could not be stored.",
  forbidden: "You do not have permission to store floor plan images."
};
var sentenceForStatus = (status, sentences) => {
  switch (status) {
    case 415:
      return sentences.unsupported;
    case 413:
      return sentences.tooLarge;
    case 404:
      return sentences.twinNotFound;
    case 401:
    case 403:
      return sentences.forbidden;
    default:
      return sentences.failed;
  }
};
function dataUrlToBlob(data) {
  const match = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(data);
  const declared = match?.[1];
  const base64 = (match ? match[3] : data).replace(/\s+/g, "");
  let binary;
  try {
    binary = atob(base64);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const isPng = bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71;
  const type = declared ?? (isPng ? "image/png" : "image/jpeg");
  return new Blob([bytes], { type });
}
async function externaliseUnderlayImages(baseUrl, accessToken, twinBid, layout, deps = { upload: uploadPlanUnderlayImage }) {
  const uploadsByData = /* @__PURE__ */ new Map();
  const stored = [];
  const floors = [];
  for (const floor of layout.floors ?? []) {
    const designs = [];
    for (const design of floor.designs ?? []) {
      if (!design.underlays?.length) {
        designs.push(design);
        continue;
      }
      const underlays = [];
      for (const u of design.underlays) {
        let imageRef = u.imageRef;
        if (!imageRef && u.data) {
          let upload = uploadsByData.get(u.data);
          if (!upload) {
            if (!twinBid) return { ok: false, message: UNDERLAY_SAVE_MESSAGES.twinUnknown };
            const blob = dataUrlToBlob(u.data);
            if (!blob) return { ok: false, message: UNDERLAY_SAVE_MESSAGES.unsupported };
            const result = await deps.upload(baseUrl, accessToken, twinBid, blob);
            if (result.error || !result.data) {
              return {
                ok: false,
                message: sentenceForStatus(result.error?.status, UNDERLAY_SAVE_MESSAGES)
              };
            }
            upload = { imageRef: result.data.imageRef, blob };
            uploadsByData.set(u.data, upload);
          }
          imageRef = upload.imageRef;
          stored.push({ underlayId: u.id, data: u.data, imageRef, blob: upload.blob });
        }
        if (!imageRef) {
          underlays.push(u);
          continue;
        }
        const { data: _inline, ...rest } = u;
        underlays.push({ ...rest, imageRef });
      }
      designs.push({ ...design, underlays });
    }
    floors.push({ ...floor, designs });
  }
  return { ok: true, layout: { ...layout, floors }, stored };
}

// src/middleware/twin-plan/underlayImageCache.ts
function createUnderlayImages(load, storer) {
  const slots = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  const notify = () => listeners.forEach((listener) => listener());
  const set = (imageRef, slot) => {
    slots.set(imageRef, slot);
    notify();
  };
  const start = (imageRef) => {
    const controller = new AbortController();
    set(imageRef, { status: "loading", controller });
    load(imageRef, controller.signal).then(
      (blob) => {
        if (slots.get(imageRef)?.controller !== controller) return;
        set(
          imageRef,
          blob ? { status: "ready", url: URL.createObjectURL(blob) } : { status: "missing" }
        );
      },
      (error) => {
        if (slots.get(imageRef)?.controller !== controller) return;
        console.warn("[planner] floor plan PDF could not be loaded", {
          imageRef,
          message: error?.message ?? String(error)
        });
        set(imageRef, { status: "failed" });
      }
    );
  };
  const release = (slot) => {
    slot?.controller?.abort();
    if (slot?.url) URL.revokeObjectURL(slot.url);
  };
  const seed = (imageRef, blob) => {
    release(slots.get(imageRef));
    set(imageRef, { status: "ready", url: URL.createObjectURL(blob) });
  };
  return {
    canStore: !!storer,
    store: async (blob, signal) => {
      if (!storer) return { ok: false, message: UNDERLAY_IMPORT_MESSAGES.failed };
      let result;
      try {
        result = await storer(blob, signal);
      } catch (err) {
        console.warn("[planner] the floor plan PDF could not be stored", err);
        return { ok: false, message: UNDERLAY_IMPORT_MESSAGES.failed };
      }
      if (result.error || !result.data) {
        return {
          ok: false,
          message: sentenceForStatus(result.error?.status, UNDERLAY_IMPORT_MESSAGES)
        };
      }
      try {
        seed(result.data.imageRef, blob);
      } catch (err) {
        console.warn("[planner] could not draw the stored floor plan image from memory", err);
      }
      return { ok: true, imageRef: result.data.imageRef };
    },
    peek: (imageRef) => slots.get(imageRef),
    ensure: (imageRef) => {
      if (!slots.has(imageRef)) start(imageRef);
    },
    retry: (imageRef) => {
      if (slots.get(imageRef)?.status !== "failed") return;
      start(imageRef);
    },
    seed,
    reportUndecodable: (imageRef) => {
      const slot = slots.get(imageRef);
      if (slot?.status !== "ready") return;
      console.warn("[planner] a stored floor plan PDF image could not be decoded", { imageRef });
      release(slot);
      set(imageRef, { status: "failed" });
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose: () => {
      slots.forEach(release);
      slots.clear();
      notify();
    }
  };
}
function adoptStoredUnderlays(stored, attach, images) {
  if (stored.length === 0) return;
  attach(stored);
  try {
    stored.forEach(({ imageRef, blob }) => images.seed(imageRef, blob));
  } catch (err) {
    console.warn(
      "[planner] could not draw the stored floor plan image from memory; it will be fetched.",
      err
    );
  }
}

// src/middleware/twin-plan/activatePlan.ts
import { extractBIDs as extractBIDs2, getVersionSuffix, prepareModelVersion } from "@repo/front-end-utils";
var activatePlan = async ({
  baseUrl,
  accessToken,
  orgBID,
  tree,
  prevModel,
  sensorInputs,
  deletableSensorModelNames
}) => {
  if (!accessToken) {
    return { success: false, error: "Invalid access token" };
  }
  if (!orgBID) {
    return { success: false, error: "Missing organisation bID" };
  }
  if (!tree?.bID) {
    return { success: false, error: "Missing twin bID on plan model" };
  }
  const updating = !!prevModel;
  const result = { success: false };
  tree.organisation = prevModel?.organisation ?? tree.organisation;
  if (prevModel?.mapOrigin) {
    tree.mapOrigin = {
      latitude: prevModel.mapOrigin.latitude,
      longitude: prevModel.mapOrigin.longitude
    };
  }
  if (typeof prevModel?.rotationY === "number") {
    tree.rotationY = prevModel.rotationY;
  }
  if (updating && prevModel && sensorInputs && sensorInputs.length > 0) {
    try {
      const prevModelBIDs = extractBIDs2(prevModel);
      const backup = await versionAndDeleteSensors(baseUrl, accessToken, {
        orgBid: orgBID,
        entityBIDs: prevModelBIDs,
        deletableModelNames: deletableSensorModelNames ?? ["PeopleCountLive"]
      });
      result.sensorBackup = {
        versionedCount: backup.versionedCount,
        versionSuffix: backup.versionSuffix,
        success: backup.success,
        error: backup.error
      };
    } catch (error) {
      result.sensorBackup = {
        versionedCount: 0,
        success: false,
        error: error instanceof Error ? error.message : "Unknown error backing up sensors"
      };
    }
  }
  if (updating && prevModel) {
    try {
      const versionSuffix = getVersionSuffix();
      const modelVersion = prepareModelVersion(prevModel, versionSuffix);
      const backupResult = await dgqInsertTwin(baseUrl, accessToken, orgBID, modelVersion);
      if (!backupResult.success) {
        return {
          ...result,
          success: false,
          error: backupResult.error || "Failed to create backup version of previous model"
        };
      }
      result.modelVersionSuffix = versionSuffix;
    } catch (error) {
      return {
        ...result,
        success: false,
        error: error instanceof Error ? error.message : "Failed to create backup version of previous model"
      };
    }
  }
  const deleteResult = await dgqDeleteTwinEntity(
    baseUrl,
    accessToken,
    orgBID,
    tree.bID,
    true
    // delete the entire old model/tree
  );
  if (!deleteResult.success) {
    return {
      ...result,
      success: false,
      error: deleteResult.error || "Failed to delete existing twin from Dgraph"
    };
  }
  const insertResult = await dgqInsertTwin(baseUrl, accessToken, orgBID, tree);
  if (!insertResult.success) {
    return {
      ...result,
      success: false,
      error: insertResult.error || "Failed to insert new twin into Dgraph"
    };
  }
  if (sensorInputs && sensorInputs.length > 0) {
    try {
      const sync = await upsertSensors(baseUrl, accessToken, {
        orgBid: orgBID,
        sensors: sensorInputs
      });
      result.sensorSync = {
        insertedCount: sync.insertedCount,
        updatedCount: sync.updatedCount,
        success: sync.success,
        error: sync.error
      };
    } catch (error) {
      result.sensorSync = {
        insertedCount: 0,
        updatedCount: 0,
        success: false,
        error: error instanceof Error ? error.message : "Unknown error syncing sensors"
      };
    }
  }
  result.success = true;
  return result;
};

// src/middleware/twin-asset/writeTransport.ts
import { MAX_SERVER_MESSAGE_LENGTH, normaliseServerMessage as normaliseServerMessage2 } from "@repo/front-end-utils";
var OFFLINE_MESSAGE = "You appear to be offline. Check your connection and try again.";
var TIMED_OUT_MESSAGE = "The request timed out. Please try again.";
var REPLY_UNREADABLE = "ReplyUnreadableError";
var isOutcomeUnconfirmed = (error) => {
  if (!(error instanceof Error)) return false;
  return error.name === "TimeoutError" || error.name === "AbortError" || error.name === REPLY_UNREADABLE;
};
var GATEWAY_STATUS = /\((502|503|504)\)$/;
var isWriteOutcomeUnknown = (error) => {
  if (isOutcomeUnconfirmed(error)) return true;
  if (error instanceof TypeError) return true;
  if (!(error instanceof Error)) return false;
  const status = error.status;
  if (status === 502 || status === 503 || status === 504) return true;
  return error.message === OFFLINE_MESSAGE || GATEWAY_STATUS.test(error.message);
};
var TIMED_OUT_CREATE_MESSAGE = "The request timed out. The asset may already have been added \u2014 check the register before adding it again.";
var forLog = (body) => body.length > MAX_SERVER_MESSAGE_LENGTH ? `${body.slice(0, MAX_SERVER_MESSAGE_LENGTH)}\u2026` : body;
var extractServerDetail = (body) => {
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { message: null, code: null };
  }
  if (typeof parsed !== "object" || parsed === null) return { message: null, code: null };
  const rawCode = parsed.code;
  const code = typeof rawCode === "string" && rawCode !== "" ? rawCode : null;
  const message = normaliseServerMessage2(parsed.message, "");
  return { message: message === "" ? null : message, code };
};
var readErrorDetail = async (response, fallback) => {
  let body = "";
  try {
    body = await response.text();
  } catch {
    body = "";
  }
  const { message, code } = extractServerDetail(body);
  if (message) return { message, code };
  console.error("Asset Register write failed with no usable server message:", {
    status: response.status,
    url: response.url,
    body: forLog(body)
  });
  return { message: `${fallback} (${response.status})`, code };
};
var asTransportError = (error) => {
  if (error instanceof TypeError) {
    console.error("Asset Register write never reached the middleware:", error);
    return new Error(OFFLINE_MESSAGE);
  }
  return error;
};
var readBodyText = async (response, failureFallback, signal) => {
  try {
    return await response.text();
  } catch (bodyFailure) {
    if (signal.aborted) throw signal.reason ?? bodyFailure;
    console.error("Asset Register write: the response body could not be read:", {
      status: response.status,
      url: response.url,
      cause: bodyFailure
    });
    throw Object.assign(new Error(`${failureFallback} (the server's reply was cut short)`), {
      name: REPLY_UNREADABLE
    });
  }
};
var boundedWrite = async (url, init, options) => {
  const {
    failureFallback,
    timedOutMessage = TIMED_OUT_MESSAGE,
    timeoutMs: requestedTimeoutMs = MIDDLEWARE_REQUEST_TIMEOUT_MS
  } = options;
  const timeoutMs = Number.isFinite(requestedTimeoutMs) && requestedTimeoutMs > 0 ? requestedTimeoutMs : MIDDLEWARE_REQUEST_TIMEOUT_MS;
  if (timeoutMs !== requestedTimeoutMs) {
    console.error("Asset Register write given an unusable timeout; using the repo cap:", {
      url,
      method: init.method,
      requestedTimeoutMs
    });
  }
  const settle = async (response, inForce) => {
    if (!response.ok) {
      const { message, code } = await readErrorDetail(response, failureFallback);
      if (inForce.aborted) throw inForce.reason ?? new Error(message);
      throw Object.assign(new Error(message), {
        status: response.status,
        // The server's machine code, where it sent one (`equipment-page-identify`, R14).
        ...code !== null ? { code } : {}
      });
    }
    return readBodyText(response, failureFallback, inForce);
  };
  const abort = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    abort.abort(Object.assign(new Error(timedOutMessage), { name: "TimeoutError" }));
  }, timeoutMs);
  try {
    return await settle(await fetch(url, { ...init, signal: abort.signal }), abort.signal);
  } catch (error) {
    if (timedOut) {
      console.error("Asset Register write timed out:", {
        url,
        method: init.method,
        timeoutMs
      });
      throw Object.assign(new Error(timedOutMessage), { name: "TimeoutError" });
    }
    throw asTransportError(error);
  } finally {
    clearTimeout(timer);
  }
};
var writeJson = async (url, init, options) => {
  const body = await boundedWrite(url, init, options);
  try {
    return JSON.parse(body);
  } catch (parseFailure) {
    console.error("Asset Register write answered with a body that is not JSON:", {
      url,
      method: init.method,
      ...options.logSuccessBody === false ? { bodyLength: body.length } : { body: forLog(body) },
      cause: parseFailure
    });
    throw Object.assign(
      new Error(`${options.failureFallback} (the server's reply could not be read)`),
      { name: REPLY_UNREADABLE }
    );
  }
};
var writeNoContent = async (url, init, options) => {
  await boundedWrite(url, init, options);
};

// src/middleware/twin-asset/bulkUploadTwinAssets.ts
var ASSET_CSV_REJECTION_RULES = ["bid", "date"];
var buildRegisterUploadForm = (file, options) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("twinId", String(options.twinId));
  if (options.sessionId !== void 0) {
    formData.append("sessionId", options.sessionId);
  }
  if (options.mode === "adopt") {
    formData.append("mode", "adopt");
  }
  return formData;
};
var bulkUploadTwinAssets = async (baseUrl, accessToken, file, options) => {
  const formData = buildRegisterUploadForm(file, options);
  if (options.leftOut && options.leftOut.length > 0) {
    formData.append("leftOut", JSON.stringify(options.leftOut));
  }
  let response;
  try {
    response = await fetch(`${baseUrl}api/twin-assets/bulk-upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      body: formData
    });
  } catch (error) {
    throw asTransportError(error);
  }
  if (!response.ok) {
    const { message, code } = await readErrorDetail(response, "Failed to upload the assets CSV");
    throw Object.assign(new Error(message), {
      status: response.status,
      ...code !== null ? { code } : {}
    });
  }
  return response.json();
};
var BI_UNREADABLE_CODE = "bi-unreadable";
var isBiUnreadableError = (error) => typeof error === "object" && error !== null && error.code === BI_UNREADABLE_CODE;

// src/middleware/twin-asset/checkTwinAssetRegister.ts
var FAILURE = "The file could not be checked";
var isEntry = (entry) => typeof entry === "object" && entry !== null && typeof entry.line === "number";
var checkTwinAssetRegister = async (baseUrl, accessToken, file, options) => {
  const body = await writeJson(
    `${baseUrl}api/twin-assets/bulk-upload/check`,
    {
      method: "POST",
      // No Content-Type — the browser owns the multipart boundary.
      headers: { Authorization: `Bearer ${accessToken}` },
      body: buildRegisterUploadForm(file, options)
    },
    // The answer names rows, BIDs and cell values, so a malformed one is never logged whole (NFR3).
    { failureFallback: FAILURE, timeoutMs: options.timeoutMs, logSuccessBody: false }
  );
  if (!body || !Array.isArray(body.rejected) || !Array.isArray(body.rowsWithoutBid) || typeof body.rowsWithIdentifier !== "number" || // Every ENTRY too: a `null` in `rejected` would pass the array test and then throw while the
  // Equipment step renders, taking the step down instead of offering *Check again* (failure
  // review).
  !body.rejected.every(isEntry) || !body.rowsWithoutBid.every(isEntry)) {
    console.error("Register check answered 200 without its envelope:", {
      keys: body && typeof body === "object" ? Object.keys(body) : typeof body
    });
    throw Object.assign(new Error(`${FAILURE} (the server's reply could not be read)`), {
      name: REPLY_UNREADABLE
    });
  }
  return body;
};

// src/middleware/twin-asset/downloadAssetCsvTemplate.ts
var ASSET_CSV_TEMPLATE_FILENAME = "twinlabs-asset-register-template.csv";
var ASSET_CSV_ADOPTION_TEMPLATE_FILENAME = "twinlabs-adoption-template.csv";
var templateQuery = ({ mode, twinId }) => {
  if (mode !== "adopt") return "";
  const params = new URLSearchParams({ mode: "adopt" });
  if (twinId !== void 0 && Number.isInteger(twinId) && twinId > 0) {
    params.set("twinId", String(twinId));
  }
  return `?${params.toString()}`;
};
var EMPTY_TEMPLATE_MESSAGE = "The template came back empty. Please try again, and tell us if it keeps happening.";
var BODY_UNREADABLE_MESSAGE = "The template download was interrupted part-way. Please try again.";
var downloadAssetCsvTemplate = async (baseUrl, accessToken, requestedTimeoutMs = MIDDLEWARE_REQUEST_TIMEOUT_MS, options = {}) => {
  const timeoutMs = Number.isFinite(requestedTimeoutMs) && requestedTimeoutMs > 0 ? requestedTimeoutMs : MIDDLEWARE_REQUEST_TIMEOUT_MS;
  if (timeoutMs !== requestedTimeoutMs) {
    console.error("CSV template download given an unusable timeout; using the repo cap:", {
      requestedTimeoutMs
    });
  }
  const controller = new AbortController();
  let timedOut = false;
  let readingBody = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch(
      `${baseUrl}api/twin-assets/csv-template${templateQuery(options)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal
      }
    );
    if (!response.ok) {
      console.error("CSV template refused:", {
        status: response.status,
        url: `${baseUrl}api/twin-assets/csv-template`
      });
      throw new Error(`Failed to download the CSV template: ${response.status}`);
    }
    readingBody = true;
    const blob = await response.blob();
    if (blob.size === 0) {
      console.error("CSV template came back empty:", {
        url: `${baseUrl}api/twin-assets/csv-template`
      });
      throw new Error(EMPTY_TEMPLATE_MESSAGE);
    }
    return blob;
  } catch (error) {
    if (timedOut) {
      console.error("CSV template request exceeded its bound:", { timeoutMs });
      throw Object.assign(new Error(TIMED_OUT_MESSAGE), { name: "TimeoutError" });
    }
    if (readingBody && error instanceof TypeError) {
      console.error("CSV template body could not be read:", {
        url: `${baseUrl}api/twin-assets/csv-template`
      });
      throw new Error(BODY_UNREADABLE_MESSAGE);
    }
    throw asTransportError(error);
  } finally {
    clearTimeout(timer);
  }
};

// src/middleware/twin-asset/getTwinAssets.ts
import axios185 from "axios";
var getTwinAssetsOrThrow = async (baseUrl, accessToken, filter, signal) => {
  const params = { twinId: String(filter.twinId) };
  const url = `${baseUrl}api/twin-assets`;
  const res = await axios185.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    params,
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal
  });
  if (!Array.isArray(res.data)) {
    throw new Error("twin-assets: expected an array of register rows");
  }
  return res.data;
};

// src/middleware/twin-asset/discardTwinAssets.ts
var DISCARD_MAX_IDS = 500;
var discardTwinAssets = async (baseUrl, accessToken, { twinId, assetIds }) => writeJson(
  `${baseUrl}api/twin-assets/discard`,
  {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ twinId, assetIds })
  },
  // The transport's own timeout sentence: whether the register is then re-read is the
  // caller's business, so this helper claims nothing about it.
  { failureFallback: "The rows could not be removed" }
);

// src/middleware/twin-asset/importRecord.ts
import axios186 from "axios";

// src/middleware/twin-asset/writeTwinAsset.ts
var isProductChangedError = (error) => {
  if (typeof error !== "object" || error === null) return false;
  const { status, code } = error;
  return status === 409 && code === "changed";
};
var isAlreadyIdentifiedError = (error) => {
  if (typeof error !== "object" || error === null) return false;
  const { status, code } = error;
  return status === 409 && code === "already-identified";
};
var jsonHeaders = (accessToken) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${accessToken}`
});
var toCreateBody = (payload) => {
  const { entityCode, modelFile, ...rest } = payload;
  return {
    ...rest,
    ...entityCode !== void 0 ? { entity_code: entityCode } : {},
    ...modelFile !== void 0 ? { model_3d: modelFile } : {}
  };
};
var createTwinAsset = async (baseUrl, accessToken, payload, timeoutMs, idempotencyKey) => {
  return writeJson(
    `${baseUrl}api/twin-assets`,
    {
      method: "POST",
      headers: {
        ...jsonHeaders(accessToken),
        /*
         * `Idempotency-Key`, when the caller has one (`register-create-hardening`, epic ledger
         * row 39).
         *
         * Spread conditionally rather than sent as an empty string: the server treats a blank key
         * as no key, but a header key that is present-and-empty is the kind of thing a proxy
         * normalises differently, and there is nothing to gain from sending it.
         *
         * ⚠ **The middleware must ship BEFORE a client that sends this.** An earlier version of this
         * comment said the header was ignored by an old server and so cost nothing — true of a
         * server-to-server call, and wrong for a browser. `Idempotency-Key` is not CORS-safelisted
         * and the middleware sets an EXPLICIT `allowedHeaders` list, so an old middleware answers
         * the preflight without it and `fetch` never issues the POST at all. The reverse direction
         * is safe (an old client simply sends no key). See the spec's NFR3.
         */
        ...idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}
      },
      body: JSON.stringify(toCreateBody(payload))
    },
    {
      failureFallback: "Failed to add the asset",
      timeoutMs,
      // A create, so the timeout must not claim nothing happened — see TIMED_OUT_CREATE_MESSAGE.
      timedOutMessage: TIMED_OUT_CREATE_MESSAGE
    }
  );
};
var updateTwinAsset = async (baseUrl, accessToken, assetId, fields, timeoutMs) => {
  return writeJson(
    `${baseUrl}api/twin-assets/${assetId}`,
    {
      method: "PATCH",
      headers: jsonHeaders(accessToken),
      body: JSON.stringify(fields)
    },
    { failureFallback: "Failed to update the asset", timeoutMs }
  );
};
var deleteTwinAsset = async (baseUrl, accessToken, assetId) => {
  await writeNoContent(
    `${baseUrl}api/twin-assets/${assetId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` }
    },
    { failureFallback: "Failed to remove the asset" }
  );
};

// src/middleware/twin-asset/importRecord.ts
var importUrl = (baseUrl, importId, tail = "") => `${baseUrl}api/twin-assets/imports/${encodeURIComponent(String(importId))}${tail}`;
var listTwinAssetImports = async (baseUrl, accessToken, twinId, signal) => {
  const res = await axios186.get(`${baseUrl}api/twin-assets/imports`, {
    params: { twinId },
    headers: jsonHeaders(accessToken),
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal
  });
  if (!Array.isArray(res.data)) throw new Error("imports: expected a list");
  return res.data;
};
var getTwinAssetImport = async (baseUrl, accessToken, { twinId, importId }, signal) => {
  const res = await axios186.get(importUrl(baseUrl, importId), {
    params: { twinId },
    headers: jsonHeaders(accessToken),
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal
  });
  if (typeof res.data?.id !== "number") throw new Error("import: expected a record");
  return res.data;
};
var getTwinAssetImportUndoPreview = async (baseUrl, accessToken, { twinId, importId }, signal) => {
  const res = await axios186.get(
    importUrl(baseUrl, importId, "/undo-preview"),
    {
      params: { twinId },
      headers: jsonHeaders(accessToken),
      timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
      signal
    }
  );
  if (!Array.isArray(res.data?.added) || !Array.isArray(res.data?.restored)) {
    throw new Error("undo-preview: expected { added: [...], restored: [...] }");
  }
  return res.data;
};
var undoTwinAssetImport = async (baseUrl, accessToken, { twinId, importId }) => writeJson(
  importUrl(baseUrl, importId, "/undo"),
  {
    method: "POST",
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ twinId })
  },
  { failureFallback: "The import could not be undone" }
);
var keepAbsentImportRows = async (baseUrl, accessToken, { twinId, importId }, body) => writeJson(
  importUrl(baseUrl, importId, "/absent/keep"),
  {
    method: "POST",
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ twinId, ...body })
  },
  { failureFallback: "The rows could not be kept" }
);

// src/middleware/twin-asset/identifyTwinAssets.ts
var IDENTIFY_MAX_IDS = 500;
var IdentifyUndoUnavailableError = class extends Error {
  constructor() {
    super("This identify cannot be undone here. Nothing was changed.");
    this.name = "IdentifyUndoUnavailableError";
  }
};
var canUndoIdentify = (result) => typeof result.undoToken === "string" && result.undoToken !== "" && result.identified.length > 0;
var headersFor = (accessToken, idempotencyKey) => ({
  ...jsonHeaders(accessToken),
  // Only when the caller has one — `createTwinAsset`'s rule; the middleware's CORS lists it.
  ...idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}
});
var identifyTwinAssets = async (baseUrl, accessToken, {
  twinId,
  referenceId,
  assetIds
}, idempotencyKey) => writeJson(
  `${baseUrl}api/twin-assets/identify`,
  {
    method: "POST",
    headers: headersFor(accessToken, idempotencyKey),
    body: JSON.stringify({ twinId, referenceId, assetIds })
  },
  { failureFallback: "The rows could not be identified" }
);
var undoIdentifyTwinAssets = async (baseUrl, accessToken, twinId, result, idempotencyKey) => {
  if (!canUndoIdentify(result)) throw new IdentifyUndoUnavailableError();
  return writeJson(
    `${baseUrl}api/twin-assets/identify/undo`,
    {
      method: "POST",
      headers: headersFor(accessToken, idempotencyKey),
      // Built from the identify answer, verbatim: the server refuses anything else.
      body: JSON.stringify({
        twinId,
        referenceId: result.product.referenceId,
        identifiedAt: result.identifiedAt,
        rows: result.identified.map(({ asset, previous }) => ({ id: asset.id, previous })),
        undoToken: result.undoToken
      })
    },
    { failureFallback: "The identify could not be undone" }
  );
};

// src/middleware/twin-asset/brandMix.ts
import axios187 from "axios";
var refreshTwinAssetProposals = async (baseUrl, accessToken, { twinId, dryRun }) => writeJson(
  `${baseUrl}api/twin-assets/proposals/refresh`,
  {
    method: "POST",
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(dryRun === void 0 ? { twinId } : { twinId, dryRun })
  },
  {
    failureFallback: "The guesses could not be refreshed",
    // A backstop ABOVE the server's own 30 s deadline, so its `incomplete` answer always arrives
    // before this fires (Copilot review, PR #2046) — see `MIDDLEWARE_WRITE_TIMEOUT_MS`.
    timeoutMs: MIDDLEWARE_WRITE_TIMEOUT_MS
  }
);
var getTwinBrandMix = async (baseUrl, accessToken, twinId, signal) => {
  const res = await axios187.get(`${baseUrl}api/twin-assets/brand-mix`, {
    params: { twinId },
    headers: jsonHeaders(accessToken),
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal
  });
  if (!Array.isArray(res.data?.twin?.brands) || !Array.isArray(res.data?.otherTwins?.brands)) {
    throw new Error("brand-mix: expected { twin: { brands }, otherTwins: { brands } }");
  }
  return res.data;
};
var getRankedCandidates = async (baseUrl, accessToken, assetId, signal, manufacturer) => {
  const url = `${baseUrl}api/twin-assets/${encodeURIComponent(String(assetId))}/ranked-candidates`;
  const res = await axios187.get(url, {
    ...manufacturer ? { params: { manufacturer } } : {},
    headers: jsonHeaders(accessToken),
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
    signal
  });
  if (!Array.isArray(res.data?.yours) || !Array.isArray(res.data?.otherTwins)) {
    throw new Error("ranked-candidates: expected { yours: [...], otherTwins: [...] }");
  }
  return res.data;
};

// src/middleware/twin-asset/boundedToken.ts
var TOKEN_TIMED_OUT_MESSAGE = "Signing in took too long, so nothing was sent. Please try again.";
var getTokenBounded = async (getAccessToken, timeoutMs = MIDDLEWARE_REQUEST_TIMEOUT_MS) => {
  let timer;
  try {
    return await Promise.race([
      getAccessToken(),
      new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
          console.error("Auth0 token fetch exceeded the write bound:", { timeoutMs });
          reject(Object.assign(new Error(TOKEN_TIMED_OUT_MESSAGE), { name: "TimeoutError" }));
        }, timeoutMs);
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// src/middleware/indicator-ramp/getIndicatorRampSets.ts
import axios188 from "axios";
var getIndicatorRampSets = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/indicator-ramps`;
    const res = await axios188.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching indicator ramp sets:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/indicator-ramp/getIndicatorRampSet.ts
import axios189 from "axios";
var getIndicatorRampSet = async (baseUrl, accessToken, metricId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/indicator-ramps/${metricId}`;
    const res = await axios189.get(url, { headers: headers3 });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching indicator ramp set:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/indicator-ramp/putIndicatorRampSet.ts
var putIndicatorRampSet = async (baseUrl, accessToken, metricId, rampSetPayload) => {
  const config2 = {
    url: `${baseUrl}api/indicator-ramps/${metricId}`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: rampSetPayload
  };
  const { data, error } = await callExternalApi({
    config: config2
  });
  return {
    data,
    error
  };
};

// src/middleware/twin-template/getTwinTemplates.ts
import axios190 from "axios";
var getTwinTemplates = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates`;
    const res = await axios190.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching twin templates:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/twin-template/getOfferedTwinTypes.ts
import axios191 from "axios";
var getOfferedTwinTypes = async (baseUrl, accessToken) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates/offered`;
    const res = await axios191.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching offered twin types:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/twin-template/getTwinTemplateAttachment.ts
import axios192 from "axios";
var getTwinTemplateAttachment = async (baseUrl, accessToken, templateId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates/${templateId}/attachment`;
    const res = await axios192.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching twin template attachment:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/twin-template/putTwinTemplateAttachment.ts
var putTwinTemplateAttachment = async (baseUrl, accessToken, templateId, attachment) => {
  const config2 = {
    url: `${baseUrl}api/twin-templates/${templateId}/attachment`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    data: attachment,
    // Bounded per call site. `callExternalApi` itself is deliberately NOT modified — it has 111
    // call sites across the repo, so bounding it there is a repo-wide behaviour change and a
    // second species (charter section 3).
    timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS
  };
  const { data, error } = await callExternalApi({ config: config2 });
  return { data, error };
};

// src/middleware/twin-template/getTwinTemplateTwinCapabilities.ts
import axios193 from "axios";
var getTwinTemplateTwinCapabilities = async (baseUrl, accessToken, templateId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates/${templateId}/twin-capabilities`;
    const res = await axios193.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching twin template twin capabilities:",
      error?.response?.status,
      error?.response?.data?.message ?? error?.message
    );
    return void 0;
  }
};

// src/middleware/twin-template/getTwinTemplateLabels.ts
import axios194 from "axios";

// src/middleware/twin-template/twinTemplateLabelError.ts
import { normaliseServerMessage as normaliseServerMessage3 } from "@repo/front-end-utils";
var TwinTemplateLabelError = class _TwinTemplateLabelError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "TwinTemplateLabelError";
    this.status = status;
    Object.setPrototypeOf(this, _TwinTemplateLabelError.prototype);
  }
};
var LABELS_UNAVAILABLE_MESSAGE = "Label configuration is not available in this environment.";

// src/middleware/twin-template/getTwinTemplateLabels.ts
var getTwinTemplateLabels = async (baseUrl, accessToken, templateId) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates/${templateId}/labels`;
    const res = await axios194.get(url, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    const like = error;
    const status = like?.response?.status ?? null;
    const message = normaliseServerMessage3(
      like?.response?.data?.message ?? like?.message,
      "Could not load the label configuration."
    );
    console.error("Error fetching twin template labels:", status, message);
    throw new TwinTemplateLabelError(message, status);
  }
};

// src/middleware/twin-template/putTwinTemplateLabels.ts
import axios195 from "axios";
var putTwinTemplateLabels = async (baseUrl, accessToken, templateId, labels) => {
  const headers3 = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const url = `${baseUrl}api/twin-templates/${templateId}/labels`;
    const res = await axios195.put(url, labels, { headers: headers3, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS });
    return res.data;
  } catch (error) {
    const like = error;
    const status = like?.response?.status ?? null;
    const message = normaliseServerMessage3(
      like?.response?.data?.message ?? like?.message,
      "Could not save the label configuration."
    );
    console.error("Error saving twin template labels:", status, message);
    throw new TwinTemplateLabelError(message, status);
  }
};
export {
  ASSET_CSV_ADOPTION_TEMPLATE_FILENAME,
  ASSET_CSV_REJECTION_RULES,
  ASSET_CSV_TEMPLATE_FILENAME,
  AVG_SPARE,
  BASELINE_MEASURE_TOKENS,
  BATCH_PROJECTORS,
  BI_UNREADABLE_CODE,
  CAPACITY,
  CATALOGUE_FILTER_KEYS,
  CaptureFramesPurpose,
  DEFAULT_DEVICE_STATUS_THRESHOLDS,
  DISCARD_MAX_IDS,
  EnsureLatestStreamFrameStatus,
  HEAT_GRID_READ_REASONS,
  IDENTIFY_MAX_IDS,
  IdentifyUndoUnavailableError,
  LABELS_UNAVAILABLE_MESSAGE,
  LiveTwinModelsError,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  MIDDLEWARE_WRITE_TIMEOUT_MS,
  OBSERVER_PROPOSALS_READ_REASONS,
  OFFLINE_MESSAGE,
  REPLY_UNREADABLE,
  REQUEST_ID_HEADER,
  SCALED_ABSOLUTE_PEAK_HEADCOUNT,
  SCALED_AVG_HEADCOUNT,
  SCALED_TYPICAL_PEAK_HEADCOUNT,
  SPARE_AT_TYPICAL_PEAK,
  TIMED_OUT_MESSAGE,
  TOKEN_TIMED_OUT_MESSAGE,
  TRACKED_CAPACITY,
  TwinTemplateLabelError,
  UNDERLAY_IMPORT_MESSAGES,
  UNDERLAY_NOT_STORED_MESSAGE,
  UNDERLAY_SAVE_MESSAGES,
  acceptTerms,
  activateNolaConfig,
  activatePlan,
  addEvalValidationSetMembers,
  addRTSPEndpoint,
  addThresholdAlertRuleset,
  adoptStoredUnderlays,
  applyStreamBalancePlan,
  archiveModelVersion,
  assembleEvalValidationSet,
  beginLoad,
  bulkCreateServices,
  bulkCreateTestSetFrames,
  bulkCreateTrainingRunFrames,
  bulkUploadTwinAssets,
  callExternalApi,
  canUndoIdentify,
  cancelEvalCaptureRun,
  captureFrame,
  changePassword,
  checkCatalogueReferences,
  checkSkeletonDetections,
  checkTwinAssetRegister,
  checkTwinAvailability,
  claimDevice,
  claimLaunch,
  createAnnotationRun,
  createAnnotationRunFrame,
  createCamera,
  createCustomerApiKey,
  createEdgeDevice,
  createEvalMetricRun,
  createEvalUsageMetricRun,
  createEvalValidationSet,
  createExistingNolaConfig,
  createHeroMetricConfig,
  createLegalDraft,
  createMetricRegistryEntry,
  createMiddlewareDataProvider,
  createModelVersion,
  createNamespace,
  createNolaComplexMetric,
  createNolaConfig,
  createNolaService,
  createNolaSimpleMetric,
  createNolaSkeletonConfig,
  createOrganisation,
  createRetentionSchedule,
  routes_default as createRoutes,
  createRtspCaptureRun,
  createSheetBackup,
  createSshConnection,
  createTestRun,
  createTestSet,
  createTestSetFrame,
  createTrainingRun,
  createTrainingRunFrame,
  createTwin,
  createTwinAsset,
  createTwinPlan,
  createTwinSenseConfig,
  createTwinSenseMessage,
  createTwinSenseObserver,
  createUnderlayImages,
  createUser,
  currentLoadId,
  dGraphQuery,
  dataUrlToBlob,
  deleteAnnotationRun,
  deleteAnnotationRunFrame,
  deleteBiDashboard,
  deleteCamera,
  deleteCustomerApiKey,
  deleteEdgeDevice,
  deleteHeroMetric,
  deleteLegalDraft,
  deleteMetricRegistryEntry,
  deleteNolaComplexMetric,
  deleteNolaConfig,
  deleteNolaService,
  deleteNolaSimpleMetric,
  deleteRTSPEndpoint,
  deleteRetentionSchedule,
  deleteRtspCaptureRun,
  deleteSshConnection,
  deleteSubscription,
  deleteTestRun,
  deleteTestSet,
  deleteTestSetFrame,
  deleteThresholdAlertRuleset,
  deleteTrainingRun,
  deleteTrainingRunFrame,
  deleteTwin,
  deleteTwinAsset,
  deleteUser,
  deriveDeviceStatus,
  describeRequestError,
  dgqDeleteTwinEntity,
  dgqFetchTwin,
  dgqFetchTwinOutcome,
  dgqGetTwinModels,
  dgqInsertTwin,
  discardTwinAssets,
  downloadAssetCsvTemplate,
  ensureLatestStreamFrame,
  externaliseUnderlayImages,
  extractSheetIdFromUrl,
  fetchCurrentLegalVersions,
  fetchPlanUnderlayImage,
  fetchSkeletonDetectionFrame,
  fetchStreamFrame,
  fileConfigurationStatementOutcome,
  findManufacturerCatalog,
  flushClientPerf,
  freezeEvalValidationSet,
  generateSheet,
  getAdminResource,
  getAllActiveEdgeDevices,
  getAnnotationRunById,
  getAnnotationRunFrameById,
  getAnnotationRunFrameImage,
  getAnnotationRunFrameOutputs,
  getAnnotationRunFrames,
  getAnnotationRuns,
  getBaselineSelectionView,
  getBaselineSummary,
  getBaselineView,
  getBaselineViewScoped,
  getBiDashboardById,
  getBiDashboardDefinition,
  getBiDashboardVersions,
  getBiDashboards,
  getCalendarSelectionView,
  getCalendarView,
  getCameraById,
  getCameras,
  getCamerasOrThrow,
  getCatalogueProductByReference,
  getConfigurationStatementOutcome,
  getControlEvents,
  getCountAggregation,
  getCountAggregationLog,
  getCustomerApiKeyById,
  getCustomerApiKeys,
  getDecodedOpStatusLog,
  getDroppedDraft,
  getEdgeDeviceById,
  getEdgeDevices,
  getEquipmentClassifications,
  getEquipmentComparison,
  getEquipmentHealth,
  getEvalCapture,
  getEvalCaptureRun,
  getEvalFrameLabel,
  getEvalFrameLabellingContext,
  getEvalGroundTruth,
  getEvalGroundTruthPersons,
  getEvalMetricRun,
  getEvalMetricRuns,
  getEvalUsageMetricRun,
  getEvalUsageMetricRuns,
  getEvalValidationSetById,
  getEvalValidationSetCandidates,
  getEvalValidationSets,
  getFrameSplitConfig,
  getHeatGridCameraOutcome,
  getHeatmap,
  getHeroMetricConfig,
  getHeroMetricConfigs,
  getHeroMetricModes,
  getHierarchy,
  getIdentifyProductsByManufacturer,
  getIndicatorRampSet,
  getIndicatorRampSets,
  getLatestLaunch,
  getLatestOpStatuses,
  getLatestStreamFrame,
  getLaunchHistory,
  getLaunchPlan,
  getLegalDocument,
  getLegalDocumentsAdmin,
  getLegalNotices,
  getLegalStatus,
  getLiveTwinModelDrift,
  getManufacturerCatalog,
  getManufacturerComparison,
  getManufacturerProductsByManufacturer,
  getMetricRegistry,
  getMetricRegistryEntry,
  getMetricRegistryEnums,
  getMetricTypesAndMetrics,
  getMetrics,
  getModelFiles,
  getModelVersions,
  getModels,
  getMySubscriptions,
  getNolaCategories,
  getNolaCategoryMetricLatestValues,
  getNolaCategoryMetricsHistoricalValues,
  getNolaComplexMetrics,
  getNolaComplexMetricsByConfigId,
  getNolaComplexMetricsById,
  getNolaConfigById,
  getNolaConfigs,
  getNolaFrame,
  getNolaHealthCheck,
  getNolaHealthCheckAuth,
  getNolaLocationCategories,
  getNolaLocationCategoryCount,
  getNolaLocations,
  getNolaMetricLatestValue,
  getNolaMetricsByCategory,
  getNolaNetwork,
  getNolaNetworkStatus,
  getNolaProcessor,
  getNolaProcessors,
  getNolaService,
  getPolygons as getNolaServicePolygons,
  getNolaServices,
  getNolaSimpleMetricById,
  getNolaSimpleMetrics,
  getNolaSimpleMetricsByConfigId,
  getNolaVenueSectors,
  getNolaVenueServices,
  getNotificationById,
  getNotifications,
  getNotificationsByOrganisation,
  getObserverProposalsOutcome,
  getOfferedTwinTypes,
  getTwins as getOrgTwins,
  getOrgUrlsAndMetaData,
  getOrganisationById,
  getOrganisations,
  getPeakHours,
  getPeakHoursView,
  getPlatformHealthHistory,
  getPlatformHealthTree,
  getPredictedBusyNextHour,
  getProductionLaunch,
  getProtectedResource,
  getPublicResource,
  getRTSPEndpointById,
  getRTSPEndpoints,
  getRTSPEndpointsOrThrow,
  getRTSPStatus,
  getRankedCandidates,
  getRanking,
  getRetentionScheduleById,
  getRetentionSchedules,
  getRtspCaptureRunById,
  getRtspCaptureRuns,
  getSensorVersions,
  getSensors,
  getSensorsByEntityBID,
  getSshConnection,
  getSshConnectionByPort,
  getSshConnections,
  getStreamBalancePlan,
  getSubscriptionCatalog,
  getSupabaseMetrics,
  getTestRunById,
  getTestRuns,
  getTestSetById,
  getTestSetFrameById,
  getTestSetFrames,
  getTestSets,
  getThresholdAlertRulesets,
  getThumbnailRun,
  getTimeseries,
  getTimeseriesScoped,
  getTokenBounded,
  getTrainingRunById,
  getTrainingRunFrames,
  getTrainingRuns,
  getTwinAssetImport,
  getTwinAssetImportUndoPreview,
  getTwinAssetsOrThrow,
  getTwinBrandMix,
  getTwinById,
  getTwinCompleteConfig,
  getTwinConfig,
  getTwinDashboardConfig,
  getTwinEntityById,
  getTwinHeroMetricsOptimized,
  getTwinIndicatorConfigOptimized,
  getTwinPlanById,
  getTwinPlans,
  getTwinPreferences,
  getTwinPreferencesOptimized,
  getTwinSenseCompositeMeasures,
  getTwinSenseConfigs,
  getTwinSenseConfigsOutcome,
  getTwinSenseObserverByBid,
  getTwinTemplateAttachment,
  getTwinTemplateLabels,
  getTwinTemplateTwinCapabilities,
  getTwinTemplates,
  getTwins2 as getTwins,
  getUsageBands,
  getUsageNow,
  getUserById,
  getUsers,
  identifyTwinAssets,
  importManufacturerHierarchy,
  importSpatialHierarchy,
  initClientPerfReporter,
  installRequestIdInterceptor,
  isAlreadyIdentifiedError,
  isBaselineMeasureToken,
  isBiUnreadableError,
  isOutcomeUnconfirmed,
  isProductChangedError,
  isWriteOutcomeUnknown,
  keepAbsentImportRows,
  listEvalCaptureRuns,
  listEvalCaptures,
  listTwinAssetImports,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  markNotificationAsUnread,
  measureStreamBalance,
  nextRequestId,
  parseBiImportFailure,
  persistTwinLifecycleStage,
  postDashboardBatch,
  preserveMetadataDuringFmlUpload,
  provisionDevice,
  publishBiDashboard,
  publishLegalDraft,
  pushRTSPConfig,
  putHeroMetricConfig,
  putIndicatorRampSet,
  putMetricRegistryEntry,
  putSubscription,
  putTwinTemplateAttachment,
  putTwinTemplateLabels,
  queryVersionedSensors,
  readOpeningHours,
  recordLaunch,
  refreshTwinAssetProposals,
  releaseLaunch,
  removeEvalValidationSetMember,
  removeSpecialPeriod,
  removeStandardHours,
  replaceDraft,
  reportClientPerf,
  requestEvalCaptureRun,
  requestEvalCaptures,
  resetNolaLocation,
  restoreDroppedDraft,
  restoreFromBackup,
  rollbackSensors,
  saveOpeningHours,
  sendNotification,
  sentenceForStatus,
  setLaunchBiOutcome,
  setPublishedVersion,
  signOffNolaService,
  startThumbnailRun,
  syncUserOnLogin,
  testSshConnection,
  toClientStatus,
  toIdentifyProduct,
  twinChangedOf,
  undoIdentifyTwinAssets,
  undoTwinAssetImport,
  updateAnnotationRun,
  updateAnnotationRunFrame,
  updateBiDashboard,
  updateCamera,
  updateCustomerApiKey,
  updateEdgeDevice,
  updateLegalDraft,
  updateLiveTwinModels,
  updateNolaComplexMetric,
  updateNolaConfig,
  updateNolaService,
  updateNolaSimpleMetric,
  updateOrganisation,
  updateProfile,
  updateRTSPEndpoint,
  updateRetentionSchedule,
  updateRtspCaptureRun,
  updateSshConnection,
  updateTestRun,
  updateTestSet,
  updateThresholdAlertRuleset,
  updateTrainingRun,
  updateTwin,
  updateTwinAsset,
  updateTwinCapabilities,
  updateTwinPlan,
  updateTwinSenseConfig,
  updateUserById,
  uploadAnnotationRunFrame,
  uploadFmlFile,
  uploadObserverFrame,
  uploadPlanUnderlayImage,
  uploadSkeletonDetectionFrame,
  upsertEvalFrameLabel,
  upsertEvalGroundTruth,
  upsertEvalGroundTruthPersons,
  upsertSensors,
  versionAndDeleteSensors
};
